import { useState, useRef, useEffect, useMemo, useCallback, Fragment } from 'react';
import { doc, getDoc, setDoc, updateDoc, deleteField, Timestamp } from 'firebase/firestore';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { BulkAIEnrich, type InMemoryEnrichHost } from './BulkAIEnrich';
import { zipTaggedImages, type TaggedEnrichImage } from '../../services/enrichImageCollection';
import { applyAiFieldPatch, applyPreviewEnrichRows, buildParentEntry, previewAcceptsAiKey, previewRowId } from './previewEnrich';
import {
  defaultStoreCategorySelection,
  filterStoreEntries,
  storeCategoryOptions,
  storeProductKey,
  titleExcludedStoreProducts,
} from './storeCategoryFilter';
import { PasteHtmlModal, type PasteHtmlResult } from '../common/PasteHtmlModal';
import { ImageDownloadPanel } from '../Extractors/ImageDownloadPanel';
import { ImageCell } from './ImageCell';
import {
  db,
  createWatchReference,
  updateWatchReference,
  computeSearchText,
  getExistingReferenceMapById,
  getLookupCollection,
  getImportConfigForBrand,
  saveImportConfigForBrand,
  setReferencePendingImageUrl,
  clearReferencePendingImageUrl,
  updateWatchBrand,
  findModulesByLegacyValues,
  saveElectronicModule,
  extractProductDataWithBackoff,
  extractProductUrls,
  getBrandImportSources,
  normalizeReference,
  getReferencesByDocIds,
  getWatchBrandById,
  logAdminEvents,
  lookupRefReleaseYearWithAI,
  ExtractionCancelledError,
  NeedsManualHtmlError,
  UNREACHABLE_LISTING_MESSAGE,
  type ExtractedProduct,
  type PersistedFieldRule,
  type PersistedExclusionRule,
} from '../../services/firebase';
import {
  ingestReferenceImage,
  ingestReferenceImages,
  probeReferenceImages,
  type ImageProbeResult,
} from '../../services/referenceImageIngest';
import {
  ImportImageFailureCollector,
  applyRetryOutcome,
  formatImageFailureHeadline,
  summarizeImageFailures,
  type ImportImageFailure,
  type ImportImageFailureRow,
} from './importImageFailures';
import { ImportBrandCell } from './ImportBrandCell';
import {
  acquisitionSiblingIds,
  buildRoutingContext,
  planImportRouting,
  routingOptionLabel,
  routingOptions,
  rowBrandView,
  type BrandAssignedBy,
  type RoutingBrand,
} from './importBrandRouting';
import { normalizeSourceName, deriveSeries } from '../../services/referenceNaming';
import { stripInlineMarkup } from '../../services/stripInlineMarkup';
import { canonicalProductUrlKey, dedupeProductUrls } from '../../utils/productUrls';
import { withoutExcludedUrls } from '../../utils/importExclusions';
import { deriveIsVariant } from '../../utils/references';
import {
  parseDialMarkerValues,
  dialMarkerValuesForWrite,
} from '../../utils/dialMarkerFields';
import {
  setMultiSelectVocabulary,
  normalizeMultiSelectValue,
} from '../../services/multiSelectNormalize';
import {
  DEFAULT_IMPORT_STOPWORDS,
  stripImportStopwords,
  stripImportStopwordsFromText,
} from '../../services/importStopwords';
import { subscribeToImportStopwords } from '../../services/importStopwordsConfig';

// CalibreData used indirectly via CalibreMapEntry from ImportCalibreCheck
import type { WatchBrand, WatchReference, GenericExtractionMethod, ReferenceSource, ImageInfo, VariantGroupingRule } from '../../types';
import { ImportCalibreCheck, type ExtractionRule, type CalibreMapEntry } from './ImportCalibreCheck';
import { ImportModuleCheck, type ModuleMapEntry } from './ImportModuleCheck';
import {
  applyManualCalibreEdit,
  applyRevisionPick,
  extractCalibre,
  importCalibreFields,
  stampCalibreOnEntry,
  type RevisionLineMember,
} from './importCalibreStamp';
import { RevisionPickCell, type RevisionPickState } from './RevisionPickCell';
import {
  namesExplicitRevision,
  refYearOf,
  resolveRevisionInLine,
  yearInRevision,
  type RevisionResolution,
} from '../../services/calibreLineageLogic';
import { routeAnalogModuleToMovement } from './routeAnalogModule';
import { importDescriptionTag } from './descriptionSource';
import {
  buildImportTierIndex,
  tierOptionsFor,
  tierRowLabel,
} from './importTierOptions';
import type { CalibreDualWriteFlags } from './calibreBulkUpdate';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { isExtractionResult, extractionResultToEntries } from './adapters/extractionToEntries';
import {
  shopifyParentDocId,
  shopifyVariantDocId,
  storeDocIdForEntry,
  variantGroupOf,
  type VariantGroup,
} from './storeDocIds';
import { extractionResultCalibres } from './adapters/extractionToCalibres';
import type { ExtractedCalibre, TaggedImage } from '@watchkeeper/shared-types';
import {
  selectGalleryImageCandidates,
  selectHeroImageTags,
  planGalleryMerge,
  planGalleryReplace,
  imageForWrite,
  MAX_GALLERY_IMAGES,
  type GalleryImageCandidate,
} from '../../services/galleryImages';
import { ImportFieldRules, applyRules, applyExclusionRules, type FieldRule, type ExclusionRule } from './ImportFieldRules';
import {
  fetchShopifyProducts,
  getProductTypes,
  isLikelyWatch,
  filterProducts,
  titleExcludedProducts,
  parseProducts,
  type ShopifyProduct,
} from './ShopifyScraper';
import { pagePreviewItems } from './previewPaging';
import { NO_IMAGE_REASON, shouldWritePreviewItem } from './importGate';
import { groupShopifyVariantsForImport } from './shopifyVariantGrouping';
import { proposeRuleGroups, resolveAcceptedGroups, ruleIsActive, type RuleGroupDecision } from './variantGroupingRule';
import { summaryLine } from './importSummaryLine';
import { describeCounts, groupedDisplayOrder, lacksNativeGrouping, NO_NATIVE_GROUPING_HINT, planImportStructure, splitCreateUpdate } from './importStructure';
import { VariantRuleGroupsPanel } from './VariantRuleGroupsPanel';
import toast from 'react-hot-toast';

/**
 * Page sizes for the preview table, matching the Brands and Calibres listings.
 *
 * The preview used to mount every parsed row at once. At 408 references that is
 * ~15,000 DOM nodes and 408 image requests in one go — survivable on a desktop,
 * not on a phone, where iOS Safari's per-tab memory ceiling ends the tab and the
 * user sees "the page refreshed". Product shots are the expensive half: a
 * 2000x2000 hero decodes to ~16MB of bitmap to paint a 40px thumbnail, and a row
 * that stays mounted never gives it back.
 *
 * Paging bounds both, on every device. Deliberately NOT gated on a mobile check
 * — a 408-row table is slow everywhere, and this codebase has no device
 * detection to key off (its one `window.innerWidth` positions a popover).
 */
const PREVIEW_PAGE_SIZE_OPTIONS = [25, 50, 100];

// ── Input JSON shape from the scraper ────────────────────────────────────────
// Brand-agnostic: all fields optional except the two required by validation.

export interface ScrapedWatchEntry {
  // Required
  reference: string;
  model: string;
  // Common
  url?: string;
  title?: string;
  productName?: string;
  imgSrc?: string;
  /**
   * Extra product shots with whatever content tags the source could infer
   * (`dial`, `bezel`, `box`…). Emitted by the extractor repo's JSON envelope and
   * carried through by `extractionToEntries`; absent on scrapes that only ever
   * had one image.
   *
   * The hero shot is normally this array's first element AND `imgSrc`. The
   * importer uploads it once, into `images[0]`, and puts the remainder in
   * `galleryImages` — see importGalleryImages.
   */
  taggedImages?: TaggedImage[];
  collection?: string;
  collAbbr?: string;
  variant?: string;
  variantCode?: string;
  variantDesc?: string;
  description?: string;
  // Movement
  /**
   * The explicit calibre NAME, written to the reference whole. Set by the
   * calibre-check step (canonical name of a matched calibre), by a curator's
   * edit on the preview, and by AI enrich. When absent the write path falls back
   * to the calibre CODE extracted from `movement` (scraped prose).
   */
  calibre?: string;
  movement?: string;       // caliber string, e.g. "Seiko NH35A"
  movementType?: string;
  // Explicitly-extracted watch-type hint. Present on file/JSON imports; when
  // unset it's derived from movement/module/text signals. Either way it's
  // canonicalized to a lookup_watch_types slug at import time (see
  // deriveWatchType / resolveWatchType) — the raw value need not be canonical.
  watchType?: string;
  // Digital-watch module identifier (electronic equivalent of a calibre), e.g.
  // Casio "3229". Resolved to a linked `electronicModules` doc (`moduleId`) at
  // save time — the digital counterpart of the calibre link.
  module?: string;
  jewels?: string;
  // Case
  caseSize?: string;
  caseHeight?: string;
  caseMat?: string;        // legacy code-based field (SS, DLC, TI…)
  caseMatCode?: string;
  caseMaterial?: string;   // direct material string
  caseFinish?: string;
  caseCoating?: string;
  lugWidth?: string;
  lugToLug?: string;
  waterRes?: string;
  // Dial & hands
  dialColor?: string;
  // Dial STYLE / pattern slug (lookup_dial_styles). Rarely present on raw scrape
  // data — usually DETECTED from dialColor/variant text by detectDialStyle — but
  // carried on the field so a parent-as-entry can pass through its stored style.
  dialStyle?: string;
  dialCode?: string;
  dialFinish?: string;
  /** Marker TYPE — how the markers are made (applied/printed/sandwich/…). */
  dialIndexes?: string;
  /** Marker STYLE — what the markers are (arabic/roman/stick/dot/baton). */
  dialNumerals?: string;
  hands?: string;
  handsColor?: string;
  /**
   * WHAT each sub-register does → `dialAndHands.subdialTypes`. MULTI-select, so
   * it arrives comma-joined, the same shape it is stored in.
   *
   * There is deliberately no entry field for the `subdials` COUNT: it is a
   * number rather than a slug and no import path has ever carried it, so a
   * scrape can say a dial has chronograph minutes and running seconds without
   * committing to how many registers that adds up to.
   */
  subdialTypes?: string;
  /**
   * Chapter ring & dial furniture → `dialAndHands.{chapterRing,minuteTrack,
   * dialScales,logoType}`. Single-select slugs except `dialScales`, which is
   * MULTI and arrives comma-joined — the same shape it is stored in, so there
   * is nothing to split or re-join in the mapper.
   *
   * Independent of one another: unlike the date fields below, none of the four
   * gates another, so an entry can carry any subset. A dial with no chapter
   * ring still has a minute track printed on its face.
   */
  chapterRing?: string;
  minuteTrack?: string;
  dialScales?: string;
  /** Structural/decorative dial attributes, comma-joined. */
  dialFeatures?: string;
  logoType?: string;
  /**
   * Date complication, written straight through to `dialAndHands` by
   * mapEntryToReference. `dateDisplay` gates the other four: a pointer_date or
   * subsidiary_dial has no aperture, so it carries no position/frame/wheel
   * colour. No extractor emitted these before shared-types v1.55.0 (v1.61.0 for
   * `dateWheelTextColor`), so an older scrape simply leaves them undefined.
   *
   * One slug each except `dateWheelTextColor` — the colour(s) of the numerals
   * printed ON the disc, which is multi-select and arrives comma-joined, the
   * same shape it is stored in.
   */
  dateDisplay?: string;
  dateWindowPosition?: string;
  dateWindowFrame?: string;
  dateWheelColor?: string;
  dateWheelTextColor?: string;
  /**
   * Lume detail → `dialAndHands.{lumePresence,lumeMaterial,lumeDayColor,
   * lumeGlowColor}`. One single-select slug each, persisted verbatim —
   * nothing to split or re-join. `lumePresence` gates the other three.
   *
   * A scrape realistically fills `lumeMaterial` alone (the compound is the one
   * lume fact a product page states); the colours arrive from the AI enrich
   * pass, which can see a lume-tagged image.
   */
  lumePresence?: string;
  lumeMaterial?: string;
  lumeDayColor?: string;
  lumeGlowColor?: string;
  glass?: string;
  crystal?: string;
  bezelType?: string;
  /**
   * Bezel material → `WatchReference.bezel`, and bezel colour(s) →
   * `WatchReference.bezelColors`. Both arrive as one flat string (the scrape
   * and the extractor callable both emit scalars); `bezelColors` is
   * multi-select, so mapEntryToReference splits it into the stored array.
   */
  bezelMaterial?: string;
  bezelColors?: string;
  /** Case body colours. Same flat-string arrival as bezelColors. */
  caseColors?: string;
  caseShape?: string;
  // Strap
  strapMaterial?: string;
  strapColor?: string;
  // Additional specs
  powerReserve?: string;
  dialMaterial?: string;
  crownType?: string;
  caseBack?: string;
  // Functions / complications extracted from the source page, as a comma-separated
  // string (e.g. "GMT, Date, Chronograph"). Merged into the ref's `functions[]`.
  functions?: string;
  // Production run, as discrete 4-digit year strings. Flattened counterparts of
  // WatchReference.production.startYear / .endYear (mapEntryToReference parses
  // them to numbers), matching how caseHeight → case.height and strapMaterial →
  // strap.material flatten. An open-ended run ("1969 to present") carries a
  // start with no end. Currently only populated by the extractor JSON import.
  productionStartYear?: string;
  productionEndYear?: string;
  // WatchReference.production.releaseYear, the same flat 4-digit string. Filled
  // by the preview AI Enrich (AI_TO_ENTRY_FIELD) — before it existed the grid
  // filtered the AI's releaseYear out, so imports never captured a year that a
  // standalone enrich of the same ref then proposed.
  productionReleaseYear?: string;
  /**
   * Accepted AI values for every grid-registry key WITHOUT a field of its own
   * here (isChronograph, caseWeight, the electronics and pocket-watch blocks…),
   * as the registry's dotted-path patch with its real types. Built by
   * suggestionValuesToEntryFields, applied last by mapEntryToReference.
   */
  _aiFields?: Record<string, unknown>;
  // Misc
  compCode?: string;
  tags?: string[];
  price?: string | number;
  // Shopify variant grouping (only present on the Shopify import path). Carried
  // through the calibre/rules/preview pipeline so handleImport can group a
  // product's variants under one parent ref. Absent on file/URL imports → those
  // are always standalone, unchanged behaviour.
  shopifyProductHandle?: string;
  /** Shopify `vendor`, verbatim — a brand-routing signal, never stored on the ref. */
  shopifyVendor?: string;
  shopifyVariantCount?: number;
  /**
   * Shopify product / variant ids (numeric strings). Present only on the
   * Shopify import path. The importer keys Shopify docs by a composite id built
   * from these (`shopify__{productId}__{variantId}`; parent
   * `shopify__{productId}__parent`) so identity is decoupled from the SKU —
   * the same SKU reused across Shopify products produces distinct docs. Absent
   * on file/URL/manual imports → those keep ref-string-derived identity.
   */
  shopifyProductId?: string;
  shopifyVariantId?: string;
  /**
   * Square Online store + product ids. Present only on the Square Online import
   * path (SquareOnlineScraper). Like the Shopify pair, they key the doc by a
   * composite id (`square__{siteId}__{productId}`) instead of the ref string —
   * Square products rarely carry a model number, so the ref is often the name.
   */
  squareSiteId?: string;
  squareProductId?: string;
  /**
   * WooCommerce shop host (lowercased, no `www.`) + product id. Present only on
   * the WooCommerce import path (WooCommerceScraper); doc id
   * `woocommerce__{host}__{productId}`, like the Shopify / Square pairs.
   */
  wooHost?: string;
  wooProductId?: string;
  /**
   * Phase 2 variant expansion (WooCommerce / Square Online): the variation /
   * SKU this row is, and how many variants its product has. With these set the
   * row's doc id is `{product-level id}__{variantId}` and it imports as a
   * variant of a parent whose doc id is the product-level id (see
   * storeDocIds.variantGroupOf).
   */
  wooVariationId?: string;
  squareSkuId?: string;
  storeVariantCount?: number;
  /**
   * The product an expanded variant belongs to — what its parent reference is
   * built from (the product's own SKU/name and images), rather than the first
   * variant's.
   */
  storeParent?: { reference?: string; imgSrc?: string; taggedImages?: TaggedImage[] };
  /**
   * The store categories this row's product is filed in (WooCommerce /
   * Square Online). Drives the shop-URL category picker; ids are the shop's
   * own (stringified), names as the shop shows them.
   */
  storeCategories?: Array<{ id: string; name: string }>;
  /**
   * Preview AI enrichment: the description text the curator accepted from AI,
   * and the text it replaced. Tells the import to record the description as
   * AI-written (`descriptionSource: 'ai'`) rather than as scraped copy. Never
   * written to the document themselves.
   */
  _aiDescription?: string;
  _preAiDescription?: string;
  /**
   * How many options (variations / SKUs) the store sells this product in, when
   * it is more than one. v1 imports such a product as ONE reference; the
   * preview flags the row as a variant-expansion candidate. Set by the Square
   * Online and WooCommerce scrapers; never written to the reference.
   */
  storeOptionCount?: number;
}

// ── Per-item import status ────────────────────────────────────────────────────

// 'diverged': the ref already existed and the freshly-extracted source data
// disagrees with a stored (curated) value, but the fill-empty-only merge diff
// came back empty — nothing was written, curated values were preserved. It is a
// pure signal ("upstream differs from your version"), NOT a write.
type ItemStatus = 'pending' | 'processing' | 'success' | 'updated' | 'diverged' | 'duplicate' | 'excluded' | 'error';

interface ImportItem {
  entry: ScrapedWatchEntry;
  status: ItemStatus;
  error?: string;
  referenceId?: string;
  included: boolean;
}

// A saved-URL that failed automated extraction and is awaiting a per-row action
// (Retry the backoff sequence, or Enter HTML manually). `retrying` gates the
// buttons off while a retry is in flight; `note` carries live progress/error text.
interface SavedUrlFailure {
  url: string;
  note?: string;
  retrying: boolean;
}

// Fields shown (and inline-editable) in the per-item preview detail panel.
// Mirrors the field set rendered in ImportSources' SourceCard "Test" preview.
const PREVIEW_DETAIL_FIELDS: Array<{ label: string; key: keyof ScrapedWatchEntry }> = [
  { label: 'Reference', key: 'reference' },
  { label: 'Model', key: 'model' },
  { label: 'Name', key: 'productName' },
  { label: 'Collection', key: 'collection' },
  { label: 'Variant', key: 'variant' },
  { label: 'Calibre', key: 'movement' },
  { label: 'Module', key: 'module' },
  { label: 'Movement type', key: 'movementType' },
  { label: 'Case size', key: 'caseSize' },
  { label: 'Case material', key: 'caseMaterial' },
  { label: 'Case shape', key: 'caseShape' },
  { label: 'Crystal', key: 'crystal' },
  { label: 'Water resistance', key: 'waterRes' },
  { label: 'Dial color', key: 'dialColor' },
  { label: 'Dial finish', key: 'dialFinish' },
  { label: 'Strap material', key: 'strapMaterial' },
  { label: 'Power reserve', key: 'powerReserve' },
  { label: 'Jewels', key: 'jewels' },
];

// ── Field mapping helpers ─────────────────────────────────────────────────────

/**
 * Removes undefined, null, and empty-string values (shallow) so Firestore never receives fields it rejects.
 */
function stripEmpty<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''),
  ) as Partial<T>;
}

/**
 * Recursively removes undefined, null, and empty-string values from an object.
 * Returns undefined if the resulting object is empty.
 */
function deepStripEmpty(obj: unknown): unknown {
  if (obj === undefined || obj === null || obj === '') return undefined;
  if (Array.isArray(obj)) return obj;
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const stripped = deepStripEmpty(value);
      if (stripped !== undefined) result[key] = stripped;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
  return obj;
}

/**
 * Checks if newData has any values that differ from existing.
 * Used in overwrite mode to detect actual changes.
 */
function hasActualChanges(
  existing: Record<string, unknown>,
  newData: Record<string, unknown>,
): boolean {
  for (const [key, value] of Object.entries(newData)) {
    if (value === undefined || value === null || value === '') continue;
    const existingVal = existing[key];

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      if (typeof existingVal !== 'object' || existingVal === null || Array.isArray(existingVal)) {
        return true; // Type mismatch = change
      }
      if (hasActualChanges(existingVal as Record<string, unknown>, value as Record<string, unknown>)) {
        return true;
      }
    } else if (Array.isArray(value)) {
      if (!Array.isArray(existingVal) || JSON.stringify(value) !== JSON.stringify(existingVal)) {
        return true;
      }
    } else {
      // Primitive comparison
      if (existingVal !== value) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Builds a diff object containing only fields from newData where the existing document
 * has no value (undefined/null/empty string). Recurses into nested objects.
 */
function buildMergeDiff(
  existing: Record<string, unknown>,
  newData: Record<string, unknown>,
): Record<string, unknown> {
  const diff: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(newData)) {
    if (value === undefined || value === null || value === '') continue;
    const existingVal = existing[key];
    if (existingVal === undefined || existingVal === null || existingVal === '') {
      diff[key] = value;
    } else if (
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof existingVal === 'object' &&
      existingVal !== null &&
      !Array.isArray(existingVal)
    ) {
      const nestedDiff = buildMergeDiff(
        existingVal as Record<string, unknown>,
        value as Record<string, unknown>,
      );
      if (Object.keys(nestedDiff).length > 0) diff[key] = nestedDiff;
    }
    // else: existing has a value — keep it, don't overwrite
  }
  return diff;
}

/**
 * Flattens a nested object to dot-notation keys for Firestore updateDoc,
 * so nested field updates don't overwrite sibling fields.
 */
function flattenToDotNotation(
  obj: Record<string, unknown>,
  prefix = '',
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(result, flattenToDotNotation(value as Record<string, unknown>, fullKey));
    } else {
      result[fullKey] = value;
    }
  }
  return result;
}

// caseMat code → { material, coating }
function parseCaseMat(code: string): { material?: string; coating?: string } {
  switch (code?.toUpperCase()) {
    case 'DLC': return { material: 'steel', coating: 'dlc' };
    case 'PVD': return { material: 'steel', coating: 'pvd' };
    case 'SS':  return { material: 'steel' };
    case 'TI':  return { material: 'titanium' };
    default:    return code ? { material: code.toLowerCase() } : {};
  }
}

// dialColor code → human-readable color string
function parseDialColor(code: string): string | undefined {
  const map: Record<string, string> = {
    BLK: 'black', BLU: 'blue', WHT: 'white', GRN: 'green',
    SLV: 'silver', RED: 'red', ORG: 'orange', YEL: 'yellow',
  };
  return map[code?.toUpperCase()] ?? (code ? code.toLowerCase() : undefined);
}

// Dial-STYLE detection is DATA-DRIVEN from the lookup_dial_styles collection —
// NO hardcoded style→slug or style→color maps. EVERY ref gets a dialStyle: a
// themed slug when a synonym matches the variant's dial option / label, else the
// default 'single-color'. A themed style's `properties` are the constituent dial
// colors it implies (Panda → black+white, Pepsi → blue+red); empty for finishes
// (Sunburst/Gilt) that leave the color untouched.
//
// The detector is built once per import run (handleImport) from the live lookup
// and stashed module-side (mapEntryToReference is a plain top-level fn called
// from several spots). Until loaded — or if the lookup is unseeded — it's empty,
// so every ref defaults to single-color and no themed colors are applied.
const DEFAULT_DIAL_STYLE_SLUG = 'single-color';

interface DialStyleHit {
  slug: string;
  properties: string[];
}
interface DialStyleDetector {
  byKey: Map<string, DialStyleHit>;
  maxGram: number;
}
let dialStyleDetector: DialStyleDetector = { byKey: new Map(), maxGram: 1 };

function normStyleKey(s: string): string {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Build the module-level detector from lookup_dial_styles values. Detection keys
// come from each themed style's value/displayName/synonyms; the default
// (single-color) contributes none (it IS the fallback).
function buildDialStyleDetector(
  values: { value?: string; displayName?: string; synonyms?: string[]; properties?: string[] }[],
): void {
  const byKey = new Map<string, DialStyleHit>();
  let maxGram = 1;
  for (const v of values) {
    const slug = (v.value || '').trim();
    if (!slug || slug === DEFAULT_DIAL_STYLE_SLUG) continue;
    const hit: DialStyleHit = {
      slug,
      properties: Array.isArray(v.properties)
        ? v.properties.map((p) => String(p).trim()).filter(Boolean)
        : [],
    };
    const keys = [v.value, v.displayName, ...(v.synonyms ?? [])];
    for (const raw of keys) {
      const key = normStyleKey(raw ?? '');
      if (!key || byKey.has(key)) continue;
      byKey.set(key, hit);
      maxGram = Math.max(maxGram, key.split(' ').length);
    }
  }
  dialStyleDetector = { byKey, maxGram };
}

// Detect a themed dial style in a source string (the variant's dialColor +
// variant label — NOT free-text title/description, to avoid model-name false
// positives). Longest-n-gram-first so "Reverse Panda" wins over "Panda".
function detectDialStyle(raw: string | undefined): DialStyleHit | undefined {
  if (!raw || dialStyleDetector.byKey.size === 0) return undefined;
  const words = normStyleKey(raw).split(' ').filter(Boolean);
  for (let i = 0; i < words.length; i++) {
    for (let n = Math.min(dialStyleDetector.maxGram, words.length - i); n >= 1; n--) {
      const hit = dialStyleDetector.byKey.get(words.slice(i, i + n).join(' '));
      if (hit) return hit;
    }
  }
  return undefined;
}

// ── Dial COLOR + FINISH decomposition (import-time) ──────────────────────────
// Ports the watchlock dialColorBackfill `matchColors` / `decomposeExistingColor`
// algorithm (functions/src/dialColorBackfill.ts — SOURCE OF TRUTH) to the import
// path so new Shopify imports write the SAME shape the backfill produces, and a
// re-import of an already-backfilled brand is a no-op.
//
// A "<finish> <color>" composite in a dial value ("Sunburst Plum") is SPLIT: the
// finish slug goes to dialAndHands.finish and only the canonical color
// ("plum") lands in dialAndHands.color — the raw composite is never persisted. A
// brand-specific raw with NO finish prefix ("Maelstrom Blue") is preserved
// verbatim. Mirrors decomposeExistingColor's lenient remainder handling
// (canonical color when the lookup knows it, raw remainder otherwise) so the
// split lands even for colors not yet in lookup_dial_colors.
//
// Both resolvers are built once per import run (handleImport) from the live
// lookups and stashed module-side, exactly like dialStyleDetector. Until loaded
// — or if a lookup is unseeded — they are empty, so no decomposition occurs and
// the raw dial value is preserved (fails safe — never mis-splits).

// lookup_dial_colors: normalized name (value / displayName / synonym) → canonical
// value slug. Precedence value > displayName > synonym (earlier pass wins).
let dialColorResolver = new Map<string, string>();
// lookup_dial_finish: normalized name → canonical finish slug, plus the longest
// key length in words (bounds the n-gram window for multi-word finishes).
let dialFinishResolver: { byKey: Map<string, string>; maxGram: number } = {
  byKey: new Map(),
  maxGram: 1,
};

function buildDialColorResolver(
  values: { value?: string; displayName?: string; synonyms?: string[] }[],
): void {
  const byKey = new Map<string, string>();
  const add = (raw: string | undefined, canonical: string) => {
    const key = normStyleKey(raw ?? '');
    if (!key || byKey.has(key)) return;
    byKey.set(key, canonical);
  };
  // Three precedence passes so a direct value/displayName key is never shadowed
  // by another entry's synonym (mirrors the backfill's addPass ordering).
  for (const v of values) if (v.value) add(v.value, v.value);
  for (const v of values) if (v.value && v.displayName) add(v.displayName, v.value);
  for (const v of values)
    if (v.value) for (const s of v.synonyms ?? []) add(s, v.value);
  dialColorResolver = byKey;
}

function buildDialFinishResolver(
  values: { value?: string; displayName?: string; synonyms?: string[] }[],
): void {
  const byKey = new Map<string, string>();
  let maxGram = 1;
  const add = (raw: string | undefined, slug: string) => {
    const key = normStyleKey(raw ?? '');
    if (!key || byKey.has(key)) return;
    byKey.set(key, slug);
    maxGram = Math.max(maxGram, key.split(' ').length);
  };
  for (const v of values) {
    const slug = (v.value || '').trim();
    if (!slug) continue;
    add(v.value, slug);
    add(v.displayName, slug);
    for (const s of v.synonyms ?? []) add(s, slug);
  }
  dialFinishResolver = { byKey, maxGram };
}

interface DialColorDecomp {
  // Canonical (or preserved brand-specific raw) dial colors, deduped, first-seen.
  colors: string[];
  // Finish slug lifted from a "<finish> <color>" composite, when present.
  finish?: string;
}

// Decompose a dial-color value (a scalar, a comma/semicolon/slash-joined string,
// or a string[] carrying both a canonical color and the brand's raw name) into
// canonical colors + an optional finish. See the block comment above.
function decomposeDialColor(raw: unknown): DialColorDecomp {
  const colors: string[] = [];
  const seen = new Set<string>();
  let finish: string | undefined;
  const push = (c: string) => {
    const t = c.trim().toLowerCase();
    const k = normStyleKey(t);
    if (k && !seen.has(k)) {
      seen.add(k);
      colors.push(t);
    }
  };
  const canonOf = (s: string): string | undefined =>
    dialColorResolver.get(normStyleKey(s));

  // Flatten every shape into a list of trimmed pieces.
  const rawList = Array.isArray(raw) ? raw : [raw];
  const pieces: string[] = [];
  for (const r of rawList) {
    if (r === undefined || r === null) continue;
    for (const piece of String(r).split(/[,;/]/)) {
      const t = piece.trim();
      if (t) pieces.push(t);
    }
  }

  for (const piece of pieces) {
    const words = normStyleKey(piece).split(' ').filter(Boolean);
    // Finish PREFIX, longest-first, leaving ≥1 word as the color remainder.
    let matched = false;
    for (let n = Math.min(dialFinishResolver.maxGram, words.length - 1); n >= 1; n--) {
      const slug = dialFinishResolver.byKey.get(words.slice(0, n).join(' '));
      if (!slug) continue;
      const restRaw = words.slice(n).join(' ');
      // Lenient: keep the remainder as the color even when the lookup lacks it
      // (canonical when known, raw otherwise) — matches decomposeExistingColor.
      finish = finish ?? slug;
      push(canonOf(restRaw) ?? restRaw);
      matched = true;
      break;
    }
    if (matched) continue;
    // No finish prefix: preserve the piece exactly as the importer did before —
    // code-mapped (BLK → black), or the lowercased raw (canonical + brand name).
    push(parseDialColor(piece) ?? piece.toLowerCase());
  }
  return { colors, finish };
}

// A themed dial-style hit whose slug is actually a FINISH (Sunburst / Gilt that
// should have been pruned from lookup_dial_styles into lookup_dial_finish) is
// reclassified: callers write the slug to dialAndHands.finish and fall back to
// the single-color style. Returns the finish slug when the hit is a finish.
function finishStyleSlug(hit: DialStyleHit | undefined): string | undefined {
  if (!hit) return undefined;
  return dialFinishResolver.byKey.get(normStyleKey(hit.slug));
}


/**
 * Derive a parent reference string for a Shopify variant group.
 *
 * Shopify has no "reference" field — `variant.sku` is the closest thing — so the
 * parent's displayed ref is the LEAD variant's SKU: a real-world reference like
 * `W-BEL-GYS-DAE` rather than a synthetic prefix/slug. The lead is Shopify's
 * `variants[0]` (position 1 = default); `variantRefs` arrives in that order
 * (and is already `.filter(Boolean)`-ed at the call site). We additionally skip
 * whitespace-only SKUs and fall through to the next variant that has one.
 *
 *   parentRef = leadVariant.sku
 *     || firstVariantWithSku.sku
 *     || normalizeReference(productHandle)   // existing handle-slug fallback
 *
 * Only when NO variant carries a SKU do we fall back to the product-handle slug
 * (e.g. `goofy-panda-gen3` → `GOOFYPANDAGEN3`).
 *
 * The handle fallback is stopword-stripped BEFORE `normalizeReference` runs, so
 * a handle like `goofy-panda-pre-order` yields `GOOFYPANDA` rather than
 * `GOOFYPANDAPREORDER` — normalizing first would weld the junk word into the
 * identity string, where no word boundary survives to find it again.
 *
 * The SKU branch is deliberately NOT re-stripped: `variantRefs` are the group's
 * EFFECTIVE refs, already cleaned on the way into the wizard and then adjusted
 * for any row whose REF CHANGED box the admin unticked. Stripping again here
 * would quietly override that "keep the stored ref" decision.
 *
 * TODO(per-brand ref extraction): brands that store their reference number
 * OUTSIDE the SKU field (e.g. in a product/variant metafield, or encoded in the
 * title) are the natural extension point — plug a per-brand regex/metafield
 * extractor in here to override the SKU-derived ref before the handle fallback.
 * Out of scope for now (no brand needs it yet).
 */
function deriveParentReference(
  variantRefs: string[],
  productHandle: string,
  stopwords: string[],
): string {
  const leadSku = variantRefs.map((r) => (r ?? '').trim()).find(Boolean);
  if (leadSku) return leadSku;
  return normalizeReference(stripImportStopwords(productHandle || '', stopwords));
}

// Shopify / Square Online composite doc ids live in storeDocIds.ts.

// Aggregate one variant-distinguishing field across a parent's variant group
// into a deduped, sorted, canonical array. The array is emitted whenever there
// is at least ONE distinct value — it is the canonical "what does this model
// offer for this dimension" surface, so a single shared value is still useful
// (consumers can see what the parent carries without inferring it from the
// parent's own field). Only a completely empty set (field absent everywhere)
// returns []. `extract` returns the canonical value(s) for one entry (same
// derivation mapEntryToReference uses for the underlying field), or undefined
// when that entry lacks the field — undefined/empty are skipped.
function aggregateVariantField(
  entries: ScrapedWatchEntry[],
  extract: (e: ScrapedWatchEntry) => string[] | undefined,
): string[] {
  const set = new Set<string>();
  for (const e of entries) {
    for (const v of extract(e) ?? []) {
      const t = v.trim();
      if (t) set.add(t);
    }
  }
  return set.size >= 1 ? Array.from(set).sort() : [];
}

// Per-field canonical extractors, mirroring mapEntryToReference's logic so the
// aggregated arrays hold the same canonical values as the single-value fields.
function variantDialColors(e: ScrapedWatchEntry): string[] | undefined {
  // dialColor can be a scalar, a comma/semicolon/slash-joined multi-value
  // string, OR an actual string[] (a variant can carry both a canonical color
  // and the brand's name, e.g. ["blue", "maelstrom blue"]). decomposeDialColor
  // normalizes every shape to a flat list of canonical tokens AND splits any
  // "<finish> <color>" composite (dropping the raw composite) — the SAME
  // derivation mapEntryToReference now uses — so the parent's aggregated colors
  // match the variant docs. aggregateVariantField's Set dedupes by content.
  const decomp = decomposeDialColor(e.dialColor);
  return decomp.colors.length ? decomp.colors : undefined;
}
function variantMovements(e: ScrapedWatchEntry): string[] | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cal = (e as any).calibre || (e.movement ? extractCalibre(e.movement) : undefined);
  return cal ? [String(cal)] : undefined;
}
function variantStrapTypes(e: ScrapedWatchEntry): string[] | undefined {
  return e.strapMaterial ? [e.strapMaterial] : undefined;
}
function variantCaseMaterials(e: ScrapedWatchEntry): string[] | undefined {
  const mat = e.caseMaterial || parseCaseMat(e.caseMat ?? '').material;
  return mat ? [mat] : undefined;
}
function variantDialStyles(e: ScrapedWatchEntry): string[] | undefined {
  // EVERY ref has a dialStyle, so every variant contributes one: an explicit
  // slug (parent-as-entry), else a themed detection from the dial option/label
  // (same source as mapEntryToReference), else the single-color default. The
  // parent's dialStyleVariants then emits only when ≥2 distinct values exist.
  if (e.dialStyle) return [e.dialStyle];
  const hit = detectDialStyle([e.dialColor, e.variant].filter(Boolean).join(' '));
  // A reclassified finish-style (Sunburst/Gilt) is NOT a style — fall back to the
  // single-color default so the parent aggregate matches the variant docs.
  const slug =
    hit && !finishStyleSlug(hit) ? hit.slug : DEFAULT_DIAL_STYLE_SLUG;
  return [slug];
}
function variantDialFinishes(e: ScrapedWatchEntry): string[] | undefined {
  // Mirror mapEntryToReference's dialAndHands.finish derivation EXACTLY so the
  // parent's aggregate holds the same value written to every variant doc (and
  // thus what the functions-side trigger would later recompute — no divergence,
  // no extra write on the first variant trigger). An explicit scraped finish
  // wins; otherwise the finish lifted from a "<finish> <color>" dial-color
  // composite ("Sunburst Plum") or a reclassified finish-style (Sunburst/Gilt),
  // both already canonicalized via lookup_dial_finish (dialFinishResolver).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ea = e as any;
  const finish =
    e.dialFinish ||
    ea.dialFinish ||
    decomposeDialColor(e.dialColor).finish ||
    finishStyleSlug(detectDialStyle([e.dialColor, e.variant].filter(Boolean).join(' ')));
  return finish ? [finish] : undefined;
}

/**
 * Split one CSV-joined entry value into the distinct values it represents.
 *
 * When a source page lists a model's variants inline, the extractor collapses
 * them onto a single field — `caseMaterial: "steel, bronze, titanium"` — and the
 * importer used to store that whole string as one opaque scalar. The `*Variants`
 * arrays are the surface RN's "Also available with" rows read, but nothing
 * populated them outside the Shopify parent/child aggregation (see
 * aggregateVariantField), so extractor-sourced refs never showed the rows.
 *
 * Returns undefined unless ≥2 distinct pieces survive. A single value is already
 * covered by the ref's own scalar field, and RN subtracts the ref's own value
 * from these arrays before rendering, so a one-element array is guaranteed to
 * render nothing — writing it would be pure noise on every imported doc.
 */
function splitVariantValues(raw: unknown): string[] | undefined {
  if (typeof raw !== 'string' || !raw.includes(',')) return undefined;
  const out: string[] = [];
  for (const piece of raw.split(',')) {
    const t = piece.trim();
    if (t && !out.some((v) => v.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out.length >= 2 ? out : undefined;
}

/**
 * Dial-colour counterpart of splitVariantValues. Gated on the raw value actually
 * being CSV (so a themed single dial like "Panda" is never mistaken for a
 * variant list), then canonicalized through the SAME extractor the Shopify
 * parent aggregation uses so both paths produce identical slugs.
 */
function splitDialColorVariants(entry: ScrapedWatchEntry): string[] | undefined {
  if (typeof entry.dialColor !== 'string' || !entry.dialColor.includes(',')) {
    return undefined;
  }
  const colors = variantDialColors(entry);
  return colors && colors.length >= 2 ? colors : undefined;
}

/**
 * Parse a water-resistance string into canonical meters, detecting the unit from
 * the source string instead of guessing by magnitude. Fixes high-ATM divers like
 * Ballast "50 ATM" (50 > 30) that the old heuristic stored as 50m instead of 500m.
 *
 * Unit handling (case-insensitive; whitespace/punctuation between number and unit
 * is tolerated — "50ATM", "50 ATM", "50-atm", "50/ATM" all parse):
 *   - ATM / atmosphere(s) / bar  → value × 10   (1 ATM ≈ 1 bar ≈ 10m)
 *   - m / meter(s) / metre(s)    → value as-is
 *   - ft / feet / foot           → value ÷ 3.281, rounded to nearest 10m
 *   - no recognised unit suffix  → magnitude heuristic (unchanged legacy
 *                                   behaviour), with a console warning so brands
 *                                   shipping ambiguous bare numbers are spottable.
 */
function parseWaterResistance(
  raw: string | undefined,
  label?: string,
): { meters: number; key: string } | undefined {
  if (!raw) return undefined;
  const str = String(raw).trim();
  const numMatch = str.match(/(\d+(?:\.\d+)?)/);
  if (!numMatch || numMatch.index === undefined) return undefined;
  const value = parseFloat(numMatch[1]);
  if (isNaN(value)) return undefined;

  // Unit token = first run of letters after the number, skipping separators.
  const after = str.slice(numMatch.index + numMatch[1].length);
  const unit = (after.match(/^[\s\-_/.]*([a-z]+)/i)?.[1] ?? '').toLowerCase();

  let meters: number;
  if (/^(?:atm|atmosphere|atmospheres|bar)$/.test(unit)) {
    meters = Math.round(value * 10);
  } else if (/^(?:m|meter|meters|metre|metres)$/.test(unit)) {
    meters = Math.round(value);
  } else if (/^(?:ft|feet|foot)$/.test(unit)) {
    meters = Math.round(value / 3.281 / 10) * 10;
  } else {
    // No recognised unit — fall back to the legacy magnitude heuristic, but warn
    // so ambiguous brands surface in the import logs.
    meters = value <= 30 ? value * 10 : value;
    console.warn(
      `[BulkImport] Ambiguous water resistance "${raw}"${label ? ` (${label})` : ''} — no unit suffix; ` +
        `assumed ${value <= 30 ? 'ATM (×10)' : 'meters'} → ${meters}m`,
    );
  }
  return { meters, key: `${meters}m` };
}

// lookup_watch_types: normalized name (value / displayName / synonym) → canonical
// value slug. Built once per import run from the live lookup so the derivation's
// loose hints ("ana-digi", "pocket watch", "smartwatch") resolve to whatever the
// admin has actually configured as the canonical slug — never hardcoded here.
let watchTypeResolver = new Map<string, string>();

function buildWatchTypeResolver(
  values: { value?: string; displayName?: string; synonyms?: string[] }[],
): void {
  const byKey = new Map<string, string>();
  const add = (raw: string | undefined, canonical: string) => {
    const key = normStyleKey(raw ?? '');
    if (!key || byKey.has(key)) return;
    byKey.set(key, canonical);
  };
  // Three precedence passes so a direct value/displayName key is never shadowed
  // by another entry's synonym (mirrors buildDialColorResolver's addPass order).
  for (const v of values) if (v.value) add(v.value, v.value);
  for (const v of values) if (v.value && v.displayName) add(v.displayName, v.value);
  for (const v of values)
    if (v.value) for (const s of v.synonyms ?? []) add(s, v.value);
  watchTypeResolver = byKey;
}

// Resolve a loose watch-type hint to its canonical lookup_watch_types slug by
// matching value / displayName / synonym (case-insensitive, punctuation-folded).
// Returns undefined when nothing matches (or the lookup wasn't loaded).
function resolveWatchType(hint: string | undefined): string | undefined {
  const key = normStyleKey(hint ?? '');
  if (!key) return undefined;
  return watchTypeResolver.get(key);
}

/**
 * Derive a loose watch-type HINT from the scraped fields when a watchType wasn't
 * explicitly extracted. The hint is resolved to a canonical lookup_watch_types
 * slug by deriveWatchType (via resolveWatchType) — NOT a canonical slug itself,
 * so the exact spelling need only match the lookup's value/displayName/synonyms.
 *
 * Signals (checked in specificity order):
 *  - "smartwatch"       — an OS token (Wear OS, watchOS, Snapdragon Wear) or a
 *                         "smartwatch" product-type / tag.
 *  - "activity tracker" — a fitness/activity-tracker token.
 *  - "ana-digi"         — an "ana-digi" / "analog-digital" / "hybrid" token, OR
 *                         the watch has BOTH analog hands AND an electronic module.
 *  - "digital"          — an explicit digital movement type, OR an electronic
 *                         module is present (a module is the digital counterpart
 *                         of a calibre, so its presence alone implies a digital dial).
 *  - "stopwatch"        — a "stopwatch" token.
 *  - "pocket watch"     — a "pocket watch" token, or a "railroad" token on a
 *                         strapless watch (pocket watches carry no strap material).
 *  - "analog"           — the default for everything else (mechanical/quartz).
 */
function deriveWatchTypeHint(entry: ScrapedWatchEntry): string {
  const text = [
    entry.description,
    entry.title,
    entry.productName,
    entry.variant,
    entry.url,
    ...(entry.tags ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const movementType = (entry.movementType || '').trim().toLowerCase();
  const hasModule = !!(entry.module && entry.module.trim());
  const hasHands = !!(entry.hands && entry.hands.trim());
  const hasStrap = !!(entry.strapMaterial && entry.strapMaterial.trim());

  // Smart watch — OS token or an explicit smartwatch product type/tag.
  if (
    /\bwear\s?os\b|\bwatch\s?os\b|\bsnapdragon\s+wear\b|\bsmart[\s-]?watch\b|\bsmartwatch\b/.test(text)
  ) {
    return 'smartwatch';
  }

  // Activity tracker — a fitness/activity band token (no analog/digital dial).
  if (/\bactivity\s+tracker\b|\bfitness\s+tracker\b|\bactivity\s+band\b/.test(text)) {
    return 'activity tracker';
  }

  // Analog-digital — an explicit ana-digi/hybrid token, or both an analog dial
  // (hands) and an electronic module present on the same watch.
  if (/\bana[\s-]?digi\b|\banalog[\s-]?digital\b|\bhybrid\b/.test(text) || (hasHands && hasModule)) {
    return 'ana-digi';
  }

  // Digital — explicit digital movement, or an electronic module with no analog
  // hands (module ⇒ electronic; without hands it's a pure-digital display).
  if (movementType === 'digital' || hasModule) {
    return 'digital';
  }

  // Stopwatch — an explicit stopwatch token.
  if (/\bstop[\s-]?watch\b|\bstopwatch\b/.test(text)) {
    return 'stopwatch';
  }

  // Pocket watch — an explicit token, or a "railroad" grade watch with no strap.
  if (/\bpocket[\s-]?watch\b|\bpocketwatch\b/.test(text) || (/\brailroad\b/.test(text) && !hasStrap)) {
    return 'pocket watch';
  }

  return 'analog';
}

/**
 * Derive the watch's canonical `watchType` slug. An explicitly-extracted
 * watchType always wins — this only ever FILLS an unset value, never overrides
 * one. Both the explicit value and the derived hint are canonicalized through
 * the lookup_watch_types resolver (see deriveWatchTypeHint / resolveWatchType):
 *  - explicit set & resolves      → canonical slug
 *  - explicit set but no match    → the explicit value verbatim (never dropped)
 *  - derived hint resolves        → canonical slug
 *  - derived hint has no match    → 'analog' (the fallback default)
 */
function deriveWatchType(entry: ScrapedWatchEntry): string {
  const explicit = entry.watchType?.trim();
  if (explicit) return resolveWatchType(explicit) ?? explicit;

  const hint = deriveWatchTypeHint(entry);
  return resolveWatchType(hint) ?? 'analog';
}

/** What one reference's gallery merge actually did, for the run summary. */
interface GalleryImportResult {
  /** Images fetched, uploaded and appended to the gallery. */
  added: number;
  /** Images already in the gallery that gained the extractor's tags. */
  tagged: number;
  /** Galleries rebuilt from scratch — 0 or 1, since this runs per reference. */
  replaced: number;
  /** Images that could not be fetched, with why — surfaced in the run summary. */
  failures: Array<{ url: string; error: string }>;
}

const NO_GALLERY_CHANGE: GalleryImportResult = { added: 0, tagged: 0, replaced: 0, failures: [] };

/**
 * Most selected images the preview checks on its own when it opens; above this
 * the "Check image fetches" button does it on request. Each one is a real fetch
 * from the brand's host.
 */
const AUTO_IMAGE_PROBE_MAX = 250;

/** References retried at once by the done step's "Retry failed images". */
const RETRY_IMAGE_CONCURRENCY = 4;

/**
 * Merge an entry's extra product shots into the ref's `galleryImages`, carrying
 * the extractor's inferred tags.
 *
 * Runs on EVERY import path — create, merge, and overwrite — and is not gated on
 * the overwrite toggle. Tags are additive metadata about images rather than a
 * value the scrape overwrites, and the references that need them most are the
 * hundreds already imported before the extractors could infer any. See
 * planGalleryMerge for the merge rules; the short version is that it only ever
 * adds, so a re-import can't duplicate an image, clobber a curator's tags, or
 * delete a shot the source stopped serving.
 *
 * Per-image failure is non-fatal and does not abort the rest: images come from
 * hosts that block hotlinking (Akamai/VCA), and losing one gallery shot must not
 * cost the ones that did download. Tag backfill still lands even when every
 * upload fails, since it needs no bytes.
 */
async function importGalleryImages(
  brandId: string,
  referenceId: string,
  entry: ScrapedWatchEntry,
  primaryImageUrl: string,
  existingGallery: ImageInfo[] | undefined,
  replaceAll: boolean,
): Promise<GalleryImportResult> {
  const candidates = selectGalleryImageCandidates(
    entry.taggedImages,
    primaryImageUrl,
    (tag, url) =>
      console.warn(
        `Unknown gallery image tag "${tag}" on ${entry.reference} (${url}) — dropped`,
      ),
  );
  // Guards BOTH modes, and carries most of the weight in replace mode: a scrape
  // that surfaced no images at all must leave the gallery alone rather than
  // rebuild it as empty. An extractor regression is not a delete instruction.
  if (candidates.length === 0) return NO_GALLERY_CHANGE;

  if (replaceAll) {
    return replaceGalleryImages(brandId, referenceId, entry, existingGallery, candidates);
  }

  const held = existingGallery ?? [];
  const plan = planGalleryMerge(held, candidates);
  if (plan.tagUpdates.length === 0 && plan.toAdd.length === 0) return NO_GALLERY_CHANGE;

  // One call for the whole reference: the server fetches and stores these
  // concurrently, where the browser used to do them one after another.
  const outcomes = await ingestReferenceImages(
    brandId,
    plan.toAdd.map((candidate, i) => ({
      referenceId,
      imageUrl: candidate.url,
      kind: 'gallery' as const,
      index: held.length + i,
    })),
  );
  const uploaded: ImageInfo[] = [];
  const failures: GalleryImportResult['failures'] = [];
  plan.toAdd.forEach((candidate, i) => {
    const outcome = outcomes[i];
    if (!outcome.ok) {
      console.warn(
        `Gallery image upload failed for ${entry.reference} (${candidate.url}):`,
        outcome.error,
      );
      failures.push({ url: candidate.url, error: outcome.error });
      return;
    }
    uploaded.push({
      storagePath: outcome.storagePath,
      contentType: outcome.contentType,
      index: held.length + uploaded.length,
      // Omit the key rather than write undefined — an untagged image is one
      // the curator still has to tag, and `tags: undefined` would be rejected
      // by the Firestore write.
      ...(candidate.tags.length > 0 ? { tags: candidate.tags } : {}),
      sourceUrl: candidate.url,
    });
  });
  if (uploaded.length === 0 && plan.tagUpdates.length === 0) return { ...NO_GALLERY_CHANGE, failures };

  const tagsByIndex = new Map(plan.tagUpdates.map((u) => [u.index, u.tags]));
  const merged: ImageInfo[] = [
    ...held.map((img, i) => {
      const tags = tagsByIndex.get(i);
      return tags ? { ...img, tags } : img;
    }),
    ...uploaded,
  ];

  await updateDoc(doc(db, 'watchBrands', brandId, 'references', referenceId), {
    galleryImages: merged,
    updatedAt: Timestamp.now(),
  });
  return { added: uploaded.length, tagged: plan.tagUpdates.length, replaced: 0, failures };
}

/**
 * Rebuild a reference's `galleryImages` from the current scrape — the
 * "Replace all images" path.
 *
 * Exists because the additive merge above cannot fix a gallery that is WRONG.
 * Once an extractor's image extraction improves, the shots already stored are
 * the ones to be rid of, and every rule the merge planner enforces (never
 * remove, only fill free slots) works against that. See planGalleryReplace for
 * what survives the rebuild anyway and why.
 *
 * The failure posture is inverted from the merge path's, because the stakes are:
 * a merge that uploads nothing has simply not helped, whereas a replace that
 * uploads nothing and writes anyway has DESTROYED a gallery. So:
 *
 *   - a candidate whose fetch fails degrades to the held image it was replacing,
 *     when there is one — bytes already in Storage are never traded for a 403;
 *   - if that leaves the gallery byte-for-byte what it already was, nothing is
 *     written and the run reports no change, so a brand whose host blocks us
 *     wholesale (VCA/Akamai) is a no-op rather than a wipe.
 */
async function replaceGalleryImages(
  brandId: string,
  referenceId: string,
  entry: ScrapedWatchEntry,
  existingGallery: ImageInfo[] | undefined,
  candidates: GalleryImageCandidate[],
): Promise<GalleryImportResult> {
  const held = existingGallery ?? [];
  const plan = planGalleryReplace(held, candidates);
  if (plan.length === 0) return NO_GALLERY_CHANGE;

  // Every slot that needs bytes goes to the server in one call, so they are
  // fetched and stored concurrently; the walk below then keeps the plan's order.
  const fetchSlots = plan.flatMap((slot, position) =>
    slot.kind === 'keep' ? [] : [{ position, url: slot.url }],
  );
  const outcomes = await ingestReferenceImages(
    brandId,
    fetchSlots.map(({ position, url }) => ({
      referenceId,
      imageUrl: url,
      kind: 'gallery' as const,
      index: position,
    })),
  );
  const outcomeByPosition = new Map(fetchSlots.map(({ position }, i) => [position, outcomes[i]]));

  const next: ImageInfo[] = [];
  const failures: GalleryImportResult['failures'] = [];
  let fetched = 0;
  plan.forEach((slot, position) => {
    if (slot.kind === 'keep') {
      next.push(imageForWrite(slot.image, { index: next.length }));
      return;
    }
    const outcome = outcomeByPosition.get(position);
    if (outcome?.ok) {
      next.push({
        storagePath: outcome.storagePath,
        contentType: outcome.contentType,
        index: next.length,
        ...(slot.tags.length > 0 ? { tags: slot.tags } : {}),
        sourceUrl: slot.url,
      });
      fetched += 1;
      return;
    }
    console.warn(
      `Gallery image replace failed for ${entry.reference} (${slot.url}):`,
      outcome?.error,
    );
    failures.push({
      url: slot.url,
      error: outcome && !outcome.ok ? outcome.error : 'No result returned',
    });
    // Q2: keep the bytes we already own rather than dropping the slot.
    if (slot.fallback) next.push(imageForWrite(slot.fallback, { index: next.length }));
  });

  // Nothing new landed AND the array is the same set of storage paths in the
  // same order — a write here would only burn a Firestore op and stamp
  // updatedAt, and would report the reference as 'updated' in the summary.
  if (fetched === 0 && sameStoragePaths(held, next)) return { ...NO_GALLERY_CHANGE, failures };

  await updateDoc(doc(db, 'watchBrands', brandId, 'references', referenceId), {
    galleryImages: next,
    updatedAt: Timestamp.now(),
  });
  return { added: fetched, tagged: 0, replaced: 1, failures };
}

/** Whether two image arrays point at exactly the same Storage objects, in order. */
function sameStoragePaths(a: readonly ImageInfo[], b: readonly ImageInfo[]): boolean {
  return a.length === b.length && a.every((img, i) => img?.storagePath === b[i]?.storagePath);
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Retry one reference's failed images after the run — the done step's "Retry
 * failed images". Returns what is STILL failing (empty when all of it landed).
 *
 * Re-attempts only what the run itself tried and lost:
 *   - the hero goes to `images[0]` exactly as the import would have written it
 *     (and clears `pendingImageUrl`, which the failed attempt set);
 *   - the gallery re-runs the additive merge against the gallery as it is NOW
 *     (re-read, since the run's copy is stale), so a retry can only add shots —
 *     never the "Replace all images" rebuild, even if the run was one.
 */
async function retryImportImageFailureRow(
  row: ImportImageFailureRow<ScrapedWatchEntry>,
): Promise<ImportImageFailure[]> {
  const remaining: ImportImageFailure[] = [];
  const docRef = doc(db, 'watchBrands', row.brandId, 'references', row.referenceId);

  const mainFailure = row.failures.find((f) => f.kind === 'main');
  if (mainFailure) {
    try {
      const { storagePath, contentType } = await ingestReferenceImage(row.brandId, {
        referenceId: row.referenceId,
        imageUrl: mainFailure.url,
        kind: 'main',
        index: 0,
      });
      const heroTags = selectHeroImageTags(row.entry.taggedImages, mainFailure.url);
      await updateDoc(docRef, {
        images: [{ storagePath, contentType, index: 0, ...(heroTags.length > 0 ? { tags: heroTags } : {}) }],
        pendingImageUrl: deleteField(),
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      remaining.push({ ...mainFailure, error: errorText(err) });
    }
  }

  const galleryFailures = row.failures.filter((f) => f.kind === 'gallery');
  if (galleryFailures.length > 0) {
    try {
      const snapshot = await getDoc(docRef);
      const held = snapshot.exists()
        ? (snapshot.data().galleryImages as ImageInfo[] | undefined)
        : undefined;
      const result = await importGalleryImages(
        row.brandId,
        row.referenceId,
        row.entry,
        mainFailure?.url ?? row.entry.imgSrc ?? '',
        held,
        false,
      );
      const failedBefore = new Set(galleryFailures.map((f) => f.url));
      for (const f of result.failures) {
        if (failedBefore.has(f.url)) remaining.push({ kind: 'gallery', url: f.url, error: f.error });
      }
    } catch (err) {
      const error = errorText(err);
      remaining.push(...galleryFailures.map((f) => ({ ...f, error })));
    }
  }
  return remaining;
}

function mapEntryToReference(
  entry: ScrapedWatchEntry,
  brandId: string,
  brandName: string,
  flags: CalibreDualWriteFlags,
) {
  // ── Numeric parsing (guard against "XX" and empty strings) ──────────────────
  const rawSize = parseFloat(entry.caseSize ?? '');
  const diameter = isNaN(rawSize) ? undefined : rawSize;

  const rawHeight = parseFloat(entry.caseHeight ?? '');
  const caseHeight = isNaN(rawHeight) ? undefined : rawHeight;

  // Convert water resistance: detect the unit from the source string (ATM/bar/m/ft)
  // rather than guessing by magnitude, so high-ATM divers ("50 ATM" → 500m) are
  // correct. Bare numbers fall back to the legacy heuristic (see parseWaterResistance).
  const wr = parseWaterResistance(entry.waterRes, entry.reference || entry.model);
  const wrMeters = wr?.meters;
  const wrKey = wr?.key; // "50m", "100m", "200m", "500m"

  const rawLugWidth = parseFloat(entry.lugWidth ?? '');
  const lugWidth = isNaN(rawLugWidth) ? undefined : rawLugWidth;

  const rawLugToLug = parseFloat(entry.lugToLug ?? '');
  const lugToLug = isNaN(rawLugToLug) ? undefined : rawLugToLug;

  const rawJewels = parseInt(entry.jewels ?? '', 10);
  const jewels = isNaN(rawJewels) ? undefined : rawJewels;

  const rawPrice = entry.price !== undefined && entry.price !== null && entry.price !== ''
    ? parseFloat(String(entry.price))
    : NaN;
  const retailPrice = isNaN(rawPrice) ? undefined : rawPrice;

  // ── Derived values ───────────────────────────────────────────────────────────
  // Case material: prefer explicit caseMaterial field, fall back to parseCaseMat(caseMat code)
  const { material: matFromCode, coating: coatingFromCode } = parseCaseMat(entry.caseMat ?? '');
  const caseMaterial = entry.caseMaterial || matFromCode;
  const caseCoating = entry.caseCoating || coatingFromCode;

  // Dial color + finish: decompose entry.dialColor into canonical color(s) plus
  // an optional finish lifted from a "<finish> <color>" composite ("Sunburst
  // Plum" → finish "sunburst" + color "plum"; the raw composite is dropped). A
  // brand-specific raw with no finish prefix ("Maelstrom Blue") is preserved.
  // Mirrors the dialColorBackfill algorithm so a re-import of an already-
  // backfilled brand is a no-op. (entry.dialColor may also be a code like BLK.)
  const dialDecomp = decomposeDialColor(entry.dialColor);

  // Dial style: detect a themed style from the variant's dial option / label
  // (data-driven). EVERY ref gets a dialStyle — the themed slug or single-color.
  // A stale finish-style (Sunburst/Gilt that should have been pruned from
  // lookup_dial_styles into lookup_dial_finish) is reclassified: its slug becomes
  // the finish and the style falls back to single-color.
  const dialStyleHit = detectDialStyle(
    [entry.dialColor, entry.variant].filter(Boolean).join(' '),
  );
  const promotedFinish = finishStyleSlug(dialStyleHit);
  const dialStyle = promotedFinish
    ? DEFAULT_DIAL_STYLE_SLUG
    : (dialStyleHit?.slug ?? DEFAULT_DIAL_STYLE_SLUG);
  // A themed style with constituent colors (its lookup `properties`, e.g. Panda →
  // black+white) overrides the decomposed colors; a reclassified finish-style
  // does not (finishes carry no properties).
  const themedColors =
    !promotedFinish && dialStyleHit && dialStyleHit.properties.length > 0
      ? dialStyleHit.properties
      : [];
  const dialColor =
    themedColors.length > 0
      ? themedColors.join(', ')
      : dialDecomp.colors.length > 0
        ? dialDecomp.colors.join(', ')
        : undefined;

  // ── Auto-detect from description/title text ──────────────────────────────────
  const allText = [entry.description, entry.title, entry.productName, entry.variant]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Chronograph detection
  const isChronograph = allText.includes('chrono');

  // GMT detection
  const isGMT = allText.includes('gmt') || allText.includes('greenwich mean time');

  // Luminova detection
  const hasLuminova =
    allText.includes('luminova') ||
    allText.includes('lume') ||
    allText.includes('super-luminova') ||
    allText.includes('superluminova') ||
    allText.includes('luminescent') ||
    allText.includes('luminescente');

  // Calibre string + link — the decision lives in importCalibreFields (tested):
  // the explicit calibre NAME whole, else the code in scraped movement prose;
  // an unlinked row always keeps its string.
  const {
    calibre: calibreToWrite,
    calibreId: linkedCalibreId,
    calibreName: calibre,
  } = importCalibreFields(entry, flags);

  // Watch type: an explicitly-extracted watchType always wins; otherwise it's
  // derived from movement type, module/hands presence, and title/description
  // tokens (see deriveWatchType). Drives the RN Electronics `showModule` /
  // Dial & Hands `showDial` gates.
  const watchType = deriveWatchType(entry);

  // Source casing, verbatim — see referenceNaming.ts for why these are no
  // longer title-cased.
  // Names are cleaned of HTML-like markup HERE, in the one mapping step every
  // import source passes through (Shopify, file, JSON feed, extractor), so a
  // feed that wraps its titles in theme tags (`<tc>ALLIANCE</tc>`) cannot put
  // them on a reference. The reference number is deliberately not touched.
  // `series` is derived from the cleaned model, so it is clean too.
  const model = normalizeSourceName(stripInlineMarkup(entry.model));
  const collection = normalizeSourceName(stripInlineMarkup(entry.collection));
  const series = deriveSeries(model);
  // The preview grid's editable "Name" field writes entry.productName, so it
  // must win here — otherwise inline edits to the product name were dropped on
  // save (the mapper previously read only entry.title). Falls back to the
  // scraped title / variant description when unset.
  const productName = stripInlineMarkup(entry.productName || entry.title || entry.variantDesc || '');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ea = entry as any; // rule-applied fields live on entry as dynamic props
  const toBool = (v: unknown) => v === true || v === 'true' || undefined;
  // Accepts a number or a string, and tolerates the thousands separator the
  // source writes ("1,000") — a bare parseInt stops at the comma and records
  // an edition of one.
  const limitedEditionSize = (v: unknown): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = parseInt(String(v).replace(/[.,\s]/g, ''), 10);
    return Number.isNaN(n) || n <= 0 ? undefined : n;
  };

  // ── Nested objects — only map fields that have actual values from the entry ──
  const caseInfo = stripEmpty({
    diameter,
    diameterMm: diameter,
    sizeMm: diameter,
    height: caseHeight,
    heightMm: caseHeight,
    material: caseMaterial,
    shape: entry.caseShape || undefined,
    finish: entry.caseFinish || undefined,
    coating: caseCoating,
    waterResistance: wrKey,
    waterResistanceM: wrMeters,
    lugWidth,
    lugWidthMm: lugWidth,
    lugToLug,
    lugToLugMm: lugToLug,
    caseBack: entry.caseBack || undefined,
    caseback: entry.caseBack || undefined,
    // Resistance to LOW pressure — a sealed case that stays sealed as ambient
    // pressure DROPS, which is the opposite direction from waterResistance.
    // A boolean and not a rating: Sinn, the source that publishes it, states
    // the bare phrase "Low pressure resistant" on 197 of 207 archive refs and
    // never a figure — every number on that line is water pressure.
    isLowPressureResistant: toBool(ea.isLowPressureResistant) || undefined,
  });

  const rawPowerReserve = parseInt(entry.powerReserve ?? '', 10);
  const powerReserveHours = isNaN(rawPowerReserve) ? undefined : rawPowerReserve;

  // Merge calibre DB data if available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const calDb = (entry as any)?._calibreData as Record<string, unknown> | undefined;


  // When calibre DB data exists, it's the authoritative source for movement specs.
  // Scraped values (entry.movement, entry.movementType, etc.) are only fallbacks.
  const movementInfo = stripEmpty({
    baseMovement: calDb?.baseMovement as string || undefined,
    type: calDb?.movementType as string || entry.movementType || undefined,
    jewels: calDb?.jewels as number ?? jewels ?? undefined,
    powerReserveHours: calDb?.powerReserveHours as number ?? powerReserveHours ?? undefined,
    frequencyVph: calDb?.frequencyVph as number || undefined,
    diameterMm: calDb?.diameterMm as number || undefined,
    heightMm: calDb?.heightMm as number || undefined,
    hacking: calDb?.hacking as boolean || toBool(ea.hacking) || undefined,
    manualWinding: calDb?.manualWinding as boolean || toBool(ea.manualWinding) || undefined,
    quickSetDate: calDb?.quickSetDate as boolean || toBool(ea.quickSetDate) || undefined,
    windingDirection: calDb?.windingDirection as string || undefined,
    isChronograph: Boolean(calDb?.isChronograph as boolean || toBool(ea.isChronograph) || isChronograph),
    isChronometer: calDb?.isChronometer as boolean || toBool(ea.chronometer) || undefined,
    isCosc: calDb?.isCosc as boolean || undefined,
    // Dual-name trap shim: shared-types carries both spellings and the RN app's
    // COSC certification chip reads only `isCOSC`. Writing just the admin
    // spelling meant every imported ref rendered with no COSC row.
    isCOSC: calDb?.isCosc as boolean || undefined,
    isGlashuetteRegulation: calDb?.isGlashuetteRegulation as boolean || undefined,
    isGMT: Boolean(calDb?.isGMT as boolean || toBool(ea.isGMT) || isGMT),
    isInHouse: calDb?.isInHouse as boolean || toBool(ea.isInHouse) || undefined,
    columnWheel: calDb?.columnWheel as boolean || toBool(ea.columnWheel) || undefined,
    doubleBarrel: calDb?.doubleBarrel as boolean || toBool(ea.doubleBarrel) || undefined,
    liftAngleDegrees: calDb?.liftAngleDegrees as number || undefined,
    escapement: calDb?.escapement as string || undefined,
    antiShockSystem: calDb?.antiShockSystem as string || undefined,
    rotorType: calDb?.rotorType as string || undefined,
    regulatorSystem: calDb?.regulatorSystem as string || undefined,
    countryOfManufacture: calDb?.countryOfManufacture as string || undefined,
    serviceIntervalYears: calDb?.serviceIntervalYears as number || undefined,
    lignes: calDb?.lignes as string || undefined,
    casingDiameterMm: calDb?.casingDiameterMm as number || undefined,
    manualWindingDirection: calDb?.manualWindingDirection as string || undefined,
    hairspring: calDb?.hairspring as string || undefined,
    batteryType: calDb?.batteryType as string || undefined,
    batteryLifeYears: calDb?.batteryLifeYears as number || undefined,
    // Array fields from calibre DB
    date: calDb?.date as string[] || undefined,
    hands: calDb?.hands as string[] || undefined,
    chronograph: calDb?.chronograph as string[] || undefined,
    additionals: calDb?.additionals as string[] || undefined,
    astronomical: calDb?.astronomical as string[] || undefined,
    acoustic: calDb?.acoustic as string[] || undefined,
  });

  const dialAndHandsInfo = stripEmpty({
    color: dialColor,
    hands: entry.hands || undefined,
    handsColor: entry.handsColor || undefined,
    material: entry.dialMaterial || undefined,
    // Explicit scraped finish wins; otherwise the finish lifted from a dial-color
    // composite ("Sunburst Plum") or a reclassified finish-style (Sunburst/Gilt).
    finish:
      entry.dialFinish || ea.dialFinish || dialDecomp.finish || promotedFinish || undefined,
    // Marker type / marker style persist as string[] (shared-types v1.51.0).
    // The scrape carries one comma-joined string per field, so split it here —
    // writing the raw scalar is what the migrateDialIndexesNumerals sweep has
    // to clean up afterwards.
    indexes: dialMarkerValuesForWrite(parseDialMarkerValues(entry.dialIndexes)),
    numerals: dialMarkerValuesForWrite(parseDialMarkerValues(entry.dialNumerals)),
    luminova: Boolean(hasLuminova),
    // Dual-name trap shim — the RN app reads only `lume`, so an imported ref
    // carrying just `luminova` renders with no "Luminous" row.
    lume: Boolean(hasLuminova),
    // Date complication, persisted verbatim — the first four are single-select
    // slugs and the fifth is stored in exactly the comma-joined form it arrives
    // in (unlike indexes/numerals above, which are arrays in storage and so have
    // to be split here). Nothing to split or canonicalize either way. stripEmpty
    // drops whichever the scrape didn't carry, which today is usually all five.
    dateDisplay: entry.dateDisplay || undefined,
    dateWindowPosition: entry.dateWindowPosition || undefined,
    dateWindowFrame: entry.dateWindowFrame || undefined,
    dateWheelColor: entry.dateWheelColor || undefined,
    dateWheelTextColor: entry.dateWheelTextColor || undefined,
    // Lume detail, persisted verbatim for the same reason as the date block
    // above — four single-select slugs, nothing to split or canonicalize.
    // Deliberately independent of the `luminova`/`lume` booleans a few lines
    // up: those are text-detected from the whole page ("Super-LumiNova"
    // appearing anywhere sets them), whereas these come from a spec row or the
    // AI and are the ones that carry an actual answer.
    lumePresence: entry.lumePresence || undefined,
    lumeMaterial: entry.lumeMaterial || undefined,
    lumeDayColor: entry.lumeDayColor || undefined,
    lumeGlowColor: entry.lumeGlowColor || undefined,
    // Subdial functions, persisted verbatim — it arrives comma-joined and is
    // stored comma-joined, so there is nothing to split or re-join here (the
    // extractor's coerceExtractedString already flattened an array answer).
    subdialTypes: entry.subdialTypes || undefined,
    // Chapter ring & dial furniture, persisted verbatim. `dialScales` arrives
    // comma-joined and is stored comma-joined, so it needs no more handling
    // than the three single-select slugs beside it.
    chapterRing: entry.chapterRing || undefined,
    minuteTrack: entry.minuteTrack || undefined,
    dialScales: entry.dialScales || undefined,
    dialFeatures: entry.dialFeatures || undefined,
    logoType: entry.logoType || undefined,
  });

  const strapInfo = stripEmpty({
    material: entry.strapMaterial || undefined,
    color: entry.strapColor || undefined,
  });

  // Production run. The entry carries flat 4-digit year strings (extractor JSON
  // import); WatchReference.production stores them as numbers.
  const rawStartYear = parseInt(entry.productionStartYear ?? '', 10);
  const rawEndYear = parseInt(entry.productionEndYear ?? '', 10);
  const rawReleaseYear = parseInt(entry.productionReleaseYear ?? '', 10);
  const productionInfo = stripEmpty({
    startYear: isNaN(rawStartYear) ? undefined : rawStartYear,
    endYear: isNaN(rawEndYear) ? undefined : rawEndYear,
    releaseYear: isNaN(rawReleaseYear) ? undefined : rawReleaseYear,
    isLimited: toBool(ea.isLimited) || undefined,
    // THE EDITION SIZE HAS THREE NAMES and they meet here. `limitedSize` is
    // storage (ProductionInfo); `limitedEditionCount` is what the enrichment
    // prompt offers and what ExtractedWatch carries since shared-types
    // v1.124.0; `limitedQty` is this importer's own older rule-field name.
    // Both inputs are accepted so an extractor emitting the shared-types name
    // is not a silent no-op, and existing import rules keyed on limitedQty
    // keep working.
    limitedSize: limitedEditionSize(ea.limitedEditionCount ?? ea.limitedQty),
  });

  // The curator's ticked AI values for registry-path fields go on LAST, over
  // the importer's own detection (see applyAiFieldPatch).
  return applyAiFieldPatch(stripEmpty({
    brandId,
    reference: entry.reference,
    watchType,
    movementType: entry.movementType || undefined,
    // Crown Type / Crystal persist as string[] (shared-types v1.53.0). The
    // extractors have not been updated for the split and may never be — Rolex
    // still emits one comma-joined phrase ("Screw-down, Twinlock double
    // waterproofness system") — so ingest resolves it to slugs here, using the
    // SAME matcher migrateCrownTypeCrystal uses. Scalar, comma-joined scalar
    // and array all normalize to string[]; a freshly imported ref therefore
    // needs no migration run against it.
    //
    // Depends on loadMultiSelectVocabularies having run for this import (see
    // handleImport). Unloaded, values degrade to raw split text rather than
    // being mis-mapped.
    //
    // Note the return type of this function is INFERRED, so nothing here is
    // checked against WatchReference: get the shape right by hand.
    crownType: normalizeMultiSelectValue(entry.crownType, 'lookup_crown_types'),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    bezelType: (entry as any).bezelType || undefined,
    // Bezel material lands on `bezel` — the historical (and only) key any
    // reader uses for it. Colours are multi-select and persist as string[],
    // so the flat scraped string is split here; writing the raw scalar is
    // exactly what the crown/crystal migration had to clean up afterwards.
    bezel: entry.bezelMaterial || undefined,
    bezelColors: normalizeMultiSelectValue(entry.bezelColors, 'lookup_bezel_colors'),
    caseColors: normalizeMultiSelectValue(entry.caseColors, 'lookup_case_colors'),
    model,
    collection,
    productName,
    description: entry.description || undefined,
    dial: dialColor,
    dialStyle: dialStyle || undefined,
    glass: entry.glass || undefined,
    crystal: normalizeMultiSelectValue(entry.crystal, 'lookup_crystal_materials'),
    // The legacy free-text calibre. `dualWriteLegacyCalibreString` retires it
    // ONLY where a link replaces it: an import is create-only, so dropping the
    // string on a row that resolved to nothing would leave the reference with
    // neither a link nor a name and lose the scrape outright. Where the link
    // exists the string is redundant, which is exactly what the flag is for.
    calibre: calibreToWrite,
    calibreId: linkedCalibreId,
    // Tier auto-detected from the calibre string at calibre-check time (see
    // detectTierFromCalibreString). stripEmpty drops it when unset.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    calibreTierId: ((entry as any)._calibreTierId as string) || undefined,
    series,
    nickname: stripInlineMarkup(entry.variant) || undefined,
    specialEdition: toBool(ea.specialEdition) || undefined,
    retailPrice,
    tags: entry.tags && entry.tags.length > 0 ? entry.tags : undefined,
    movement: Object.keys(movementInfo).length > 0 ? movementInfo : undefined,
    case: Object.keys(caseInfo).length > 0 ? caseInfo : undefined,
    dialAndHands: Object.keys(dialAndHandsInfo).length > 0 ? dialAndHandsInfo : undefined,
    strap: Object.keys(strapInfo).length > 0 ? strapInfo : undefined,
    // Variant surfacing for extractor-sourced refs. A model whose variants are
    // listed inline arrives with the values comma-joined on one field; split
    // them onto the `*Variants` arrays RN reads for its "Also available with"
    // rows. The scalar field is deliberately left as-is (RN reads both, and the
    // scalar is what every other consumer and the merge diff key off).
    //
    // Purely additive to the Shopify parent/child path: those entries carry one
    // value per field so nothing here fires, and upsertParentReference
    // overwrites these keys with the real cross-variant aggregate anyway.
    // computeParentAggregates stays authoritative for parents.
    dialColorVariants: splitDialColorVariants(entry),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    movementVariants: splitVariantValues((entry as any).calibre),
    strapTypeVariants: splitVariantValues(entry.strapMaterial),
    caseMaterialVariants: splitVariantValues(caseMaterial),
    functions: (() => {
      // Functions/complications extracted from the source page (comma-separated
      // string on the entry) are merged with the text-detected (chrono/GMT) and
      // calibre-derived ones. Without this, per-row extracted complications were
      // silently dropped on save.
      const extractedFns = (entry.functions || '')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);
      const fns = [
        ...(isChronograph ? ['Chronograph'] : []),
        ...(isGMT ? ['GMT'] : []),
        ...extractedFns,
        ...((calDb?.functions as string[]) || []),
      ];
      const unique = [...new Set(fns)];
      return unique.length > 0 ? unique : undefined;
    })(),
    // stripEmpty always returns an object, so the old `|| undefined` never
    // fired and every import wrote `production: {}`. Matches the nested-object
    // pattern used by case/movement/dial/strap above: omit the key entirely
    // when nothing was extracted.
    production: Object.keys(productionInfo).length > 0 ? productionInfo : undefined,
    source: entry.url ? { url: entry.url } : undefined,
    // Dedicated field the RN explore detail reads directly for the "View
    // product page" link. Kept in addition to the legacy `links[]` entry below
    // (purely additive) so older RN clients still resolve the URL.
    productUrl: entry.url || undefined,
    links: entry.url
      ? [{ url: entry.url, title: 'Product Page', type: 'official' }]
      : undefined,
    searchText: computeSearchText(
      brandName,
      entry.reference,
      model,
      calibre,
      undefined,
      diameter,
      collection,
    ),
  }), entry._aiFields);
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface BulkImportStats {
  addedCount: number;
  updatedCount: number;
  /**
   * Refs that existed and whose source data diverged from stored (curated)
   * values, but where nothing was written (curated values preserved). Purely
   * informational — not part of added/updated/unchanged.
   */
  divergedCount: number;
  duplicateCount: number;
  errorCount: number;
  excludedCount: number;
  /**
   * Parent/variant breakdown of the create/update totals (Shopify variant
   * imports). parents + variants sum to addedCount / updatedCount respectively.
   * Absent/zero for non-variant imports.
   */
  parentsAdded?: number;
  variantsAdded?: number;
  parentsUpdated?: number;
  variantsUpdated?: number;
  parentsDiverged?: number;
  variantsDiverged?: number;
}

export interface StoreCategoryPickerConfig {
  /** Category id → name and → parent id (WooCommerce), for "Parent › Child" labels. */
  hierarchy?: { names: ReadonlyMap<string, string>; parents: ReadonlyMap<string, string> };
  /** The source's saved excluded category ids; undefined = never saved (heuristic defaults). */
  savedExcluded?: string[];
  /** Called after the import with the ids left unticked, so the source remembers them. */
  onExclusionsSaved?: (excluded: string[]) => void;
}

interface BulkImportProps {
  brand: WatchBrand;
  onComplete: (stats?: BulkImportStats) => void;
  onBrandUpdated?: (updatedBrand: WatchBrand) => void;
  /** Hide the default 'Bulk Import' trigger button (used when the wizard is opened externally). */
  hideTriggerButton?: boolean;
  /** Open the wizard immediately when the component mounts. */
  autoOpen?: boolean;
  /** Called when the wizard modal closes (either via finish or cancel). */
  onClose?: () => void;
  /**
   * Seeds the local "Skip cache" checkbox on mount. INITIAL VALUE ONLY — once
   * mounted the user's checkbox is authoritative, so unticking it here beats a
   * parent that still has its own "Skip cache" on. The wizard is remounted per
   * run (keyed on the source id), so each new run re-reads the parent's value.
   */
  skipCache?: boolean;
  /**
   * Which configured import source launched this run. Drives which actions the
   * launch panel offers (Shopify fetch vs URL fetch / paste HTML). Taken from
   * the source config rather than sniffed from the URL — the config already
   * knows, and a brand's own domain is not reliably identifiable as Shopify.
   */
  sourceKind?: 'shopify' | 'generic' | 'json-feed' | 'manual' | 'square-online' | 'woocommerce';
  /**
   * Products already extracted on the Import Sources "Test" preview, keyed by
   * URL. Reused verbatim instead of re-fetching — this is what carries a
   * manually-pasted page (Omega, Rolex, …) through from the test into the
   * import, without depending on the server-side extraction cache or the
   * skip-cache toggle.
   */
  prefetchedProducts?: Array<{ url: string; extracted: ExtractedProduct }>;
  /**
   * An extractor result envelope to load INSTEAD of an uploaded file, used with
   * `autoOpen` by the auto-import handoff from a finished scraper job.
   *
   * Deliberately `unknown`: it comes back through a callable as whatever the
   * extractor wrote, and `isExtractionResult` below is the thing that decides
   * whether it is an envelope at all. Everything after that point is the same
   * pipeline an uploaded file goes through — including the brand-mismatch stop.
   */
  prefetchedExtraction?: unknown;
  /**
   * WooCommerce / Square Online SHOP-URL runs only (a category URL has already
   * filtered): open on a category picker — the store counterpart of the Shopify
   * product-type step — before the rows in `prefetchedExtraction` go on.
   */
  storeCategoryPicker?: StoreCategoryPickerConfig;
  /**
   * The launching source's variant grouping rule. Groups rows the store leaves
   * standalone; proposed in the preview, written only for accepted groups. See
   * variantGroupingRule.ts.
   */
  variantGroupingRule?: VariantGroupingRule;
  /**
   * When provided and autoOpen is true, the wizard is armed with the
   * generic-URL pipeline: crawl each collection page for product URLs, merge
   * with directUrls, dedupe, then extract each URL. The run WAITS for the user
   * to press a launch-panel button — it no longer fires on mount, so the
   * skip-cache / manual-paste choices can be made before anything is fetched.
   */
  autoStartUrls?: {
    directUrls: string[];
    collectionPageUrls: string[];
    /**
     * `productLink` steers the listing crawl; `image` overrides which image the
     * product-page extraction picks.
     */
    cssSelectorOverrides?: { productLink?: string; image?: string };
    /**
     * URLs the admin ticked "Exclude" on in the Test preview. Filtered out of
     * the crawl, the extraction and the import — see handleImportFromSavedUrls,
     * which is the chokepoint every generic path funnels through.
     */
    excludedUrls?: string[];
    /** Extraction strategy chosen on the source; defaults to 'auto'. */
    extractionMethod?: GenericExtractionMethod;
    /** JSON listing endpoint; when set, overrides the HTML collection crawl. */
    jsonListingUrl?: string;
  };
}

type Step = 'upload' | 'shopify-types' | 'store-categories' | 'calibre-check' | 'module-check' | 'field-rules' | 'preview' | 'importing' | 'done';

/**
 * Where a routed row is written, and what to record about how that was chosen.
 * Only ever built when brand routing is active — see importBrandRouting.ts.
 */
interface ImportRoute {
  brand: Pick<WatchBrand, 'id' | 'name' | 'displayName'>;
  /** Value for `brandAssignedBy`, or null to leave the reference's stamp alone. */
  stamp: BrandAssignedBy | null;
}

/** The two assignment fields as a write payload — the timestamp moves only with the stamp. */
function brandStampFields(
  stamp: BrandAssignedBy | null | undefined,
): { brandAssignedBy: BrandAssignedBy; brandAssignedAt: Timestamp } | null {
  return stamp ? { brandAssignedBy: stamp, brandAssignedAt: Timestamp.now() } : null;
}

export function BulkImport({ brand, onComplete, onBrandUpdated, hideTriggerButton = false, autoOpen = false, onClose, autoStartUrls, skipCache = false, sourceKind, prefetchedProducts, prefetchedExtraction, storeCategoryPicker, variantGroupingRule }: BulkImportProps) {
  // Calibre write-path migration: both default ON, so an import writes exactly
  // what it always did, plus the `calibreId` link. Same pair the References bulk
  // action bar and the calibre enricher read.
  // The brand the wizard was opened from. Named apart from `brand` because the
  // write functions shadow that with the row's routed brand.
  const importingBrand = brand;
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const extractorJobsEnabled = useFeatureFlag('tools.extractorJobs');
  const dualWriteLegacyCalibreString = useFeatureFlag(
    'calibre.dualWriteLegacyCalibreString',
  );
  const calibreDualWriteFlags: CalibreDualWriteFlags = useMemo(
    () => ({
      legacyMovement: dualWriteLegacyMovement,
      legacyCalibreString: dualWriteLegacyCalibreString,
    }),
    [dualWriteLegacyMovement, dualWriteLegacyCalibreString],
  );

  const [isOpen, setIsOpen] = useState(autoOpen);
  const [step, setStep] = useState<Step>('upload');
  const [items, setItems] = useState<ImportItem[]>([]);
  const [importing, setImporting] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  // Phase label + parent-resolution counter for the "importing" screen. The
  // composite-id work added a pre-pass (load existing refs + upsert one parent
  // doc per multi-variant product) that runs BEFORE the item counter starts
  // moving, so without these the UI sat at "0 / N" for ~45–60s on a big brand.
  const [importPhase, setImportPhase] = useState('');
  const [parentProgress, setParentProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  // Brief feedback for the synchronous "Extract & Preview" parse step.
  const [proceedLoading, setProceedLoading] = useState(false);

  // Parent/variant breakdown for the post-import summary. Synthetic parent refs
  // (created/updated by upsertParentReference) aren't in `items`, so their
  // create/update counts are tracked here; `variantItemIndices` marks which
  // `items` are variants (the rest are parents/standalones).
  const [syntheticParentStats, setSyntheticParentStats] = useState<{ added: number; updated: number; diverged: number }>({ added: 0, updated: 0, diverged: 0 });
  const [variantItemIndices, setVariantItemIndices] = useState<Set<number>>(new Set());
  // Rows written as the parent of an accepted rule group this run — a parent,
  // not a single, in the summary.
  const [ruleParentItemIndices, setRuleParentItemIndices] = useState<Set<number>>(new Set());
  // Gallery-image totals for the run summary. Tag backfill happens on refs that
  // are otherwise unchanged, so without its own line the work is invisible —
  // "Unchanged 500" would be the only thing on screen after a tag backfill of
  // the whole brand. Accumulated in a ref (importSingle runs in parallel
  // batches, so a state setter would drop increments) and snapshotted at the end.
  const galleryStatsRef = useRef({ refs: 0, added: 0, tagged: 0, replaced: 0 });
  const [galleryStats, setGalleryStats] = useState({ refs: 0, added: 0, tagged: 0, replaced: 0 });
  // Every image the run could not get, per reference — hero and gallery, items
  // and synthetic parents alike. Collected in a ref for the same reason as the
  // gallery stats (parallel batches), snapshotted into state at the end of the
  // run so the done step can list them and retry them. Before this existed a
  // failed fetch was a console.warn, and 40 Panerai refs committed imageless
  // under a summary that read "40 added".
  const imageFailuresRef = useRef(new ImportImageFailureCollector<ScrapedWatchEntry>());
  const [imageFailureRows, setImageFailureRows] = useState<ImportImageFailureRow<ScrapedWatchEntry>[]>([]);
  const [retryingImages, setRetryingImages] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  // Re-download main + gallery for every reference, replacing what's stored,
  // rather than the default fill-empty/add-only image handling. Deliberately
  // INDEPENDENT of overwriteExisting: that toggle governs spec fields, this one
  // governs bytes, and the case this exists for (an extractor that now picks a
  // proper packshot where it used to pick a wristshot) wants the images redone
  // while curated field values stay exactly as they are.
  const [replaceAllImages, setReplaceAllImages] = useState(false);
  // Preview-step AI enrichment, BEFORE anything is committed: the included
  // preview rows (plus each variant group's in-memory parent) open in the Bulk
  // AI Enrich review grid in its in-memory mode. Null = closed.
  const [previewEnrich, setPreviewEnrich] = useState<{
    refs: WatchReference[];
    parents: Map<string, WatchReference>;
    /** The brand's saved catalogue — the "Notable Variants" sibling pool beyond the batch. */
    siblingPool: WatchReference[];
    /** Each pseudo-ref's scraped images by URL (preview rows have no Storage paths yet). */
    imagesById: Map<string, TaggedEnrichImage[]>;
  } | null>(null);
  const [openingPreviewEnrich, setOpeningPreviewEnrich] = useState(false);
  const enrichExitGuardRef = useRef<((proceed: () => void) => void) | null>(null);
  /** Reviewed AI values for each variant group's parent (by parent doc id), applied when the parent is written. */
  const previewParentValuesRef = useRef<Map<string, Partial<ScrapedWatchEntry>>>(new Map());
  /** Each parent's ticked variant rows (indices into `items`) at the time the grid opened. */
  const previewGroupIndicesRef = useRef<Map<string, number[]>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);
  // Per-run token stamped on every variant write this import makes, so the
  // watchlock `propagateVariantAggregatesToParent` trigger can skip its (now
  // redundant) recompute on importer-driven writes — the importer computes and
  // writes each parent's aggregate arrays itself in the parent pre-pass. The
  // trigger skips only when the token CHANGED, so a later manual edit (which
  // never sets it) is still processed normally. Re-stamped per handleImport run.
  const importRunTokenRef = useRef('');

  // Resolved electronic-module links for this import run, keyed by the (trimmed)
  // extracted module string → its `electronicModules` doc id. Populated once in
  // handleImport's pre-pass (match-or-create) so importSingle can stamp `moduleId`
  // on each digital ref without a per-item query. The digital counterpart of the
  // calibre link. Re-populated per run.
  const moduleIdByStringRef = useRef<Map<string, string>>(new Map());

  // Module strings the module-check step already resolved to a concrete
  // `electronicModules` doc id (via user AI-link, manual link, or local-index
  // match), keyed by the FINAL `entry.module` value. Seeded in ImportModuleCheck's
  // onProceed and consumed by handleImport's module link pre-pass, which links
  // these directly and skips the findModulesByLegacyValues match-or-create — so an
  // existing module isn't duplicated when its extracted string ("5476") differs
  // from its canonical name ("Casio Module 5476"). Re-populated per proceed.
  const preResolvedModuleIdsRef = useRef<Map<string, string>>(new Map());

  // Skip extraction cache. The prop seeds the checkbox on mount and then stops
  // mattering: the USER'S checkbox is authoritative, so unticking it here wins
  // over a parent that still has its own "Skip cache" on. (It used to be OR'd
  // with the prop, which made the local checkbox impossible to turn off.) Read
  // through a ref at each call site because the extraction pipeline is kicked
  // off from callbacks that would otherwise capture a stale value.
  const [skipExtractionCache, setSkipExtractionCache] = useState(skipCache);
  const skipExtractionCacheRef = useRef(skipCache);
  skipExtractionCacheRef.current = skipExtractionCache;

  // Extraction strategy from the import source (auto/json-ld/html-specs/ai).
  // Read live at each call site to avoid stale closures in the auto-start path.
  const extractionMethodRef = useRef<GenericExtractionMethod>('auto');
  extractionMethodRef.current = autoStartUrls?.extractionMethod ?? 'auto';

  // Product-page image CSS override from the import source, read live at each
  // extraction call site for the same stale-closure reason as the method above.
  const imageSelectorRef = useRef<string | undefined>(undefined);
  imageSelectorRef.current = autoStartUrls?.cssSelectorOverrides?.image?.trim() || undefined;

  // URLs the admin excluded in the Test preview. Applied in
  // handleImportFromSavedUrls — the single funnel every generic URL reaches —
  // so an excluded page can't be fetched, enriched or written no matter which
  // entry point started the run.
  const excludedUrlsRef = useRef<string[] | undefined>(undefined);
  excludedUrlsRef.current = autoStartUrls?.excludedUrls;

  // Products carried in from the Test preview, keyed canonically so a URL that
  // differs only by trailing slash / tracking params still matches. Rebuilt on
  // render (cheap — one entry per previewed URL) and read through a ref at the
  // extraction call sites for the same stale-closure reason as above.
  const prefetchedByUrl = useMemo(() => {
    const map = new Map<string, ExtractedProduct>();
    for (const { url, extracted } of prefetchedProducts ?? []) {
      map.set(canonicalProductUrlKey(url), extracted);
    }
    return map;
  }, [prefetchedProducts]);
  const prefetchedByUrlRef = useRef(prefetchedByUrl);
  prefetchedByUrlRef.current = prefetchedByUrl;
  // Carried-over results are honoured only while "Skip cache" is off: ticking it
  // means "re-fetch everything fresh", and a test result is exactly the stale
  // data that asks to bypass. Render-only — the extraction loop reads
  // `skipExtractionCacheRef` directly to avoid a stale closure.
  const usePrefetched = !skipExtractionCache;

  // Generic URL import state
  const [genericUrlsLoading, setGenericUrlsLoading] = useState(false);
  const [genericUrlsProgress, setGenericUrlsProgress] = useState(0);
  const [genericUrlsTotal, setGenericUrlsTotal] = useState(0);
  // Live retry status for the URL currently being fetched (exponential backoff).
  const [genericUrlsNote, setGenericUrlsNote] = useState('');

  // Armed-but-not-started: the wizard was launched from Import Sources with a
  // configured source and is waiting for the user to pick an action on the
  // launch panel. Cleared the moment a run starts, so the panel disappears and
  // doesn't reappear if the run fails (the failure rows own that screen).
  const [awaitingLaunch, setAwaitingLaunch] = useState(false);

  // Stop. Set by the Stop button, polled by the extraction loop AND by the
  // backoff sleep inside extractProductDataWithBackoff — without the latter a
  // stop during a 256s wait wouldn't be noticed for minutes. A ref (not state)
  // so the in-flight loop sees it without a re-render.
  const cancelRef = useRef(false);
  // URLs that never ran because the user stopped the batch. Offered as a
  // "Paste HTML for remaining" action rather than force-opening the modal —
  // Stop is allowed to just mean stop.
  const [stoppedUrls, setStoppedUrls] = useState<string[]>([]);
  // Entries already extracted before a stop, carried into the resumed run so
  // resuming appends to them rather than starting the batch over.
  const carryEntriesRef = useRef<ScrapedWatchEntry[]>([]);

  // Auto-start crawl phase state (collection-page URLs → discovered product URLs)
  const [autoCrawlLoading, setAutoCrawlLoading] = useState(false);
  const [autoCrawlProgress, setAutoCrawlProgress] = useState(0);
  const [autoCrawlTotal, setAutoCrawlTotal] = useState(0);
  const [autoCrawlError, setAutoCrawlError] = useState<string | null>(null);

  // Collection-page URL extraction state
  const [collectionPageUrl, setCollectionPageUrl] = useState('');
  const [collectionPageLoading, setCollectionPageLoading] = useState(false);
  const [collectionPageError, setCollectionPageError] = useState('');
  const [collectionPageUrls, setCollectionPageUrls] = useState<string[]>([]);
  const [collectionPageMethod, setCollectionPageMethod] = useState<string>('');

  // Paste HTML fallback state for generic URL import. The paste-queue UI itself
  // lives in the shared <PasteHtmlModal>; here we hold the URLs that need manual
  // paste plus any entries already auto-extracted, so they merge on completion.
  const [pasteQueue, setPasteQueue] = useState<string[]>([]);
  const [pasteAutoEntries, setPasteAutoEntries] = useState<ScrapedWatchEntry[]>([]);
  // Saved-URL extraction failures, shown as per-URL rows with Retry / Enter HTML
  // buttons instead of silently dropping or auto-opening the paste modal. Entries
  // that DID extract are held so a later retry/paste appends to them before we
  // continue into the wizard.
  const [savedUrlEntries, setSavedUrlEntries] = useState<ScrapedWatchEntry[]>([]);
  const [savedUrlFailures, setSavedUrlFailures] = useState<SavedUrlFailure[]>([]);
  // URL currently being manually pasted from a failure row (single-URL queue).
  const [savedUrlPasteUrl, setSavedUrlPasteUrl] = useState<string | null>(null);
  // When on, skip auto-extract entirely and send every URL straight to the paste queue.
  const [forceManualPaste, setForceManualPaste] = useState(false);
  const forceManualPasteRef = useRef(false);
  forceManualPasteRef.current = forceManualPaste;

  // Shopify URL import state
  const [shopifyUrl, setShopifyUrl] = useState('');
  const [shopifyProducts, setShopifyProducts] = useState<ShopifyProduct[]>([]);
  const [shopifyTypes, setShopifyTypes] = useState<{ type: string; count: number }[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  // Shopify product ids the curator has ticked back in after the automatic
  // title exclusion dropped them. Cleared whenever a new product list loads.
  const [titleIncludeOverrides, setTitleIncludeOverrides] = useState<Set<number>>(new Set());
  // Store (WooCommerce / Square Online) shop-URL category step — see storeCategoryFilter.ts.
  const [storeCatEntries, setStoreCatEntries] = useState<ScrapedWatchEntry[]>([]);
  const [selectedStoreCats, setSelectedStoreCats] = useState<Set<string>>(new Set());
  const [storeTitleOverrides, setStoreTitleOverrides] = useState<Set<string>>(new Set());
  const [viewingStoreCat, setViewingStoreCat] = useState<string | null>(null);
  /** Excluded category ids as chosen at the picker, saved back to the source after the import. */
  const storeCatExcludedRef = useRef<string[] | null>(null);
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyError, setShopifyError] = useState('');
  // Read, not discarded: the reference-list step's per-row tier selector needs
  // the tiers of whichever calibre each row linked, and this map is the only
  // place they survive (the entries themselves only carry `_calibreData` when
  // the legacy-movement dual-write flag is on). See importTierOptions.
  const [calibreMap, setCalibreMap] = useState<Map<string, CalibreMapEntry>>(new Map());
  const [prePreviewItems, setPrePreviewItems] = useState<ImportItem[]>([]);
  // Calibre pages that came with an extractor upload. Only the calibre-check
  // step reads these; every other import source leaves the array empty, which
  // switches its "From Extractor" action off.
  const [extractorCalibres, setExtractorCalibres] = useState<ExtractedCalibre[]>([]);
  const [extractionRules, setExtractionRules] = useState<ExtractionRule[]>([]);
  const [savedFieldRules, setSavedFieldRules] = useState<FieldRule[]>([]);
  const [savedExclusionRules, setSavedExclusionRules] = useState<ExclusionRule[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  // Track excluded product types locally so re-imports within the same session use the latest saved values.
  // Kept as `string[] | undefined` so we can distinguish "no saved config" (undefined → isLikelyWatch
  // heuristic) from "saved config that excludes nothing" (empty array → everything selected).
  const savedExcludedTypesRef = useRef<string[] | undefined>(brand.excludedProductTypes);
  // When the brand's Shopify import source opts out of parent/child grouping,
  // every variant imports as its own top-level ref (see handleShopifyProceed).
  // Loaded from brand_import_sources when the wizard opens.
  const ignoreShopifyParentChildRef = useRef(false);

  // ── Reference stopwords (Shopify path only) ──────────────────────────────────
  // Merchandising junk baked into SKUs / product handles ("PREORDER",
  // "coming soon") that must never become part of a ref's identity. Live list is
  // admin-editable at admin_config/import_stopwords (Configuration → Catalog
  // Indexes & Recompute); the seed defaults apply until it's first saved, and on
  // any read failure — an import is never blocked by this config.
  //
  // Held in a ref as well as state: the strip happens inside async handlers that
  // capture their scope, and a snapshot landing mid-run must not change what
  // this run is doing.
  const [importStopwords, setImportStopwords] = useState<string[]>(DEFAULT_IMPORT_STOPWORDS);
  const importStopwordsRef = useRef<string[]>(DEFAULT_IMPORT_STOPWORDS);
  importStopwordsRef.current = importStopwords;
  useEffect(() => subscribeToImportStopwords(setImportStopwords), []);

  // ── Subsequent-import ref diff (Shopify path only) ───────────────────────────
  // Shopify docs are keyed `shopify__{productId}__{variantId}`, so identity is
  // decoupled from the ref string and a ref CAN safely be rewritten in place on
  // a re-import. When the freshly-derived (stopword-cleaned) ref differs from
  // what's stored, the preview surfaces `REF CHANGED: stored → cleaned` with a
  // per-row accept checkbox.
  //
  // Stored `reference` per existing Shopify doc id, fetched ONCE when the
  // preview opens (targeted `documentId() in` reads — N docs, not the whole
  // brand). Kept raw so the diff below recomputes for free as the admin edits a
  // ref inline, without another round-trip.
  const [storedRefByDocId, setStoredRefByDocId] = useState<Map<string, string>>(new Map());
  // Item indices whose rewrite the admin UNCHECKED. Opt-OUT rather than opt-in so
  // the default is "accept the cleaned ref" without needing to seed state.
  const [refRewriteOptOut, setRefRewriteOptOut] = useState<Set<number>>(new Set());
  // Brand routing as the preview sees it — null unless the importing brand has
  // acquired siblings (importBrandRouting.ts). Resolved again, fresh, at import.
  const [routingPreview, setRoutingPreview] = useState<Awaited<
    ReturnType<typeof resolveImportRouting>
  > | null>(null);
  // The curator's brand pick per Shopify product id. A product's rows share it.
  const [brandChoiceByProduct, setBrandChoiceByProduct] = useState<Map<string, string>>(new Map());
  const [refDiffLoading, setRefDiffLoading] = useState(false);
  // Doc ids (rows + synthetic store parents) that already exist, for the
  // preview's would-create / would-update split. null = not known (no store
  // ids to look up, or the read failed).
  const [previewExistingDocIds, setPreviewExistingDocIds] = useState<Set<string> | null>(null);

  // Composite store ids (Shopify or Square Online) come from storeDocIdForEntry,
  // the same helper handleImport uses — that's what makes the preview's "this
  // row will land on that doc" claim true.

  // Load the stored refs for the previewed Shopify items. Keyed on the doc-id
  // SET (not on `items`), so inline edits and include-toggles during preview
  // don't re-trigger the read.
  const previewShopifyDocIds = useMemo(
    () =>
      step === 'preview'
        ? items
            // The row's own doc, plus its store product's synthetic parent so
            // the preview can say whether that parent is new.
            .flatMap((it) => [storeDocIdForEntry(it.entry), variantGroupOf(it.entry)?.parentDocId])
            .filter((id): id is string => !!id)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step, items.length, items.map((it) => storeDocIdForEntry(it.entry) ?? '').join('|')],
  );

  useEffect(() => {
    if (previewShopifyDocIds.length === 0) {
      setStoredRefByDocId(new Map());
      setPreviewExistingDocIds(null);
      setRoutingPreview(null);
      return;
    }
    let cancelled = false;
    setRefDiffLoading(true);
    // With brand routing active the stored refs are looked for under the
    // importing brand AND its acquired siblings — the same widened check the
    // import itself makes — so a reference living under Smiths shows its REF
    // CHANGED diff here rather than being rewritten unseen. Without routing
    // this is the single read it always was.
    resolveImportRouting(null, new Map(), false)
      .then(async (routed) => {
        const existing = routed
          ? [...routed.existingByBrand.values()].flatMap((m) => [...m])
          : [...(await getReferencesByDocIds(brand.id, previewShopifyDocIds))];
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const [docId, ref] of existing) {
          if (ref.reference) map.set(docId, ref.reference);
        }
        setStoredRefByDocId(map);
        setPreviewExistingDocIds(new Set(existing.map(([docId]) => docId)));
        setRoutingPreview(routed);
      })
      .catch((err) => {
        // Non-fatal: no diff rows and no brand dropdown. The import resolves
        // routing again itself, and stops if it cannot.
        console.warn('Failed to load existing refs for change detection:', err);
        if (!cancelled) {
          setStoredRefByDocId(new Map());
          setPreviewExistingDocIds(null);
          setRoutingPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setRefDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Keyed on the doc-id SET on purpose (see previewShopifyDocIds): the routing
    // resolver is a new function every render, and listing it would re-read
    // Firestore on every inline edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id, previewShopifyDocIds]);

  /**
   * Item index → the stored ref string it disagrees with. Three states, exactly
   * as designed: a NEW product has no doc id in `storedRefByDocId` (absent → no
   * row); an existing product whose ref matches is filtered out (no row); only a
   * genuine disagreement lands here.
   *
   * Compared on the raw strings, not `normalizeReference`d ones — a purely
   * cosmetic difference is still a change worth showing, and the whole point of
   * the strip is that it changes the normalized value too.
   */
  /**
   * `calibreId` → the tiers this brand may assign, for the reference list's
   * per-row tier selector. Rebuilt only when the calibre-check step hands over a
   * new map, so scrolling a 600-row preview costs nothing.
   */
  const importTierIndex = useMemo(
    () => buildImportTierIndex(calibreMap, brand.id),
    [calibreMap, brand.id],
  );

  /**
   * Rows whose tier this admin has set by hand in the reference list, by index.
   *
   * Kept beside the items rather than as a fourth underscore field on the entry
   * because it is a UI provenance note, not import data: it decides whether the
   * row reads "auto" or "set" and nothing else. `_calibreTierId` remains the one
   * value `mapEntryToReference` reads.
   */
  const [manualTierRows, setManualTierRows] = useState<Set<number>>(new Set());

  /**
   * Rows whose calibre matched a REVISION LINE, by index: the line and the
   * revision the resolver (or the curator) picked, with the method and
   * confidence. Kept beside the items like `manualTierRows`; the entry itself
   * carries only the `_calibreId` the pick resolved to.
   */
  const [revisionPicks, setRevisionPicks] = useState<Map<number, RevisionPickState>>(new Map());
  /** The row whose ✨ release-year lookup is running; one at a time. */
  const [revisionAiRow, setRevisionAiRow] = useState<number | null>(null);
  /** ✨ results this session, by brand + reference, so a repeat click costs nothing. */
  const releaseYearCache = useRef(new Map<string, { year?: number; confidence: 'high' | 'medium' | 'low' }>());
  /** Row index → the reference doc id the import wrote, for the resolver analytics. */
  const writtenRefIds = useRef(new Map<number, string>());

  // ── Variant grouping rule (source config) ───────────────────────────────────
  // Groups the rule proposes over rows the store leaves standalone, and the
  // curator's call on each (keyed by group key). Only accepted groups change
  // what is written — see the rule pre-pass in handleImport.
  const [ruleDecisions, setRuleDecisions] = useState<Map<string, RuleGroupDecision>>(new Map());
  const ruleGroups = useMemo(
    () =>
      ruleIsActive(variantGroupingRule)
        ? proposeRuleGroups(
            items.map((it) => ({
              reference: it.entry.reference ?? '',
              included: it.included,
              storeGrouped: !!variantGroupOf(it.entry),
            })),
            variantGroupingRule,
          )
        : [],
    [items, variantGroupingRule],
  );
  const decideRuleGroup = useCallback((key: string, decision: RuleGroupDecision) => {
    setRuleDecisions((prev) => new Map(prev).set(key, decision));
  }, []);
  const decideAllRuleGroups = useCallback(
    (status: RuleGroupDecision['status']) => {
      setRuleDecisions((prev) => {
        const next = new Map(prev);
        for (const g of ruleGroups) next.set(g.key, { ...(prev.get(g.key) ?? {}), status });
        return next;
      });
    },
    [ruleGroups],
  );

  // What the import would write, as the curator has it set up right now: store
  // groups, ACCEPTED rule groups, and singles (importStructure.ts). Drives the
  // preview's grouped layout and its structure summary.
  const previewStructure = useMemo(
    () =>
      planImportStructure(
        items,
        resolveAcceptedGroups(ruleGroups, ruleDecisions, (i) => items[i]?.entry.reference ?? ''),
      ),
    [items, ruleGroups, ruleDecisions],
  );

  /** Re-point one row to another revision of its line, and record why. */
  const setRowRevision = useCallback(
    (rowIndex: number, next: RevisionResolution<RevisionLineMember>) => {
      const cur = revisionPicks.get(rowIndex);
      if (!cur) return;
      const from = cur.resolution.member;
      if (from.id !== next.member.id) {
        setItems((prev) =>
          prev.map((it, idx) =>
            idx === rowIndex
              ? {
                  ...it,
                  entry: applyRevisionPick({
                    entry: it.entry,
                    from,
                    to: next.member,
                    brandId: brand.id,
                    flags: calibreDualWriteFlags,
                  }),
                }
              : it,
          ),
        );
      }
      setRevisionPicks((prev) => new Map(prev).set(rowIndex, { ...cur, resolution: next }));
    },
    [revisionPicks, brand.id, calibreDualWriteFlags],
  );

  const pickRowRevision = useCallback(
    (rowIndex: number, memberId: string) => {
      const cur = revisionPicks.get(rowIndex);
      const member = cur?.line.find((m) => m.id === memberId);
      if (!cur || !member) return;
      const year = refYearOf(items[rowIndex]?.entry as unknown as Record<string, unknown>) ?? undefined;
      setRowRevision(rowIndex, {
        member,
        method: 'manual_override',
        confidence: 'high',
        inRange: yearInRevision(member, year),
      });
    },
    [revisionPicks, items, setRowRevision],
  );

  const setRowRevisionYear = useCallback(
    (rowIndex: number, year: number, method: 'manual_override' | 'ai_retry' = 'manual_override', confidence?: 'high' | 'medium' | 'low') => {
      const cur = revisionPicks.get(rowIndex);
      if (!cur) return;
      const next = resolveRevisionInLine({ members: cur.line, override: { year, method, confidence } });
      if (next) setRowRevision(rowIndex, next);
    },
    [revisionPicks, setRowRevision],
  );

  const lookupRowReleaseYear = useCallback(
    async (rowIndex: number) => {
      if (revisionAiRow !== null) return;
      const entry = items[rowIndex]?.entry;
      if (!entry) return;
      const refNumber = String(entry.reference ?? '').trim();
      const refName = String(entry.model || entry.productName || '').trim();
      const key = `${brand.id}|${refNumber || refName}`.toLowerCase();
      let found = releaseYearCache.current.get(key);
      if (!found) {
        setRevisionAiRow(rowIndex);
        try {
          found = await lookupRefReleaseYearWithAI({ brandId: brand.id, refName: refName || undefined, refNumber: refNumber || undefined });
          releaseYearCache.current.set(key, found);
        } catch (err) {
          toast.error(`Release-year lookup failed: ${err instanceof Error ? err.message : String(err)}`);
          return;
        } finally {
          setRevisionAiRow(null);
        }
      }
      if (typeof found.year !== 'number') {
        toast('No release year found for this reference.');
        return;
      }
      setRowRevisionYear(rowIndex, found.year, 'ai_retry', found.confidence);
    },
    [revisionAiRow, items, brand.id, setRowRevisionYear],
  );

  /**
   * Point one preview row at a tier, or clear it.
   *
   * Per REFERENCE — the exception lever. The bulk lever is the calibre-check
   * step's selector, which sets the tier for every row sharing a calibre string;
   * this overrides one of them without disturbing the rest.
   *
   * Both `_calibreTierId` and `_calibreTierName` are written together, and both
   * are deleted together: the name is what the row renders and the id is what
   * `mapEntryToReference` writes, so a row carrying one without the other is
   * either an invisible tier or a phantom one.
   */
  const setRowTier = useCallback((rowIndex: number, tierId: string) => {
    setItems((prev) =>
      prev.map((it, idx) => {
        if (idx !== rowIndex) return it;
        const entry = { ...it.entry } as Record<string, unknown>;
        const tier = tierOptionsFor(importTierIndex, entry._calibreId).find(
          (t) => t.id === tierId,
        );
        if (tier) {
          entry._calibreTierId = tier.id;
          entry._calibreTierName = tier.name;
        } else {
          delete entry._calibreTierId;
          delete entry._calibreTierName;
        }
        return { ...it, entry: entry as unknown as typeof it.entry };
      }),
    );
    setManualTierRows((prev) => new Set(prev).add(rowIndex));
  }, [importTierIndex]);

  const refDiffs = useMemo(() => {
    const diffs = new Map<number, string>();
    if (storedRefByDocId.size === 0) return diffs;
    items.forEach((item, i) => {
      const docId = storeDocIdForEntry(item.entry);
      if (!docId) return;
      const stored = storedRefByDocId.get(docId);
      if (!stored) return;
      const derived = (item.entry.reference ?? '').trim();
      if (!derived || derived === stored) return;
      diffs.set(i, stored);
    });
    return diffs;
  }, [items, storedRefByDocId]);

  // How many of the changed rows are currently set to take the new ref. Drives
  // the "N of M accepted" counter in the banner — without it, Accept all /
  // Keep all existing look like no-ops when the affected rows are scrolled out
  // of view. Intersected with refDiffs so stale opt-out indices can't skew it.
  const refRewriteAcceptedCount = useMemo(() => {
    let accepted = 0;
    refDiffs.forEach((_stored, idx) => {
      if (!refRewriteOptOut.has(idx)) accepted += 1;
    });
    return accepted;
  }, [refDiffs, refRewriteOptOut]);

  /**
   * How many image requests a "Replace all images" run would actually send —
   * counted off the parsed entries rather than guessed at, so the warning banner
   * quotes this brand's real numbers instead of a rule of thumb.
   *
   * Counts what the importer would fetch: the hero shot, plus the gallery
   * candidates that survive selection, capped at the gallery's own limit. Held
   * images the scrape no longer emits cost nothing (they're kept, not fetched),
   * so this is an upper bound and can only over-warn.
   */
  const imageFetchEstimate = useMemo(() => {
    const included = items.filter((i) => i.included);
    let total = 0;
    for (const item of included) {
      const primary = item.entry.imgSrc ?? '';
      if (primary) total += 1;
      total += Math.min(
        selectGalleryImageCandidates(item.entry.taggedImages, primary).length,
        MAX_GALLERY_IMAGES,
      );
    }
    const refs = included.length;
    return { refs, total, perRef: refs > 0 ? Math.round((total / refs) * 10) / 10 : 0 };
  }, [items]);

  // Per-product-type JSON viewer modal (Enhancement 1)
  const [viewingType, setViewingType] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Image URLs that failed to load, keyed by URL rather than by row so that a
  // re-parse of a different file simply stops matching instead of needing a
  // reset. Only failures are reported back: successes are the common case and
  // would cost a state update per row.
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set());
  const markImageFailed = (url: string) =>
    setFailedImageUrls((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));

  // Whether the IMPORT will get each row's hero, asked of the server before
  // anything is committed. Distinct from `failedImageUrls` above, which is only
  // whether this browser could DISPLAY it: Akamai hosts (Panerai, VCA) render
  // fine as an <img> and still refuse every fetch the import makes from the
  // cloud, which is how 40 imageless Panerai refs got through a clean-looking
  // preview. Keyed by URL like the display map, so a re-parse never needs a
  // reset and a URL is probed once per session.
  const [imageProbe, setImageProbe] = useState<Map<string, ImageProbeResult>>(new Map());
  const [probingImages, setProbingImages] = useState(false);

  const runImageProbe = async (urls: string[]) => {
    if (urls.length === 0) return;
    setProbingImages(true);
    try {
      const results = await probeReferenceImages(importingBrand.id, urls);
      setImageProbe((prev) => {
        const next = new Map(prev);
        results.forEach((result, url) => next.set(url, result));
        return next;
      });
      if (results.size < new Set(urls).size) {
        toast.error('Some images could not be checked — use "Check image fetches" to try again');
      }
    } finally {
      setProbingImages(false);
    }
  };

  /** Included rows' hero URLs the probe has no verdict on yet. */
  const unprobedImageUrls = () =>
    [
      ...new Set(
        items
          .filter((i) => i.included && i.entry.imgSrc?.trim())
          .map((i) => i.entry.imgSrc!.trim())
          .filter((url) => !imageProbe.has(url)),
      ),
    ];

  // Check automatically on arrival at the preview, so the warning is in front
  // of the curator without a click. Capped: each URL is a real fetch from the
  // brand's host, and a whole-catalogue run (Rolex: 1,400+ rows) should not
  // fire that on page entry — above the cap the button does it on request.
  useEffect(() => {
    if (step !== 'preview' || probingImages) return;
    const urls = unprobedImageUrls();
    if (urls.length > 0 && urls.length <= AUTO_IMAGE_PROBE_MAX) void runImageProbe(urls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Preview paging. `previewPage` is the REQUESTED page; the rendered one is
  // clamped below, so a shorter list (a re-parse, or an exclusion rule applied
  // from the table) can't strand the admin on an empty page.
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(PREVIEW_PAGE_SIZE_OPTIONS[0]);

  // Start each visit to the preview at page 1. Keyed on the STEP, not on
  // `items`: items changes on every include-toggle and inline edit, so resetting
  // on that would throw the admin back to page 1 mid-review.
  useEffect(() => {
    if (step === 'preview') setPreviewPage(1);
  }, [step]);

  // Inline exclusion form on the preview step (Enhancement 3)
  const [excludeFormRow, setExcludeFormRow] = useState<number | null>(null);
  const [excludeFormField, setExcludeFormField] = useState<string>('title');
  const [excludeFormCondition, setExcludeFormCondition] = useState<ExclusionRule['condition']>('contains');
  const [excludeFormValue, setExcludeFormValue] = useState('');

  // Per-item detail expansion on the preview step — view all extracted fields,
  // source URL, and warnings, and edit values inline before importing.
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const updateEntryField = (index: number, field: keyof ScrapedWatchEntry, value: string) => {
    // A typed calibre that drops the link leaves no revision pick to show.
    if (field === 'movement') {
      const linked = (items[index]?.entry as unknown as Record<string, unknown> | undefined)?._calibreId;
      const after = applyManualCalibreEdit(items[index]?.entry ?? ({} as ScrapedWatchEntry), value) as unknown as Record<string, unknown>;
      if (linked && after._calibreId !== linked) {
        setRevisionPicks((prev) => {
          if (!prev.has(index)) return prev;
          const next = new Map(prev);
          next.delete(index);
          return next;
        });
      }
    }
    setItems((prev) =>
      prev.map((it, idx) => {
        if (idx !== index) return it;
        // The Calibre input: kept whole as the explicit calibre name, and a
        // changed name drops the calibre-check link (see applyManualCalibreEdit).
        if (field === 'movement') return { ...it, entry: applyManualCalibreEdit(it.entry, value) };
        return { ...it, entry: { ...it.entry, [field]: value } };
      }),
    );
  };

  // Surface extraction gaps so the user can spot bad results at a glance.
  const entryWarnings = (entry: ScrapedWatchEntry): string[] => {
    const warnings: string[] = [];
    if (!entry.imgSrc) warnings.push(NO_IMAGE_REASON);
    // A ref needs EITHER a calibre (analog) OR a module (digital). Only warn when
    // both are missing; a digital watch with a module — or an analog one with a
    // calibre — is complete. The message reflects which one this watch wants.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasCalibre = !!((entry as any).calibre || entry.movement);
    const hasModule = !!(entry.module && entry.module.trim());
    const isDigital = (entry.movementType || '').trim().toLowerCase() === 'digital';
    if (!hasCalibre && !hasModule) {
      warnings.push(isDigital ? 'No module' : 'No calibre');
    }
    if (!entry.model || entry.model === 'Unknown') warnings.push('No model');
    return warnings;
  };

  const reset = () => {
    setStep('upload');
    setItems([]);
    setImporting(false);
    setProcessedCount(0);
    setImportPhase('');
    setParentProgress({ done: 0, total: 0 });
    setProceedLoading(false);
    setOverwriteExisting(false);
    setReplaceAllImages(false);
    abortRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShopifyUrl('');
    setShopifyProducts([]);
    setTitleIncludeOverrides(new Set());
    setShopifyTypes([]);
    setSelectedTypes(new Set());
    setShopifyLoading(false);
    setShopifyError('');
    setCalibreMap(new Map());
    setManualTierRows(new Set());
    setRevisionPicks(new Map());
    setRuleDecisions(new Map());
    writtenRefIds.current = new Map();
    setStoredRefByDocId(new Map());
    setRefRewriteOptOut(new Set());
    setRoutingPreview(null);
    setBrandChoiceByProduct(new Map());
    setRefDiffLoading(false);
    setPrePreviewItems([]);
    setExtractorCalibres([]);
    setExtractionRules([]);
    setSavedFieldRules([]);
    setConfigLoaded(false);
    setViewingType(null);
    setExcludeFormRow(null);
    setAutoCrawlLoading(false);
    setAutoCrawlProgress(0);
    setAutoCrawlTotal(0);
    setAutoCrawlError(null);
    setAwaitingLaunch(false);
    setStoppedUrls([]);
    // NB: cancelRef is deliberately NOT cleared here. reset() runs from
    // handleClose *after* it raises the cancel flag, so clearing it would let a
    // closed modal's extraction loop carry on. Every path that starts a run
    // clears the flag itself.
  };

  const handleClose = () => {
    if (importing) return; // prevent closing mid-import
    // Closing mid-extraction must also stop it, or the loop keeps fetching
    // (and toasting) against an unmounted modal.
    cancelRef.current = true;
    setIsOpen(false);
    reset();
    onClose?.();
  };

  const handleOpen = () => {
    reset();
    cancelRef.current = false;
    // Pre-fill Shopify URL from saved brand data
    if (brand.shopifyUrl) setShopifyUrl(brand.shopifyUrl);
    setIsOpen(true);
  };

  // Pre-fill Shopify URL when the modal is auto-opened on mount.
  useEffect(() => {
    if (autoOpen && brand.shopifyUrl) {
      setShopifyUrl(brand.shopifyUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arm — but do NOT start — the configured pipeline when launched from Import
  // Sources. This used to fire runAutoStart straight from mount, which meant a
  // site needing a manual paste burned the full ~8.5-minute backoff before the
  // user could intervene, and the skip-cache / force-paste toggles were set too
  // late to matter. The launch panel now owns the decision to start.
  useEffect(() => {
    if (!autoOpen) return;
    if (sourceKind === 'shopify' && brand.shopifyUrl) {
      setAwaitingLaunch(true);
      return;
    }
    if (!autoStartUrls) return;
    const direct = autoStartUrls.directUrls ?? [];
    const pages = autoStartUrls.collectionPageUrls ?? [];
    const jsonListingUrl = autoStartUrls.jsonListingUrl?.trim();
    if (direct.length === 0 && pages.length === 0 && !jsonListingUrl) return;
    setAwaitingLaunch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Launch-panel action — run the configured generic pipeline (crawl → extract).
  const handleLaunchFetch = () => {
    if (!autoStartUrls) return;
    setAwaitingLaunch(false);
    cancelRef.current = false;
    setStoppedUrls([]);
    runAutoStart(
      autoStartUrls.directUrls ?? [],
      autoStartUrls.collectionPageUrls ?? [],
      autoStartUrls.cssSelectorOverrides,
      autoStartUrls.jsonListingUrl?.trim(),
    );
  };

  // Launch-panel action — skip automated fetching entirely and paste each page
  // by hand. Only the configured DIRECT product URLs can be pasted: a
  // collection page has no paste flow (there'd be nothing to extract a product
  // from), so those still need the crawl first.
  const handleLaunchPaste = () => {
    // Filtered here as well as in handleImportFromSavedUrls: this path opens the
    // paste queue directly and never passes through that funnel, so without it
    // an excluded page would still be handed to the user to paste by hand.
    const direct = withoutExcludedUrls(
      dedupeProductUrls(autoStartUrls?.directUrls ?? []),
      autoStartUrls?.excludedUrls,
    );
    if (direct.length === 0) {
      toast.error('No direct product URLs on this source to paste — run the crawl first.');
      return;
    }
    setAwaitingLaunch(false);
    cancelRef.current = false;
    setStoppedUrls([]);
    toast(`Manual HTML paste for ${direct.length} URL${direct.length === 1 ? '' : 's'}`, { icon: '📋' });
    openBulkPasteQueue(direct, []);
  };

  // Launch-panel action (Shopify sources) — fetch the store's product list.
  const handleLaunchShopify = () => {
    setAwaitingLaunch(false);
    handleShopifyFetch();
  };

  // Stop the in-flight extraction batch. The loop checks this between URLs and
  // extractProductDataWithBackoff polls it during the backoff wait, so a stop
  // lands within ~250ms instead of after the remaining retries.
  const handleStopExtraction = () => {
    cancelRef.current = true;
    toast('Stopping…', { icon: '🛑' });
  };

  // Load saved config (field rules + extraction rules) when dialog opens
  useEffect(() => {
    if (!isOpen || configLoaded) return;
    (async () => {
      try {
        const config = await getImportConfigForBrand(brand.id);
        if (config.extractionRules.length > 0) {
          let id = 0;
          setExtractionRules(config.extractionRules.map((r) => ({ ...r, id: `er-${++id}` })));
        }
        if (config.rules.length > 0) {
          let id = 0;
          setSavedFieldRules(config.rules.map((r) => ({ ...r, id: `rule-${++id}` } as FieldRule)));
        }
        if (config.exclusionRules.length > 0) {
          let id = 0;
          setSavedExclusionRules(config.exclusionRules.map((r) => ({ ...r, id: `excl-${++id}` } as ExclusionRule)));
        }
        // Read the per-brand Shopify parent/child opt-out from the import-sources
        // doc so the flag applies regardless of where the wizard was launched.
        try {
          const importSources = await getBrandImportSources(brand.id);
          const shopifySource = importSources?.sources.find((s) => s.kind === 'shopify');
          ignoreShopifyParentChildRef.current =
            shopifySource?.kind === 'shopify'
              ? shopifySource.ignoreParentChildRelationships ?? false
              : false;
        } catch {
          ignoreShopifyParentChildRef.current = false;
        }
        setConfigLoaded(true);
      } catch {
        setConfigLoaded(true);
      }
    })();
  }, [isOpen, configLoaded, brand.id]);

  /**
   * The upload path, from the point the JSON exists.
   *
   * Split out of `processFile` so a result that never was a file can use it:
   * the extractor's own output arrives through a callable as an object, and
   * round-tripping it through a download and a re-upload just to reach this
   * code would be the manual path the auto-import exists to remove.
   */
  const loadParsedJson = (parsed: unknown) => {
      try {
        // An extractor result is an ENVELOPE ({ extractorId, brandId, watches,
        // stats, … }), not the bare ScrapedWatchEntry array this upload has
        // always taken. Detect it and unwrap; everything downstream of here is
        // the unchanged shared pipeline (calibre check → rules → preview →
        // import), including the single `overwriteExisting` gate.
        let entries: ScrapedWatchEntry[];
        // Scraped calibre pages ride along on the envelope. Reset on every
        // upload so a second, non-extractor file can't inherit the first's.
        let calibres: ExtractedCalibre[] = [];
        if (isExtractionResult(parsed)) {
          // Hard reject on a brand mismatch. Importing an Omega crawl into the
          // Tudor catalogue writes hundreds of mis-branded refs, and the merge
          // is not reversible per-doc — so this is a stop, not a warning.
          // TODO: offer an opt-in "import anyway, re-brand to the selected
          // brand" path once there's a real use case for it.
          if (parsed.brandId !== brand.id) {
            toast.error(
              `This file was extracted for brand “${parsed.brandId}”, but you're importing into “${brand.id}”. Open the import from the ${parsed.brandId} brand instead.`,
              { duration: 8000 },
            );
            return;
          }
          entries = extractionResultToEntries(parsed);
          calibres = extractionResultCalibres(parsed);
          const dropped = parsed.watches.length - entries.length;
          if (dropped > 0) {
            toast(`${dropped} extracted watch${dropped === 1 ? '' : 'es'} skipped — no reference number.`);
          }
          if (entries.length > 0) {
            toast.success(
              `Loaded ${entries.length} watch${entries.length === 1 ? '' : 'es'} from the ${parsed.extractorId} extractor.`,
            );
          }
        } else {
          entries = Array.isArray(parsed) ? parsed : [parsed];
        }

        if (entries.length === 0) {
          toast.error('JSON file contains no entries');
          return;
        }

        // Validate first entry has required fields
        const first = entries[0];
        if (!first.reference || !first.model) {
          toast.error('JSON entries must have "reference" and "model" fields');
          return;
        }

        const importItems = entries
          .map(routeAnalogModuleToMovement)
          .map((entry) => ({ entry, status: 'pending' as const, included: !!entry.imgSrc }));
        setPrePreviewItems(importItems);
        setExtractorCalibres(calibres);
        setStep('calibre-check');
      } catch {
        toast.error('Invalid JSON file');
      }
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.target?.result as string);
      } catch {
        toast.error('Invalid JSON file');
        return;
      }
      loadParsedJson(parsed);
    };
    reader.readAsText(file);
  };

  /*
   * An extractor result handed straight to the wizard, in place of an upload.
   *
   * Consumed once: `loadParsedJson` moves the wizard off the upload step, and
   * re-running it would throw away whatever the operator has done since. A
   * different job means a different mounted wizard (the launcher keys on the
   * payload), so the ref does not need resetting.
   */
  const consumedExtractionRef = useRef(false);
  useEffect(() => {
    if (!isOpen || !prefetchedExtraction || consumedExtractionRef.current) return;
    consumedExtractionRef.current = true;
    // A store shop-URL run opens on the category picker — unless every product
    // sits in one category (or none), where there is nothing to choose.
    if (storeCategoryPicker && Array.isArray(prefetchedExtraction)) {
      const entries = prefetchedExtraction as ScrapedWatchEntry[];
      const options = storeCategoryOptions(entries, storeCategoryPicker.hierarchy);
      if (options.length > 1) {
        setStoreCatEntries(entries);
        setStoreTitleOverrides(new Set());
        setSelectedStoreCats(defaultStoreCategorySelection(options, storeCategoryPicker.savedExcluded));
        setStep('store-categories');
        return;
      }
    }
    loadParsedJson(prefetchedExtraction);
  }, [isOpen, prefetchedExtraction]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const updateItemStatus = (index: number, update: Partial<ImportItem>) => {
    if (update.referenceId) writtenRefIds.current.set(index, update.referenceId);
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...update } : item)));
  };

  // ── Generic URL import ────────────────────────────────────────────────────────
  const mapExtractedToScrapedEntry = (extracted: ExtractedProduct): ScrapedWatchEntry => {
    // Derive reference: explicit, else last URL path segment
    let reference = extracted.reference?.trim();
    if (!reference) {
      try {
        const u = new URL(extracted.url);
        const segments = u.pathname.split('/').filter(Boolean);
        reference = segments[segments.length - 1] || u.hostname;
      } catch {
        reference = extracted.url;
      }
    }
    const model = (extracted.model || extracted.name || 'Unknown').trim();
    return {
      reference,
      model,
      url: extracted.url,
      title: extracted.name,
      productName: extracted.name,
      imgSrc: extracted.imgSrc,
      collection: extracted.collection,
      description: extracted.description,
      price: extracted.price,
      movement: extracted.movement,
      movementType: extracted.movementType,
      watchType: extracted.watchType,
      module: extracted.module,
      jewels: extracted.jewels,
      caseSize: extracted.caseSize,
      caseHeight: extracted.caseHeight,
      caseMaterial: extracted.caseMaterial,
      lugWidth: extracted.lugWidth,
      lugToLug: extracted.lugToLug,
      waterRes: extracted.waterRes,
      dialColor: extracted.dialColor,
      dialFinish: extracted.dialFinish,
      dialIndexes: extracted.dialIndexes,
      dialNumerals: extracted.dialNumerals,
      dateDisplay: extracted.dateDisplay,
      dateWindowPosition: extracted.dateWindowPosition,
      dateWindowFrame: extracted.dateWindowFrame,
      dateWheelColor: extracted.dateWheelColor,
      dateWheelTextColor: extracted.dateWheelTextColor,
      lumePresence: extracted.lumePresence,
      lumeMaterial: extracted.lumeMaterial,
      lumeDayColor: extracted.lumeDayColor,
      lumeGlowColor: extracted.lumeGlowColor,
      subdialTypes: extracted.subdialTypes,
      chapterRing: extracted.chapterRing,
      minuteTrack: extracted.minuteTrack,
      dialScales: extracted.dialScales,
      dialFeatures: extracted.dialFeatures,
      logoType: extracted.logoType,
      hands: extracted.hands,
      handsColor: extracted.handsColor,
      crystal: extracted.crystal,
      crownType: extracted.crownType,
      caseShape: extracted.caseShape,
      bezelType: extracted.bezelType,
      bezelMaterial: extracted.bezelMaterial,
      bezelColors: extracted.bezelColors,
      strapMaterial: extracted.strapMaterial,
      strapColor: extracted.strapColor,
      powerReserve: extracted.powerReserve,
      functions: extracted.functions,
    };
  };

  // Open the manual-paste queue for a set of URLs, carrying through any entries
  // already auto-extracted. Shared by the NeedsManualHtmlError fallback and the
  // explicit "force manual paste" path. The queue UI + parse loop live in the
  // shared <PasteHtmlModal>; completion is handled by its onDone below.
  const openBulkPasteQueue = (queueUrls: string[], autoEntries: ScrapedWatchEntry[]) => {
    setPasteAutoEntries([...autoEntries]);
    setPasteQueue(queueUrls);
  };

  // Merge auto-extracted entries with the manually-pasted results once the paste
  // queue is drained, then continue into the wizard.
  const handlePasteQueueDone = (results: PasteHtmlResult[]) => {
    const manualEntries = results.map((r) => mapExtractedToScrapedEntry(r.extracted));
    const allEntries = [...pasteAutoEntries, ...manualEntries];
    setPasteQueue([]);
    setPasteAutoEntries([]);
    if (allEntries.length === 0) {
      toast.error('No products extracted');
      return;
    }
    toast.success(`Extracted ${allEntries.length} products total`);
    continueWithEntries(allEntries);
  };

  const handleImportFromSavedUrls = async (overrideUrls?: string[]) => {
    const requested = overrideUrls ?? brand.productUrls ?? [];
    // Last line of defence for the exclusion list: every generic path — launch
    // panel, auto-start crawl, saved URLs, "import these extracted URLs" —
    // arrives here, so filtering once at this point means an excluded URL can
    // never be fetched, AI-enriched or written.
    const urls = withoutExcludedUrls(requested, excludedUrlsRef.current);
    const droppedCount = requested.length - urls.length;
    if (droppedCount > 0) {
      toast(`Skipping ${droppedCount} excluded URL${droppedCount === 1 ? '' : 's'}`);
    }
    if (urls.length === 0) {
      if (droppedCount > 0) toast.error('Every URL in this run is excluded — nothing to import.');
      return;
    }

    // Force-manual: skip auto-extract and queue every URL for manual HTML paste.
    // Anything carried in from a stopped run rides along so resuming this way
    // still appends rather than starting over.
    if (forceManualPasteRef.current) {
      const carried = carryEntriesRef.current;
      carryEntriesRef.current = [];
      toast(`Manual HTML paste for ${urls.length} URL${urls.length === 1 ? '' : 's'}`, { icon: '📋' });
      openBulkPasteQueue(urls, carried);
      return;
    }

    setGenericUrlsLoading(true);
    setGenericUrlsProgress(0);
    setGenericUrlsTotal(urls.length);
    setGenericUrlsNote('');
    setSavedUrlEntries([]);
    setSavedUrlFailures([]);
    setStoppedUrls([]);
    cancelRef.current = false;

    // Seeded from a prior stopped run when resuming, so those entries aren't lost.
    const entries: ScrapedWatchEntry[] = carryEntriesRef.current;
    carryEntriesRef.current = [];
    const failedRows: SavedUrlFailure[] = [];
    // URLs never attempted because the user stopped the batch part-way.
    const notAttempted: string[] = [];

    // Sequential (never parallel) so the exponential backoff below throttles a
    // rate-limited site instead of hammering it. Each URL starts fresh — a
    // success carries no backoff state into the next URL.
    for (const url of urls) {
      if (cancelRef.current) {
        notAttempted.push(url);
        continue;
      }

      // Reuse a result already extracted on the Test preview (including a
      // manually-pasted page) instead of re-fetching. Skipped when the user
      // asked to skip the cache — that means "get it fresh".
      const carried = skipExtractionCacheRef.current
        ? undefined
        : prefetchedByUrlRef.current.get(canonicalProductUrlKey(url));
      if (carried) {
        entries.push(mapExtractedToScrapedEntry(carried));
        setGenericUrlsProgress((p) => p + 1);
        continue;
      }

      try {
        const extracted = await extractProductDataWithBackoff(
          url,
          skipExtractionCacheRef.current,
          extractionMethodRef.current,
          ({ attempt, waitMs, phase }) =>
            setGenericUrlsNote(
              attempt === 1
                ? ''
                : phase === 'waiting'
                  ? `Retrying (attempt ${attempt}, ${waitMs / 1000}s wait)…`
                  : `Retry attempt ${attempt}, fetching…`,
            ),
          () => cancelRef.current,
          imageSelectorRef.current,
        );
        entries.push(mapExtractedToScrapedEntry(extracted));
      } catch (err) {
        // A stopped URL isn't a failure — park it with the not-yet-attempted
        // ones so the whole remainder is offered as a single paste/retry batch.
        if (err instanceof ExtractionCancelledError) {
          notAttempted.push(url);
        } else {
          failedRows.push({
            url,
            retrying: false,
            note:
              err instanceof NeedsManualHtmlError
                ? 'Automated fetch failed — enter HTML manually'
                : err instanceof Error
                  ? err.message
                  : String(err),
          });
        }
      }
      setGenericUrlsNote('');
      setGenericUrlsProgress((p) => p + 1);
    }

    setGenericUrlsLoading(false);
    setGenericUrlsNote('');

    if (notAttempted.length > 0) {
      setStoppedUrls(notAttempted);
      setSavedUrlEntries(entries);
      setSavedUrlFailures(failedRows);
      toast(
        `Stopped — ${notAttempted.length} URL${notAttempted.length === 1 ? '' : 's'} not attempted`,
        { icon: '🛑' },
      );
      return;
    }

    // Any failures stay on this step as per-URL rows (Retry / Enter HTML) rather
    // than auto-advancing or silently dropping. Successful entries are parked so
    // a later retry/paste appends to them before continuing into the wizard.
    if (failedRows.length > 0) {
      setSavedUrlEntries(entries);
      setSavedUrlFailures(failedRows);
      toast(
        `${failedRows.length} URL${failedRows.length === 1 ? '' : 's'} failed — retry or enter HTML`,
        { icon: '⚠️' },
      );
      return;
    }

    if (entries.length === 0) {
      toast.error('No products extracted');
      return;
    }
    toast.success(`Extracted ${entries.length} products`);
    continueWithEntries(entries);
  };

  // Re-run the full backoff sequence (2/4/…/256s, up to 8 retries) for one failed
  // saved URL. On success the row is removed and its entry parked; on exhaustion
  // the row stays so the user can retry again or enter HTML.
  const retrySavedUrl = async (url: string) => {
    cancelRef.current = false;
    setSavedUrlFailures((prev) =>
      prev.map((f) => (f.url === url ? { ...f, retrying: true, note: 'Trying…' } : f)),
    );
    const setNote = (note: string) =>
      setSavedUrlFailures((prev) => prev.map((f) => (f.url === url ? { ...f, note } : f)));
    try {
      const extracted = await extractProductDataWithBackoff(
        url,
        skipExtractionCacheRef.current,
        extractionMethodRef.current,
        ({ attempt, waitMs, phase }) =>
          setNote(
            attempt === 1
              ? 'Trying…'
              : phase === 'waiting'
                ? `Retrying (attempt ${attempt}, ${waitMs / 1000}s wait)…`
                : `Retry attempt ${attempt}, fetching…`,
          ),
        () => cancelRef.current,
        imageSelectorRef.current,
      );
      setSavedUrlEntries((prev) => [...prev, mapExtractedToScrapedEntry(extracted)]);
      setSavedUrlFailures((prev) => prev.filter((f) => f.url !== url));
      toast.success(`Extracted ${extracted.fieldsFound} fields`);
    } catch (err) {
      setSavedUrlFailures((prev) =>
        prev.map((f) =>
          f.url === url
            ? {
                ...f,
                retrying: false,
                note:
                  err instanceof ExtractionCancelledError
                    ? 'Stopped — retry or enter HTML'
                    : err instanceof NeedsManualHtmlError
                      ? 'Automated fetch failed — enter HTML manually'
                      : err instanceof Error
                        ? err.message
                        : String(err),
              }
            : f,
        ),
      );
    }
  };

  // Stopped-batch actions. "Paste" walks the remaining URLs through the manual
  // paste queue; "Resume" re-runs the normal extraction over just those URLs
  // (already-extracted entries are preserved in both cases).
  const pasteStoppedUrls = () => {
    const remaining = stoppedUrls;
    if (remaining.length === 0) return;
    setStoppedUrls([]);
    openBulkPasteQueue(remaining, [...savedUrlEntries]);
    setSavedUrlEntries([]);
    setSavedUrlFailures([]);
  };

  const resumeStoppedUrls = () => {
    const remaining = stoppedUrls;
    if (remaining.length === 0) return;
    carryEntriesRef.current = [...savedUrlEntries];
    setStoppedUrls([]);
    handleImportFromSavedUrls(remaining);
  };

  // Continue into the wizard with what extracted before the stop, abandoning
  // the URLs that were never attempted.
  const continueFromStop = () => {
    const entries = savedUrlEntries;
    setStoppedUrls([]);
    setSavedUrlFailures([]);
    setSavedUrlEntries([]);
    if (entries.length === 0) {
      toast.error('No products extracted');
      return;
    }
    continueWithEntries(entries);
  };

  // Continue into the wizard with whatever extracted so far, abandoning any
  // still-failed rows.
  const continueFromSavedUrlFailures = () => {
    const entries = savedUrlEntries;
    setSavedUrlFailures([]);
    setSavedUrlEntries([]);
    setStoppedUrls([]);
    if (entries.length === 0) {
      toast.error('No products extracted');
      return;
    }
    continueWithEntries(entries);
  };

  const continueWithEntries = (entries: ScrapedWatchEntry[]) => {
    const importItems = entries.map(routeAnalogModuleToMovement).map((entry) => ({
      entry,
      status: 'pending' as const,
      included: !!entry.imgSrc,
    }));
    setPrePreviewItems(importItems);
    setExtractorCalibres([]);
    setStep('calibre-check');
  };

  // ── Collection-page URL handler ──────────────────────────────────────────────
  // Given a listing/collection page URL, extract individual product page URLs
  // (DOM links → JSON-LD images → meta tags) and then run the existing per-URL
  // extractor on the result.
  const handleExtractFromCollectionPage = async () => {
    const url = collectionPageUrl.trim();
    if (!url) return;
    setCollectionPageError('');
    setCollectionPageUrls([]);
    setCollectionPageMethod('');
    setCollectionPageLoading(true);
    try {
      const result = await extractProductUrls(url);
      setCollectionPageUrls(result.urls);
      setCollectionPageMethod(result.method);
      if (result.urls.length === 0) {
        setCollectionPageError('No product URLs found on that page.');
        return;
      }
      toast.success(`Found ${result.urls.length} URL${result.urls.length === 1 ? '' : 's'} (${result.method})`);
    } catch (err) {
      setCollectionPageError(
        err instanceof NeedsManualHtmlError
          ? UNREACHABLE_LISTING_MESSAGE
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setCollectionPageLoading(false);
    }
  };

  const handleImportExtractedCollectionUrls = () => {
    if (collectionPageUrls.length === 0) return;
    handleImportFromSavedUrls(collectionPageUrls);
  };

  // ── Auto-start: crawl collection pages, then extract merged URL set ──────────
  // Triggered when the wizard is mounted with autoStartUrls. Crawls each
  // collection page sequentially via extractProductUrls, merges with the
  // configured direct product URLs, dedupes, and hands off to the existing
  // per-URL extractor.
  const runAutoStart = async (
    directUrls: string[],
    collectionPagesToCrawl: string[],
    cssSelectorOverrides?: { productLink?: string; image?: string },
    jsonListingUrl?: string,
  ) => {
    const crawled: string[] = [];
    const pageErrors: string[] = [];

    // Only productLink is meaningful to the listing crawl; the image selector
    // rides along on the same object and is applied per-product downstream.
    const listingOverrides = cssSelectorOverrides?.productLink
      ? { productLink: cssSelectorOverrides.productLink }
      : undefined;

    // A JSON listing URL overrides the HTML collection crawl: discover product
    // URLs from the JSON API (paginated) instead of scraping the pages.
    // An excluded listing page is never crawled — cheapest possible skip.
    const crawlTargets = withoutExcludedUrls(
      jsonListingUrl ? [jsonListingUrl] : collectionPagesToCrawl,
      excludedUrlsRef.current,
    );

    if (crawlTargets.length > 0) {
      setAutoCrawlLoading(true);
      setAutoCrawlError(null);
      setAutoCrawlProgress(0);
      setAutoCrawlTotal(crawlTargets.length);
      for (const target of crawlTargets) {
        // Stop applies to the crawl phase too — otherwise a stop during a slow
        // multi-page crawl still runs every remaining listing page first.
        if (cancelRef.current) break;
        try {
          const result = jsonListingUrl
            ? await extractProductUrls(target, listingOverrides, target)
            : await extractProductUrls(target, listingOverrides);
          if (result.urls.length === 0) {
            pageErrors.push(`${target}: no URLs found`);
          }
          crawled.push(...result.urls);
        } catch (err) {
          pageErrors.push(
            `${target}: ${
              err instanceof NeedsManualHtmlError
                ? UNREACHABLE_LISTING_MESSAGE
                : err instanceof Error
                  ? err.message
                  : String(err)
            }`,
          );
        }
        setAutoCrawlProgress((p) => p + 1);
      }
      setAutoCrawlLoading(false);
      if (cancelRef.current) {
        setAutoCrawlError('Stopped before the crawl finished — nothing was imported.');
        toast('Stopped', { icon: '🛑' });
        return;
      }
    }

    // Merge direct URLs with crawled URLs, preserving order, deduped. Canonical
    // dedup so a product listed on two overlapping pagination pages — or linked
    // with different tracking params — is only imported once.
    const merged = dedupeProductUrls([...directUrls, ...crawled]);

    if (merged.length === 0) {
      const msg =
        pageErrors.length > 0
          ? `No product URLs discovered. ${pageErrors.join('; ')}`
          : 'No product URLs to import.';
      setAutoCrawlError(msg);
      toast.error(msg);
      return;
    }

    if (pageErrors.length > 0) {
      toast(`Crawl finished with ${pageErrors.length} page error${pageErrors.length === 1 ? '' : 's'} — continuing with ${merged.length} URL${merged.length === 1 ? '' : 's'}`);
    }

    await handleImportFromSavedUrls(merged);
  };

  // ── Shopify URL import handlers ──────────────────────────────────────────────
  const handleShopifyFetch = async () => {
    if (!shopifyUrl.trim()) return;
    setShopifyLoading(true);
    setShopifyError('');
    try {
      const url = shopifyUrl.trim().replace(/\/+$/, '');
      const products = await fetchShopifyProducts(url);
      setShopifyProducts(products);
      setTitleIncludeOverrides(new Set());
      const types = getProductTypes(products);
      setShopifyTypes(types);
      // Saved config is the source of truth when it exists (even an empty array means
      // "user reviewed types and excluded nothing"). Only fall back to the isLikelyWatch
      // heuristic for brands that have never had a config saved. Mirrors ProbeShopify.
      const savedExclusions = savedExcludedTypesRef.current;
      if (savedExclusions !== undefined) {
        const excludeSet = new Set(savedExclusions);
        const autoSelected = new Set(types.filter((t) => !excludeSet.has(t.type)).map((t) => t.type));
        setSelectedTypes(autoSelected);
      } else {
        const autoSelected = new Set(types.filter((t) => isLikelyWatch(t.type)).map((t) => t.type));
        setSelectedTypes(autoSelected);
      }
      setStep('shopify-types');
    } catch (err) {
      setShopifyError(err instanceof Error ? err.message : 'Failed to fetch products');
    } finally {
      setShopifyLoading(false);
    }
  };

  const handleShopifyProceed = async () => {
    // parseProducts is synchronous CPU work; yield once so the button's loading
    // state paints before it runs (otherwise the click feels dead until the
    // calibre-check screen appears).
    setShopifyError('');
    setProceedLoading(true);
    await new Promise((r) => setTimeout(r, 0));
    try {
      const filtered = filterProducts(shopifyProducts, selectedTypes, titleIncludeOverrides);
      const url = shopifyUrl.trim().replace(/\/+$/, '');
      const entries = parseProducts(filtered, url);

      if (entries.length === 0) {
        setShopifyError('No watch entries found with selected types');
        return;
      }

      // Convert ScrapedEntry → ScrapedWatchEntry (tags string → array).
      // When the source opts out of parent/child grouping, force each variant's
      // sibling count to 1 so the grouping pass in handleImport (which triggers
      // only on shopifyVariantCount >= 2) treats every variant as a standalone
      // top-level ref. Identity stays unique via the composite product+variant
      // doc id, so same-SKU variants still land in distinct docs.
      //
      // This is also where the ref stopword strip lands. It is the single funnel
      // every Shopify entry passes through on its way into the wizard, and it
      // sits BEFORE the preview — so the admin reviews (and the diff detection
      // compares) the CLEANED ref, and `normalizeReference` downstream derives
      // `referenceNormalized` from the cleaned string too. Shopify-only by
      // design: file/URL/extractor imports derive their refs differently and are
      // deliberately left alone.
      const flattenParentChild = ignoreShopifyParentChildRef.current;
      const stopwords = importStopwordsRef.current;
      // The same merchandising words pollute the readable strings Shopify hands
      // us, not just the SKU: a product titled "X - Summer Series - Purple Haze
      // - Preorder" splits into model "X" and variant (→ nickname) "Summer
      // Series - Purple Haze - Preorder". Same list, same matcher — only the
      // tidy-up differs, and an all-stopword value is dropped rather than kept
      // (see stripImportStopwordsFromText). Cleaning `model` also cleans the
      // series, which mapEntryToReference derives from it.
      const cleanText = (v: string | undefined) =>
        v ? stripImportStopwordsFromText(v, stopwords) : v;
      const converted = entries.map((e) => ({
        ...e,
        reference: stripImportStopwords(e.reference, stopwords),
        model: cleanText(e.model) ?? '',
        productName: cleanText(e.productName) || undefined,
        collection: cleanText(e.collection) || undefined,
        variant: cleanText(e.variant) || undefined,
        tags: e.tags ? e.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : undefined,
        ...(flattenParentChild ? { shopifyVariantCount: 1 } : {}),
      }));
      const cleanedCount = entries.filter(
        (e, i) => e.reference !== converted[i].reference,
      ).length;
      const cleanedTextCount = entries.reduce(
        (n, e, i) =>
          n +
          (['model', 'productName', 'collection', 'variant'] as const).filter(
            (k) => (e[k] || '') !== (converted[i][k] || ''),
          ).length,
        0,
      );
      const cleanedParts: string[] = [];
      if (cleanedCount > 0) {
        cleanedParts.push(`${cleanedCount} reference${cleanedCount === 1 ? '' : 's'}`);
      }
      if (cleanedTextCount > 0) {
        cleanedParts.push(
          `${cleanedTextCount} name field${cleanedTextCount === 1 ? '' : 's'}`,
        );
      }
      if (cleanedParts.length > 0) {
        toast.success(`Stripped stopwords from ${cleanedParts.join(' and ')}`);
      }
      const importItems = converted
        .map(routeAnalogModuleToMovement)
        .map((entry) => ({ entry, status: 'pending' as const, included: !!entry.imgSrc }));
      setPrePreviewItems(importItems);
      setExtractorCalibres([]);
      setStep('calibre-check');
    } finally {
      setProceedLoading(false);
    }
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  /**
   * importGalleryImages for this brand, with the run totals kept. Returns
   * whether anything was written, so the caller can count the reference as
   * updated rather than unchanged.
   *
   * `replaceAllImages` is read from state here rather than threaded through the
   * callers, which is what makes this the ONE place the toggle has to reach the
   * gallery: all four import paths (overwrite, merge, create, parent pre-pass)
   * already funnel through this helper. Safe against a stale closure because the
   * checkbox lives on the wizard's final step, which is unmounted for the whole
   * of the run this reads during.
   */
  const recordGalleryImport = async (
    referenceId: string,
    entry: ScrapedWatchEntry,
    primaryImageUrl: string,
    existingGallery: ImageInfo[] | undefined,
    // The brand the reference lives under — a routed row's is not the importing brand's.
    targetBrandId: string = brand.id,
    // How the run summary names this reference; a synthetic parent's own ref,
    // not the template variant's it borrows the entry from.
    label: string = entry.reference,
  ): Promise<boolean> => {
    const { added, tagged, replaced, failures } = await importGalleryImages(
      targetBrandId,
      referenceId,
      entry,
      primaryImageUrl,
      existingGallery,
      replaceAllImages,
    );
    for (const f of failures) {
      imageFailuresRef.current.note(
        { brandId: targetBrandId, referenceId, reference: label, entry },
        { kind: 'gallery', url: f.url, error: f.error },
      );
    }
    if (added === 0 && tagged === 0 && replaced === 0) return false;
    const stats = galleryStatsRef.current;
    stats.refs += 1;
    stats.added += added;
    stats.tagged += tagged;
    stats.replaced += replaced;
    return true;
  };

  const importSingle = async (
    item: ImportItem,
    index: number,
    existingRef: WatchReference | undefined,
    overwrite: boolean,
    parentReferenceId?: string,
    sourceType?: ReferenceSource,
    // Precomputed composite doc id for Shopify-sourced items
    // (`shopify__{productId}__{variantId}`). When set, a new doc is created at
    // this exact id — bypassing the SKU-derived id + ref-string dedup in
    // createWatchReference — so identity is decoupled from the (non-unique) SKU.
    shopifyDocId?: string,
    // Set only for Shopify rows the preview flagged as REF CHANGED — the freshly
    // derived (stopword-cleaned) ref disagrees with what's stored on the doc.
    // `accept` carries the admin's per-row checkbox: true → rewrite the stored
    // ref, false → keep it. Absent means "no disagreement", the normal case.
    refRewrite?: { stored: string; accept: boolean },
    // Set only when brand routing is active for this run: the brand this row's
    // document lives under (which may be an acquired sibling, not the importing
    // brand) and the assignment stamp to record, if any.
    route?: ImportRoute,
  ): Promise<void> => {
    // Every write below is addressed to `brand`. Shadowing the importing brand
    // here is what keeps a routed row from touching it by accident.
    const brand = route?.brand ?? importingBrand;
    const stampFields = brandStampFields(route?.stamp);
    // Apply saved field rules fresh to this entry at import time
    // This ensures rules always apply regardless of whether the user clicked Apply or Skip
    let entry = { ...item.entry };
    const enabledRules = savedFieldRules.filter(r => r.enabled && r.targetValue?.trim());
    if (enabledRules.length > 0) {
      const { entries: [ruleApplied] } = applyRules([entry], enabledRules, overwrite);
      entry = ruleApplied;
    }
    // Admin UNCHECKED the rewrite: pin the entry back to the stored ref BEFORE
    // mapEntryToReference runs, so `reference`, `referenceNormalized` and
    // `searchText` all stay mutually consistent (and the overwrite path, which
    // set-merges the whole payload, can't clobber the ref either).
    if (refRewrite && !refRewrite.accept) {
      entry = { ...entry, reference: refRewrite.stored };
    }
    updateItemStatus(index, { status: 'processing' });

    try {
      // Build reference data and strip all undefined/null/empty values before any write
      const referenceData = deepStripEmpty(
        mapEntryToReference(
          entry,
          brand.id,
          brand.displayName || brand.name,
          calibreDualWriteFlags,
        ) as Record<string, unknown>,
      ) as Record<string, unknown>;

      // Always carry `referenceNormalized` on every write path (overwrite-set,
      // merge-update, and create). Computed from the actual `reference` value
      // being written and set after deepStripEmpty so it survives even when the
      // normalized form is empty — this makes routine re-imports self-heal
      // legacy docs that predate the field. Reuses the firebase normalizer.
      referenceData.referenceNormalized = normalizeReference(
        String(referenceData.reference ?? ''),
      );

      // Link this variant to its parent ref (Shopify variant products only).
      // Set after deepStripEmpty so it survives on every write path — create,
      // overwrite-set, and merge-update (the merge diff fills it because the
      // existing variant doc has no parentReferenceId yet, so re-imports
      // self-heal pre-existing variant docs in place, preserving their IDs).
      if (parentReferenceId) {
        referenceData.parentReferenceId = parentReferenceId;
      }

      // Provenance tag + Shopify composite-id source ids. Set after
      // deepStripEmpty (like parentReferenceId) so they survive create,
      // overwrite-set, and merge-update — letting routine re-imports backfill
      // these fields onto pre-existing docs in place, preserving their ids.
      if (sourceType) referenceData.sourceType = sourceType;
      if (entry.shopifyProductId) referenceData.shopifyProductId = entry.shopifyProductId;
      if (entry.shopifyVariantId) referenceData.shopifyVariantId = entry.shopifyVariantId;

      // Link the electronic module (digital watches) — the module counterpart of
      // the calibre link. moduleId is pre-resolved once per run (match-or-create
      // against electronicModules) in handleImport. Set on referenceData BEFORE
      // the write branches below so it flows through create, overwrite-set, AND
      // merge-update (buildMergeDiff fills it only when the existing ref has no
      // moduleId — so re-imports self-heal without clobbering a manual link).
      const moduleStr = (entry.module || '').trim();
      if (moduleStr) {
        const moduleId = moduleIdByStringRef.current.get(moduleStr);
        if (moduleId) referenceData.moduleId = moduleId;
      }

      // Per-run importer token, spread into the actual write payloads (NOT into
      // referenceData, so it never feeds hasActualChanges/buildMergeDiff and a
      // no-change re-import stays a no-op). Only stamped on VARIANT writes — those
      // are the writes that fire watchlock's propagateVariantAggregatesToParent;
      // the parent's aggregates are already computed in the parent pre-pass, so
      // the trigger's recompute is pure redundant fan-out. The trigger skips when
      // the token changed, so manual edits (which never set it) still propagate.
      const importerMark = parentReferenceId
        ? { __importWriteToken: importRunTokenRef.current }
        : {};

      // Denormalized parentage flag backing the parents-only listing query.
      // Spread into the write payloads below (like importerMark) rather than
      // set on `referenceData` ON PURPOSE: neither hasActualChanges nor
      // buildMergeDiff treats `false` as empty, so a boolean living in
      // referenceData would feed the created/updated/diverged/unchanged status
      // logic — and buildMergeDiff, being fill-empty-only, would then refuse to
      // correct a stored `false` to `true` when a standalone becomes a variant.
      // Kept out of the diff, it never influences change detection and is
      // written correctly on every write that does happen.
      //
      // Belt-and-braces only: the value is already right on every doc, and
      // watchlock's self-healing trigger catches drift. Setting it on first
      // write just means the trigger rarely has to fire.
      //
      // Derived from EFFECTIVE parentage, not just the incoming argument. A
      // non-Shopify run resolves `existingRef` through the SKU-keyed map, which
      // contains variant docs too — so an entry whose SKU collides with an
      // existing variant arrives here with no `parentReferenceId` even though
      // the doc it is about to write is a variant. No import path ever CLEARS
      // parentage (the write paths only ever add `parentReferenceId`), so the
      // stored value is authoritative whenever this run isn't supplying one.
      // Without this the write would flip a real variant to isVariant: false
      // while it still carried a parentReferenceId, listing it as a parent
      // until the self-healing trigger undid it.
      const effectiveParentId = deriveIsVariant(parentReferenceId)
        ? parentReferenceId
        : existingRef?.parentReferenceId;
      const variantMark = { isVariant: deriveIsVariant(effectiveParentId) };

      // Effective source image URL: prefer the freshly-extracted imgSrc, but
      // fall back to a previously-persisted pendingImageUrl so a re-import can
      // self-heal even when re-extraction no longer surfaces the source URL.
      const imageUrl = entry.imgSrc || existingRef?.pendingImageUrl || '';
      const hadPendingImage = !!existingRef?.pendingImageUrl;

      // Whatever the extractor inferred about the hero shot rides along on
      // images[0] — spread, so an untagged hero writes no `tags` key at all
      // rather than an undefined Firestore rejects. See selectHeroImageTags for
      // why the hero's tags are worth keeping even though only gallery images
      // show chips.
      const heroTags = selectHeroImageTags(entry.taggedImages, imageUrl);
      const heroImageTags = heroTags.length > 0 ? { tags: heroTags } : {};

      if (existingRef) {
        const referenceId = existingRef.id;

        if (overwrite) {
          // Overwrite: check if there are actual changes before writing
          const hasChanges = hasActualChanges(
            existingRef as unknown as Record<string, unknown>,
            referenceData,
          );

          let madeChanges = false;
          if (hasChanges) {
            const docRef = doc(db, 'watchBrands', brand.id, 'references', referenceId);
            await setDoc(docRef, { ...referenceData, ...importDescriptionTag(referenceData, entry, existingRef.originalDescription, Timestamp.now()), ...importerMark, ...variantMark, updatedAt: Timestamp.now() }, { merge: true });
            madeChanges = true;
          }
          // Its own write, outside the change check: the stamp is about the
          // reference, not about what this scrape happened to change.
          if (stampFields) {
            await updateDoc(doc(db, 'watchBrands', brand.id, 'references', referenceId), {
              ...stampFields,
              ...importerMark,
              updatedAt: Timestamp.now(),
            });
            madeChanges = true;
          }

          // Set when the main image failed but the reference carried on — only
          // reachable with "Replace all images" ticked. See the catch below.
          let overwriteMainImageError: string | null = null;

          // Re-download and re-upload image in overwrite mode if source has image
          if (imageUrl) {
            try {
              const { storagePath, contentType } = await ingestReferenceImage(brand.id, {
                referenceId: referenceId,
                imageUrl: imageUrl,
                kind: 'main',
                index: 0,
              });
              // Write images directly rather than via updateWatchReference: the
              // doc was just written above with `referenceNormalized`, so the
              // self-heal getDoc that helper does for an images-only patch is a
              // pure wasted read per ref — the dominant per-parent overhead in a
              // parents-only overwrite re-import (where the image branch runs for
              // every ref). Legacy normalized-field backfill is still covered by
              // the main write path + referenceNormalizedBackfill.
              await updateDoc(doc(db, 'watchBrands', brand.id, 'references', referenceId), {
                images: [{ storagePath, contentType, index: 0, ...heroImageTags }],
                updatedAt: Timestamp.now(),
              });
              if (hadPendingImage) await clearReferencePendingImageUrl(brand.id, referenceId);
              madeChanges = true;
            } catch (imgErr) {
              console.warn(`Image upload failed for ${entry.reference}:`, imgErr);
              // Persist the source URL so it can be recovered later.
              try { await setReferencePendingImageUrl(brand.id, referenceId, imageUrl); } catch { /* best effort */ }
              overwriteMainImageError = imgErr instanceof Error ? imgErr.message : 'unknown';
              imageFailuresRef.current.note(
                { brandId: brand.id, referenceId, reference: entry.reference, entry },
                { kind: 'main', url: imageUrl, error: overwriteMainImageError },
              );
              // Same reasoning as the merge path: with "Replace all images"
              // ticked the gallery rebuild is a deliverable in its own right and
              // doesn't fall with the main shot. Untouched otherwise.
              if (!replaceAllImages) {
                updateItemStatus(index, {
                  status: madeChanges ? 'updated' : 'duplicate',
                  referenceId,
                  error: madeChanges ? `Updated (image failed: ${overwriteMainImageError})` : `Skipped (image failed)`,
                });
                return;
              }
            }
          }

          if (await recordGalleryImport(referenceId, entry, imageUrl, existingRef.galleryImages, brand.id)) {
            madeChanges = true;
          }

          updateItemStatus(index, {
            status: madeChanges ? 'updated' : 'duplicate',
            referenceId,
            ...(overwriteMainImageError ? { error: `Main image failed: ${overwriteMainImageError}` } : {}),
          });
          return;
        }

        // Merge: only populate empty fields
        const updateData = buildMergeDiff(
          existingRef as unknown as Record<string, unknown>,
          referenceData,
        );

        // …with ONE deliberate exception: an accepted REF CHANGED row. The merge
        // diff is fill-empty-only, so a stored `reference` is never rewritten by
        // it — which is exactly the value this feature exists to correct. This
        // isn't a blanket force-overwrite: the row was surfaced in the preview
        // with its before → after, and the admin left its checkbox ticked. The
        // two derived fields ride along so they can't drift from the new ref.
        // Safe to rewrite in place because Shopify doc identity is the composite
        // product+variant id, not the ref string.
        if (refRewrite?.accept) {
          updateData.reference = referenceData.reference;
          updateData.referenceNormalized = referenceData.referenceNormalized;
          if (referenceData.searchText) updateData.searchText = referenceData.searchText;
        }

        let madeChanges = false;
        if (Object.keys(updateData).length > 0) {
          const cleanData = deepStripEmpty(updateData) as Record<string, unknown>;
          const flatData = flattenToDotNotation(cleanData);
          const docRef = doc(db, 'watchBrands', brand.id, 'references', referenceId);
          // Tagged only when the fill-empty diff itself writes the description.
          await updateDoc(docRef, { ...flatData, ...importDescriptionTag(cleanData, entry, existingRef.originalDescription, Timestamp.now()), ...importerMark, ...variantMark, updatedAt: Timestamp.now() });
          madeChanges = true;
        }
        // See the overwrite path: stamped whether or not the diff wrote anything.
        if (stampFields) {
          await updateDoc(doc(db, 'watchBrands', brand.id, 'references', referenceId), {
            ...stampFields,
            ...importerMark,
            updatedAt: Timestamp.now(),
          });
          madeChanges = true;
        }

        // Set when the main image failed to download but the reference was NOT
        // abandoned — see the catch below for why replace mode carries on.
        let mainImageError: string | null = null;

        // Upload the main image when the ref has none — or, with "Replace all
        // images" ticked, whatever it currently holds. The block below already
        // writes `images` as a whole array, so the gate is the only thing
        // standing between fill-empty and replace.
        if (
          imageUrl &&
          (replaceAllImages || !existingRef.images || existingRef.images.length === 0)
        ) {
          try {
            const { storagePath, contentType } = await ingestReferenceImage(brand.id, {
              referenceId: referenceId,
              imageUrl: imageUrl,
              kind: 'main',
              index: 0,
            });
            // Direct write — skip updateWatchReference's per-ref self-heal getDoc
            // (the doc already carries referenceNormalized). See the overwrite path.
            await updateDoc(doc(db, 'watchBrands', brand.id, 'references', referenceId), {
              images: [{ storagePath, contentType, index: 0, ...heroImageTags }],
              updatedAt: Timestamp.now(),
            });
            if (hadPendingImage) await clearReferencePendingImageUrl(brand.id, referenceId);
            madeChanges = true;
          } catch (imgErr) {
            console.warn(`Image upload failed for ${entry.reference}:`, imgErr);
            // Persist the source URL so it can be recovered later.
            try { await setReferencePendingImageUrl(brand.id, referenceId, imageUrl); } catch { /* best effort */ }
            mainImageError = imgErr instanceof Error ? imgErr.message : 'unknown';
            imageFailuresRef.current.note(
              { brandId: brand.id, referenceId, reference: entry.reference, entry },
              { kind: 'main', url: imageUrl, error: mainImageError },
            );
            // In replace mode the gallery is its own deliverable, so a main
            // image the host refused must not take it down with it — fall
            // through and rebuild the gallery anyway. Without the toggle this
            // branch only ever runs for a ref with no image at all, where there
            // is no such second deliverable, so the original early exit stands.
            if (!replaceAllImages) {
              updateItemStatus(index, {
                status: madeChanges ? 'updated' : 'duplicate',
                referenceId,
                error: madeChanges ? `Updated (image failed: ${mainImageError})` : `Skipped (image failed: ${mainImageError})`,
              });
              return;
            }
          }
        }

        // Unconditional — this is the branch a routine re-import of an existing
        // catalogue takes, and it is where the tag backfill has to land.
        if (await recordGalleryImport(referenceId, entry, imageUrl, existingRef.galleryImages, brand.id)) {
          madeChanges = true;
        }

        // Mark as updated only if we actually changed something. When nothing was
        // written, distinguish a true no-op ('duplicate' → "Unchanged") from a
        // divergence: the fill-empty merge diff was empty, yet a stored value
        // disagrees with the fresh extraction (curated field preserved). Same
        // signal the parent pre-pass surfaces, so re-imports flag which variants
        // have upstream data differing from the curated version.
        const divergedNoWrite =
          !madeChanges &&
          hasActualChanges(
            existingRef as unknown as Record<string, unknown>,
            referenceData,
          );
        updateItemStatus(index, {
          status: madeChanges ? 'updated' : divergedNoWrite ? 'diverged' : 'duplicate',
          referenceId,
          // Surfaced even when the gallery rebuild went on to succeed: the ref
          // still has a stale main image, which is the whole reason the admin
          // ticked the box.
          ...(mainImageError ? { error: `Main image failed: ${mainImageError}` } : {}),
        });
        return;
      }

      // Create new Firestore document
      let referenceId: string;
      if (shopifyDocId) {
        // Shopify: write directly to the composite doc id. Identity comes from
        // the product+variant ids, NOT the SKU, so we deliberately skip
        // createWatchReference's ref-string/normalized dedup — multiple Shopify
        // docs sharing one SKU (e.g. Alkin's AM1-B-1 across 3 products) is the
        // intended outcome, not a collision. (referenceData already carries
        // referenceNormalized for search.)
        const docRef = doc(db, 'watchBrands', brand.id, 'references', shopifyDocId);
        await setDoc(docRef, {
          ...referenceData,
          ...importDescriptionTag(referenceData, entry, undefined, Timestamp.now()),
          ...importerMark,
          ...variantMark,
          ...(stampFields ?? {}),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        referenceId = shopifyDocId;
      } else {
        try {
          referenceId = await createWatchReference(
            brand.id,
            { ...referenceData, ...importDescriptionTag(referenceData, entry, undefined, Timestamp.now()) } as Parameters<
              typeof createWatchReference
            >[1],
          );
        } catch (err) {
          if (err instanceof Error && err.message.includes('already exists')) {
            updateItemStatus(index, { status: 'duplicate', error: 'Already exists' });
            return;
          }
          throw err;
        }
      }

      // Download image and upload to Firebase Storage
      if (imageUrl) {
        try {
          const { storagePath, contentType } = await ingestReferenceImage(brand.id, {
            referenceId: referenceId,
            imageUrl: imageUrl,
            kind: 'main',
            index: 0,
          });
          // Direct write — skip updateWatchReference's per-ref self-heal getDoc
          // (the doc was just created with referenceNormalized). See overwrite path.
          await updateDoc(doc(db, 'watchBrands', brand.id, 'references', referenceId), {
            images: [{ storagePath, contentType, index: 0, ...heroImageTags }],
            updatedAt: Timestamp.now(),
          });
        } catch (imgErr) {
          // Image failure is non-fatal — reference was still created. Persist the
          // source URL so it's never lost and can be recovered later.
          console.warn(`Image upload failed for ${entry.reference}:`, imgErr);
          try { await setReferencePendingImageUrl(brand.id, referenceId, imageUrl); } catch { /* best effort */ }
          imageFailuresRef.current.note(
            { brandId: brand.id, referenceId, reference: entry.reference, entry },
            { kind: 'main', url: imageUrl, error: errorText(imgErr) },
          );
          updateItemStatus(index, {
            status: 'success',
            referenceId,
            error: `Created (image failed: ${imgErr instanceof Error ? imgErr.message : 'unknown'})`,
          });
          return;
        }
      }

      // Fresh doc, so there is never an existing gallery to merge into.
      await recordGalleryImport(referenceId, entry, imageUrl, undefined, brand.id);

      updateItemStatus(index, { status: 'success', referenceId });
    } catch (err) {
      updateItemStatus(index, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };


  /** A preview row's pseudo-reference id in the enrich grid. */
  /**
   * Open the review grid on the preview, before any write. Each included row
   * becomes a pseudo-reference (mapped exactly as the import will save it), and
   * each variant group's parent is built in memory the way the import builds it
   * (`buildParentEntry`) — it never needs to exist in Firestore. Variants point
   * at their in-memory parent, so the grid routes them to the variant prompt
   * with the parent as context; parents and standalones get the full prompt.
   */
  const handleOpenPreviewEnrich = async () => {
    const brandName = brand.displayName || brand.name;
    const toRef = (entry: ScrapedWatchEntry, id: string, extra: Partial<WatchReference> = {}) =>
      ({
        ...(mapEntryToReference(entry, brand.id, brandName, calibreDualWriteFlags) as Record<string, unknown>),
        id,
        brandId: brand.id,
        ...extra,
      }) as unknown as WatchReference;

    const parents = new Map<string, WatchReference>();
    const parentOfIndex = new Map<number, string>();
    // A row's images as the enrich call wants them: its own (scraped) image as
    // the tagged primary, then its gallery minus box/wrist/papers shots — the
    // same rule the Storage path applies (zipTaggedImages).
    const imagesById = new Map<string, TaggedEnrichImage[]>();
    const imagesOf = (entry: ScrapedWatchEntry): TaggedEnrichImage[] =>
      zipTaggedImages(
        entry.imgSrc?.trim() || null,
        (entry.taggedImages ?? [])
          .filter((t) => t.url && t.url !== entry.imgSrc)
          .map((t) => ({ url: t.url, tags: t.tags })),
      );
    const groupIndices = new Map<string, number[]>();
    for (const [, indices] of groupShopifyVariantsForImport(items)) {
      const group = variantGroupOf(items[indices[0]].entry);
      if (!group) continue;
      const lead = items[indices[0]].entry;
      const refs = indices.map((i) => (items[i].entry.reference ?? '').trim()).filter(Boolean);
      const parentRef =
        lead.storeParent?.reference?.trim() ||
        deriveParentReference(refs, lead.shopifyProductHandle ?? '', importStopwordsRef.current);
      if (!parentRef || !normalizeReference(parentRef)) continue; // the import writes these standalone
      const parentEntry = {
        ...buildParentEntry(lead, parentRef),
        ...(previewParentValuesRef.current.get(group.parentDocId) ?? {}),
      };
      parents.set(group.parentDocId, toRef(parentEntry, group.parentDocId));
      imagesById.set(group.parentDocId, imagesOf(parentEntry));
      groupIndices.set(group.parentDocId, indices);
      for (const i of indices) parentOfIndex.set(i, group.parentDocId);
    }
    const rows = items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.included)
      .map(({ it, i }) => {
        const pid = parentOfIndex.get(i);
        imagesById.set(previewRowId(i), imagesOf(it.entry));
        return toRef(it.entry, previewRowId(i), pid ? { isVariant: true, parentReferenceId: pid } : {});
      });
    if (rows.length === 0) {
      toast.error('No items selected to enrich');
      return;
    }
    previewGroupIndicesRef.current = groupIndices;
    // The brand's saved references, read once per opening (the pre-#684 preview
    // did the same): "Notable Variants" lists a reference's collection siblings,
    // and most of those are already in the catalogue, not in this batch. Doc-id
    // keyed, so docs sharing a reference string all survive. A failed read
    // enriches on the batch alone rather than blocking.
    setOpeningPreviewEnrich(true);
    let siblingPool: WatchReference[] = [];
    try {
      siblingPool = Array.from((await getExistingReferenceMapById(brand.id)).values());
    } catch (err) {
      console.warn('Could not load the brand catalogue for Notable Variants; using the batch only:', err);
    } finally {
      setOpeningPreviewEnrich(false);
    }
    setPreviewEnrich({ refs: [...parents.values(), ...rows], parents, siblingPool, imagesById });
  };

  /**
   * The grid's Apply, in memory: the rows' own ticked values, and each parent's
   * reviewed values — kept for the parent write and copied onto its variants'
   * empty fields. See applyPreviewEnrichRows.
   */
  const applyPreviewEnrich = (rows: Array<{ refId: string; values: Record<string, unknown> }>) => {
    setItems((prev) => {
      const { items: next, parentValues } = applyPreviewEnrichRows(
        prev,
        rows,
        previewGroupIndicesRef.current,
        previewParentValuesRef.current,
      );
      previewParentValuesRef.current = parentValues;
      return next;
    });
  };

  /**
   * The grid's in-memory host, stable for one opening — the grid keys its
   * callbacks (cache inputs, the enrich call) on it.
   */
  const previewEnrichHost = useMemo<InMemoryEnrichHost | null>(
    () =>
      previewEnrich
        ? {
            parentsById: previewEnrich.parents,
            acceptsKey: previewAcceptsAiKey,
            onApply: applyPreviewEnrich,
            siblingPool: previewEnrich.siblingPool,
            imagesFor: (ref) => previewEnrich.imagesById.get(ref.id) ?? [],
          }
        : null,
    // Per OPENING: rows and parents are captured when the grid opens; Apply
    // writes back through setItems.
    [previewEnrich],
  );

  // ── Inline exclusion rule creator (Enhancement 3) ───────────────────────────
  const EXCLUDE_FIELDS: { value: string; label: string }[] = [
    { value: 'title', label: 'Title' },
    { value: 'model', label: 'Model' },
    { value: 'collection', label: 'Collection' },
    { value: 'description', label: 'Description' },
    { value: 'productType', label: 'Product Type' },
    { value: 'variant', label: 'Variant' },
    { value: 'reference', label: 'Reference' },
  ];

  const EXCLUDE_CONDITIONS: { value: ExclusionRule['condition']; label: string }[] = [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'equals' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
  ];

  const getFieldValue = (rowIndex: number, field: string): string => {
    const entry = items[rowIndex]?.entry as unknown as Record<string, unknown>;
    const v = entry?.[field];
    return v === undefined || v === null ? '' : String(v);
  };

  const openExcludeForm = (rowIndex: number) => {
    const defaultField = 'title';
    setExcludeFormRow(rowIndex);
    setExcludeFormField(defaultField);
    setExcludeFormCondition('contains');
    setExcludeFormValue(getFieldValue(rowIndex, defaultField));
  };

  const closeExcludeForm = () => {
    setExcludeFormRow(null);
    setExcludeFormValue('');
  };

  const onExcludeFieldChange = (newField: string) => {
    setExcludeFormField(newField);
    if (excludeFormRow !== null) {
      setExcludeFormValue(getFieldValue(excludeFormRow, newField));
    }
  };

  const handleCreateExclusionRule = async () => {
    const value = excludeFormValue.trim();
    if (!value) {
      toast.error('Enter a value to match');
      return;
    }

    const newRule: ExclusionRule = {
      id: `excl-inline-${Date.now()}`,
      sourceField: excludeFormField,
      condition: excludeFormCondition,
      value,
      enabled: true,
    };
    const updatedExclusionRules = [...savedExclusionRules, newRule];
    setSavedExclusionRules(updatedExclusionRules);

    // Re-apply ALL exclusion rules so previously matched items stay excluded
    const enabledRules = updatedExclusionRules.filter((r) => r.enabled);
    const entriesArr = items.map((it) => it.entry as unknown as Record<string, unknown>);
    const { excludedIndices } = applyExclusionRules(entriesArr, enabledRules);

    const newlyExcluded = items.filter(
      (it, idx) => excludedIndices.has(idx) && it.included,
    ).length;
    setItems((prev) =>
      prev.map((it, idx) =>
        excludedIndices.has(idx) ? { ...it, included: false } : it,
      ),
    );

    closeExcludeForm();

    // Persist field + extraction + exclusion rules so this rule applies on future imports
    try {
      const fieldRulesToSave: PersistedFieldRule[] = savedFieldRules.map(
        ({ sourceField, condition, value: v, targetField, targetValue, enabled }) => ({
          sourceField, condition, value: v, targetField, targetValue, enabled,
        }),
      );
      const extractionToSave = extractionRules.map(({ sourceField, pattern, replaceWith, targetField }) => ({
        sourceField, pattern,
        ...(replaceWith ? { replaceWith } : {}),
        ...(targetField && targetField !== 'calibre' ? { targetField } : {}),
      }));
      const exclusionToSave: PersistedExclusionRule[] = updatedExclusionRules.map(
        ({ sourceField, condition, value: v, enabled }) => ({ sourceField, condition, value: v, enabled }),
      );
      await saveImportConfigForBrand(brand.id, fieldRulesToSave, extractionToSave, exclusionToSave);
    } catch {
      // non-fatal — rule still applies in this session
    }

    toast.success(
      `Excluded ${newlyExcluded} ${newlyExcluded === 1 ? 'item' : 'items'} — rule saved for future imports`,
    );
  };

  // Create or update the PARENT ref for a Shopify variant group, returning its
  // doc id. The parent holds the model's shared spec (variant-distinguishing
  // fields stripped) and IS the consumer-visible row (variants are not indexed),
  // so it also carries an image. Idempotent: on re-import it updates the
  // existing parent in place (preserving its doc id) rather than creating a
  // duplicate.
  const upsertParentReference = async (
    parentRef: string,
    groupItems: ImportItem[],
    // Doc-id-keyed existing-refs map (see getExistingReferenceMapById).
    existingById: Map<string, WatchReference>,
    // The variant group (Shopify / WooCommerce / Square Online) — its parent doc
    // id is independent of the (non-unique, derived) parentRef display string.
    group: VariantGroup,
    // Each variant's EFFECTIVE ref — the cleaned value, or the stored one where
    // the admin unticked that row's REF CHANGED box. Used for the parent's
    // `variantReferences` array so Algolia keeps resolving searches for the ref
    // each variant doc actually carries.
    effectiveVariantRefs: string[],
    // As for importSingle: present only on a routed run. `existingById` must then
    // be the map of the brand the route names.
    route?: ImportRoute,
  ): Promise<{ id: string; status: 'created' | 'updated' | 'diverged' | 'unchanged' } | undefined> => {
    const brand = route?.brand ?? importingBrand;
    const stampFields = brandStampFields(route?.stamp);
    const template = groupItems[0].entry;
    const variantEntries = groupItems.map((it) => it.entry);
    // The same in-memory parent the preview's AI enrichment reviewed, plus any
    // values the curator accepted for it there.
    const parentEntry: ScrapedWatchEntry = {
      ...buildParentEntry(template, parentRef),
      ...(previewParentValuesRef.current.get(group.parentDocId) ?? {}),
    };
    const parentData = deepStripEmpty(
      mapEntryToReference(
        parentEntry,
        brand.id,
        brand.displayName || brand.name,
        calibreDualWriteFlags,
      ) as Record<string, unknown>,
    ) as Record<string, unknown>;
    parentData.referenceNormalized = normalizeReference(parentRef);
    // A parent is never itself a variant.
    delete parentData.parentReferenceId;
    // Provenance + composite-id source id. A parent spans all variants of one
    // product, so it carries the product id but no single variant id.
    parentData.sourceType = group.source satisfies ReferenceSource;
    if (group.source === 'shopify') parentData.shopifyProductId = group.productId;

    // Stable, ref-string-independent parent identity. Found directly by its
    // composite doc id — so a re-import (or a parent whose display ref collides
    // with a sibling product's SKU) always updates the same doc in place.
    const parentDocId = group.parentDocId;
    const existingParent = existingById.get(parentDocId);

    // Aggregate variant-distinguishing fields onto the parent so consumer
    // Algolia search/filter still works (variant docs aren't indexed). The
    // union spans the PARENT'S OWN value + every variant's value: a parent can
    // be a distinct watch whose value differs from its variants (e.g. Alkin
    // AM1-B is black while its AM1-B-1/-2 variants are green → ["black",
    // "green"]). Each array is written ONLY when ≥2 distinct values exist; a
    // single shared value is already covered by the parent's own field.
    //
    // The parent's own value is taken from the EXISTING parent doc when present
    // (it carries the parent's distinct identity), mapped onto a synthetic
    // entry so the same per-field resolvers apply. A brand-new parent has no
    // prior value — its field equals a variant's, so the variants-only union is
    // already correct. An unset parent field is skipped, like a missing variant.
    const aggregationEntries: ScrapedWatchEntry[] = [...variantEntries];
    if (existingParent) {
      const parentDial = existingParent.dialAndHands?.color ?? existingParent.dial;
      const parentAsEntry = {
        reference: parentRef,
        model: existingParent.model ?? '',
        dialColor: Array.isArray(parentDial) ? parentDial.join(',') : parentDial,
        // Pass through the parent's stored dialStyle so variantDialStyles picks
        // it up directly (cast: shared-types v1.12.1 adds dialStyle, not pinned yet).
        dialStyle: (existingParent as { dialStyle?: string }).dialStyle,
        // Pass through the parent's stored finish so variantDialFinishes picks it
        // up directly (same passthrough rationale as dialStyle above).
        dialFinish: existingParent.dialAndHands?.finish,
        strapMaterial: existingParent.strap?.material,
        caseMaterial: existingParent.case?.material,
        // variantMovements reads `.calibre` first, then extractCalibre(.movement)
        calibre: existingParent.calibre,
        movement: existingParent.calibre,
      } as unknown as ScrapedWatchEntry;
      aggregationEntries.unshift(parentAsEntry);
    }

    const dialColorVariants = aggregateVariantField(aggregationEntries, variantDialColors);
    const movementVariants = aggregateVariantField(aggregationEntries, variantMovements);
    const strapTypeVariants = aggregateVariantField(aggregationEntries, variantStrapTypes);
    const caseMaterialVariants = aggregateVariantField(aggregationEntries, variantCaseMaterials);
    const dialStyleVariants = aggregateVariantField(aggregationEntries, variantDialStyles);
    const dialFinishVariants = aggregateVariantField(aggregationEntries, variantDialFinishes);
    if (dialColorVariants.length) parentData.dialColorVariants = dialColorVariants;
    if (movementVariants.length) parentData.movementVariants = movementVariants;
    if (strapTypeVariants.length) parentData.strapTypeVariants = strapTypeVariants;
    if (caseMaterialVariants.length) parentData.caseMaterialVariants = caseMaterialVariants;
    if (dialStyleVariants.length) parentData.dialStyleVariants = dialStyleVariants;
    if (dialFinishVariants.length) parentData.dialFinishVariants = dialFinishVariants;

    // Every variant's SKU, deduped + sorted, persisted on the parent so the
    // watchlock Algolia indexer (buildAlgoliaRecord) can make a search for any
    // variant's real-world ref hit this (only-indexed) parent doc — variant docs
    // themselves stay out of Algolia. The indexer unions this with the parent's
    // own `reference`, so we store the variant SKUs only. The backfill callable
    // (backfillShopifyParentRefsAndVariants) writes the same array on parents
    // imported before this shipped.
    const variantReferences = Array.from(
      new Set(effectiveVariantRefs.map((r) => (r ?? '').trim()).filter(Boolean)),
    ).sort();
    if (variantReferences.length) parentData.variantReferences = variantReferences;

    // The parent's display ref is derived from its lead variant's SKU, so a
    // stopword strip moves it too — and unlike a variant, a parent has no
    // preview row of its own to tick. It follows the group: `parentRef` is
    // derived from the EFFECTIVE variant refs, so each row's accept/keep
    // decision is already baked in, and whenever the result disagrees with
    // what's stored the parent is brought in line. Force-written for the same
    // reason as the variant path — the fill-empty diff would never correct an
    // already-populated `reference` — and it matters more here: parents are the
    // only Shopify docs Algolia indexes, so a polluted parent keeps the junk
    // word in consumer search.
    const parentRefChanged =
      !!existingParent &&
      !!existingParent.reference &&
      existingParent.reference !== parentRef;

    const parentId = parentDocId;
    // Idempotent write. Only touch Firestore when the parent is new OR the
    // mapped payload actually differs from what's stored — otherwise a no-change
    // re-import would rewrite every parent (bumping updatedAt) and report it as
    // "updated". Uses the same merge-semantics diff as the variant overwrite
    // path (hasActualChanges): true iff a non-empty field in parentData differs
    // from existingParent. Transient fields (createdAt/updatedAt) aren't in
    // parentData so they never trigger a false positive.
    const created = !existingParent;
    const changed =
      created ||
      hasActualChanges(
        existingParent as unknown as Record<string, unknown>,
        parentData,
      );

    // Resolve the status and the write payload together. Four outcomes:
    //   'created'   — new parent; write all fields (+ createdAt).
    //   'updated'   — existing parent, fill-empty diff is non-empty; write it.
    //   'diverged'  — existing parent, fresh extraction differs (changed) but the
    //                 fill-empty diff is EMPTY: a stored/curated value disagrees
    //                 with the source, so nothing is written (curation preserved).
    //   'unchanged' — existing parent, source matches stored; nothing written.
    let status: 'created' | 'updated' | 'diverged' | 'unchanged';
    // The fill-empty-only diff never clobbers user-curated fields (calibre
    // strings, AI-enriched descriptions, manually-set tiers, etc.) — same
    // semantics as the variant merge path uses via buildMergeDiff. A raw
    // `...parentData` on an existing parent would REPLACE every extracted field,
    // silently overwriting curation on every re-run.
    let diff: Record<string, unknown> | null = null;
    if (created) {
      status = 'created';
      diff = parentData; // first-time create: write all fields
    } else if (!changed) {
      status = 'unchanged';
    } else {
      const mergeDiff = buildMergeDiff(
        existingParent as unknown as Record<string, unknown>,
        parentData,
      );
      if (parentRefChanged) {
        mergeDiff.reference = parentData.reference;
        mergeDiff.referenceNormalized = parentData.referenceNormalized;
        if (parentData.searchText) mergeDiff.searchText = parentData.searchText;
      }
      if (Object.keys(mergeDiff).length > 0) {
        status = 'updated';
        diff = mergeDiff;
      } else {
        // Changed vs stored, but every difference lands on an already-populated
        // field → empty diff → no write. Report divergence, not a write.
        status = 'diverged';
      }
    }

    // A stamp is written whether or not the scrape changed anything — it is
    // about the reference, not about this diff — and then counts as an update.
    if (!diff && stampFields && status === 'unchanged') status = 'updated';
    if (diff || stampFields) {
      // Upsert at the composite doc id. merge:true adds new fields in place
      // (preserving aggregates/image); createdAt is only stamped on the first
      // write. No ref-string dedup — the composite id IS the identity.
      const docRef = doc(db, 'watchBrands', brand.id, 'references', parentId);
      await setDoc(
        docRef,
        {
          ...(diff ?? {}),
          ...(diff ? importDescriptionTag(diff, parentEntry, existingParent?.originalDescription, Timestamp.now()) : {}),
          ...(stampFields ?? {}),
          // A parent is never itself a variant (parentReferenceId is deleted
          // off parentData above). Written outside `diff` for the same reason
          // as the variant paths: keeping the boolean out of parentData stops
          // it feeding hasActualChanges/buildMergeDiff, so it can never make a
          // no-change re-import report as 'updated'.
          isVariant: false,
          ...(created ? { createdAt: Timestamp.now() } : {}),
          updatedAt: Timestamp.now(),
        },
        { merge: true },
      );
    }

    // Give the parent an image when it has none (skip on re-imports that already
    // attached one — avoids redundant re-uploads).
    const parentHasImage = !!existingParent?.images && existingParent.images.length > 0;
    if (parentId && template.imgSrc && !parentHasImage) {
      try {
        const { storagePath, contentType } = await ingestReferenceImage(brand.id, {
          referenceId: parentId,
          imageUrl: template.imgSrc,
          kind: 'main',
          index: 0,
        });
        const heroTags = selectHeroImageTags(template.taggedImages, template.imgSrc);
        await updateWatchReference(brand.id, parentId, {
          images: [
            {
              storagePath,
              contentType,
              index: 0,
              ...(heroTags.length > 0 ? { tags: heroTags } : {}),
            },
          ],
        });
      } catch (imgErr) {
        console.warn(`Parent image upload failed for ${parentRef}:`, imgErr);
        try { await setReferencePendingImageUrl(brand.id, parentId, template.imgSrc); } catch { /* best effort */ }
        imageFailuresRef.current.note(
          { brandId: brand.id, referenceId: parentId, reference: parentRef, entry: template },
          { kind: 'main', url: template.imgSrc, error: errorText(imgErr) },
        );
      }
    }

    // The parent inherits the template variant's extra shots, merged on the same
    // additive terms as everywhere else.
    if (parentId) {
      await recordGalleryImport(
        parentId,
        template,
        template.imgSrc ?? '',
        existingParent?.galleryImages,
        brand.id,
        parentRef,
      );
    }

    return { id: parentId, status };
  };

  /**
   * Work out, for every Shopify product in this run, which brand its documents
   * belong under and what to stamp on them. Returns null when routing does not
   * apply (no Shopify rows, or no acquired sibling with a brand doc).
   *
   * Reads: the importing brand doc (fresh — the `brand` prop can be a listing
   * projection without `acquisitions`), one doc per acquisition, and the run's
   * own doc ids under each sibling. It never reads a sibling's whole catalogue.
   */
  const resolveImportRouting = async (
    // The importing brand's references when the caller already holds them all
    // (the import run), or null to read just this run's doc ids (the preview).
    existingById: Map<string, WatchReference> | null,
    // The curator's per-product brand picks from the preview.
    chosen: ReadonlyMap<string, string>,
    // The import run reports what it resolved; the preview stays quiet.
    report: boolean,
  ) => {
    // Every row with a store composite id (Shopify, WooCommerce, Square Online):
    // routing is what makes a reference that already lives under an acquired
    // sibling brand win over a fresh copy under the importing brand.
    const routableItems = items.filter((it) => storeDocIdForEntry(it.entry));
    if (routableItems.length === 0) return null;

    const freshBrand = (await getWatchBrandById(importingBrand.id)) ?? importingBrand;
    const siblingIds = acquisitionSiblingIds(freshBrand);
    if (siblingIds.length === 0) return null;
    const siblingDocs = await Promise.all(siblingIds.map((id) => getWatchBrandById(id)));
    const ctx = buildRoutingContext(
      { ...importingBrand, acquisitions: freshBrand.acquisitions },
      new Map<string, RoutingBrand | null>(siblingIds.map((id, i) => [id, siblingDocs[i]])),
    );
    if (!ctx) return null;

    // Group rows by product. Shopify keeps its bare product id as the key (the
    // preview's per-product brand picks are keyed on it); a WooCommerce /
    // Square Online variant groups under its product-level doc id, and a
    // standalone store row is a product of its own.
    const productKeyOf = (it: ImportItem): string =>
      it.entry.shopifyProductId ?? variantGroupOf(it.entry)?.key ?? (storeDocIdForEntry(it.entry) as string);
    const parentDocIdOf = (key: string, group: ImportItem[]): string =>
      group[0].entry.shopifyProductId
        ? shopifyParentDocId(key)
        : variantGroupOf(group[0].entry)?.parentDocId ?? `${key}__parent`;
    const byProduct = new Map<string, ImportItem[]>();
    for (const it of routableItems) {
      const key = productKeyOf(it);
      byProduct.set(key, [...(byProduct.get(key) ?? []), it]);
    }
    const docIdsOf = (_key: string, group: ImportItem[]) =>
      group.map((it) => storeDocIdForEntry(it.entry) as string);
    const wanted = [...byProduct].flatMap(([key, group]) => [
      ...docIdsOf(key, group),
      parentDocIdOf(key, group),
    ]);

    const existingByBrand = new Map<string, Map<string, WatchReference>>();
    await Promise.all([
      (async () => {
        existingByBrand.set(
          importingBrand.id,
          existingById ?? (await getReferencesByDocIds(importingBrand.id, wanted)),
        );
      })(),
      ...ctx.siblings.map(async (sibling) => {
        existingByBrand.set(sibling.id, await getReferencesByDocIds(sibling.id, wanted));
      }),
    ]);

    const brandById = new Map(routingOptions(ctx).map((b) => [b.id, b]));
    const plan = planImportRouting(
      [...byProduct].map(([productId, group]) => {
        const first = group[0].entry;
        return {
          productId,
          label: first.productName || first.title || productId,
          variantDocIds: docIdsOf(productId, group),
          parentDocId: parentDocIdOf(productId, group),
          signals: {
            vendor: first.shopifyVendor,
            tags: first.tags,
            title: first.productName || first.title,
            handle: first.shopifyProductHandle,
          },
        };
      }),
      ctx,
      (brandId, docId) => existingByBrand.get(brandId)?.get(docId),
      chosen,
    );

    const docs = new Map<string, { route: ImportRoute; existing?: WatchReference }>();
    for (const [docId, routed] of plan.docs) {
      docs.set(docId, {
        existing: routed.existing ? existingByBrand.get(routed.brandId)?.get(docId) : undefined,
        route: { brand: brandById.get(routed.brandId) ?? importingBrand, stamp: routed.stamp },
      });
    }
    const { perBrand, flagged } = plan;
    const splitProducts = plan.split.map((sp) => `${sp.label} (${sp.brandIds.join(' + ')})`);

    if (report) {
      console.info(
        `[import-routing] ${byProduct.size} product${byProduct.size === 1 ? '' : 's'}: ` +
          [...perBrand].map(([id, n]) => `${brandById.get(id)?.name ?? id} ${n}`).join(', ') +
          (flagged > 0 ? ` — ${flagged} with an unknown vendor or a vendor/title mismatch` : ''),
      );
      if (splitProducts.length > 0) {
        console.warn('[import-routing] products split across brands (updated in place, not stamped):', splitProducts);
        toast(
          `${splitProducts.length} product${splitProducts.length === 1 ? ' has' : 's have'} references under more than one brand — each was updated where it is. See the console for the list.`,
          { icon: '⚠️', duration: 10000 },
        );
      }
    }

    return { ctx, plan, docs, existingByBrand, brandById };
  };

  const handleImport = async () => {
    setImporting(true);
    setStep('importing');
    setImportPhase('Loading existing references…');
    setParentProgress({ done: 0, total: 0 });
    galleryStatsRef.current = { refs: 0, added: 0, tagged: 0, replaced: 0 };
    setGalleryStats({ refs: 0, added: 0, tagged: 0, replaced: 0 });
    imageFailuresRef.current = new ImportImageFailureCollector<ScrapedWatchEntry>();
    setImageFailureRows([]);
    abortRef.current = false;
    // Fresh per-run token for this import — stamped on every variant write below
    // so the watchlock aggregate-propagation trigger skips its redundant
    // recompute (the parent pre-pass already writes final aggregates). A unique
    // value per run means a re-import's writes also register as "token changed".
    importRunTokenRef.current = crypto.randomUUID();

    // Fetch existing references for duplicate detection. Keyed by DOC ID so docs
    // that share a `reference` SKU (the Shopify composite-id model allows this)
    // are never dropped. The ref-string map — same last-write-wins semantics as
    // before — is derived from it for the non-Shopify path. One read, both maps.
    const existingById = await getExistingReferenceMapById(brand.id);
    const existingRefMap = new Map<string, WatchReference>();
    for (const r of existingById.values()) {
      if (r.reference) existingRefMap.set(r.reference, r);
    }

    // ── Brand routing (Shopify rows only) ─────────────────────────────────────
    // Active only when the importing brand has acquired a brand that has its own
    // brand doc; for everyone else `routing` stays null and nothing below
    // changes. See importBrandRouting.ts for the rules.
    //
    // Deliberately NOT wrapped in a try/catch: if the sibling lookup fails, the
    // run must stop rather than carry on blind and create under the importing
    // brand a second copy of references that already live under a sibling.
    const routing = await resolveImportRouting(existingById, brandChoiceByProduct, true);

    // Load the dial detectors/resolvers for this run (data-driven from
    // lookup_dial_styles / lookup_dial_colors / lookup_dial_finish). Non-fatal:
    // an unseeded lookup leaves the detector empty, so imported refs default to
    // single-color, no themed colors, and no finish decomposition.
    //
    // The crown/crystal vocabularies ride along in the same block: they let
    // ingest resolve an extractor's comma-joined "Screw-down, Twinlock double
    // waterproofness system" to ["screw_down", "twinlock"] on the way in,
    // rather than storing raw text for migrateCrownTypeCrystal to fix later.
    // Same fail-safe — an unloaded vocabulary just leaves the values raw.
    try {
      const [
        styleVals,
        colorVals,
        finishVals,
        watchTypeVals,
        crownVals,
        crystalVals,
        bezelColorVals,
      ] = await Promise.all([
        getLookupCollection('lookup_dial_styles'),
        getLookupCollection('lookup_dial_colors'),
        getLookupCollection('lookup_dial_finish'),
        getLookupCollection('lookup_watch_types'),
        getLookupCollection('lookup_crown_types'),
        getLookupCollection('lookup_crystal_materials'),
        getLookupCollection('lookup_bezel_colors'),
      ]);
      buildDialStyleDetector(styleVals);
      buildDialColorResolver(colorVals);
      buildDialFinishResolver(finishVals);
      buildWatchTypeResolver(watchTypeVals);
      setMultiSelectVocabulary('lookup_crown_types', crownVals);
      setMultiSelectVocabulary('lookup_crystal_materials', crystalVals);
      // Bezel colours ride along for the same reason: a source that writes
      // "Red / blue Cerachrom" should land as ["red", "blue"], not as one
      // custom chip. The resolver strips the generic "bezel" suffix too, so a
      // "Black bezel" row resolves to ["black"].
      setMultiSelectVocabulary('lookup_bezel_colors', bezelColorVals);
    } catch (err) {
      console.warn('Failed to load dial / watch-type lookups for import:', err);
    }

    // ── Electronic-module link pre-pass (digital watches) ─────────────────────
    // Resolve every distinct extracted `module` string against the shared
    // `electronicModules` collection: match by name OR alsoKnownAs (case-
    // insensitive), and create a bare `{ name }` doc for any that don't exist —
    // mirroring the ref editor's Link-or-Create flow. Build a string→moduleId map
    // importSingle stamps onto each ref's `moduleId`. Non-fatal: a failure just
    // leaves refs unlinked (they still import). One batched read + N creates,
    // done once per run rather than per item.
    const moduleIdByString = new Map<string, string>();
    // Extracted module strings that couldn't be resolved to (or created as) a
    // module doc this run. The refs still import — just unlinked (no moduleId) —
    // so these are collected and surfaced as a warning summary below rather than
    // failing silently (previously each failure was only console.warn'd).
    const failedModuleCreates: string[] = [];
    const moduleStrings = [
      ...new Set(
        items.map((it) => (it.entry.module || '').trim()).filter(Boolean),
      ),
    ];
    try {
      const preResolved = preResolvedModuleIdsRef.current;
      // Strings the module-check step already resolved to a concrete module id
      // (user AI-link, manual link, or local-index match) are authoritative: link
      // them directly and DON'T re-run match-or-create, which re-derives from the
      // raw string and would create a duplicate whenever that string differs from
      // the module's canonical name. Only genuinely-unresolved strings fall
      // through to findModulesByLegacyValues → match-or-create.
      const unresolved: string[] = [];
      for (const s of moduleStrings) {
        const preId = preResolved.get(s);
        if (preId) moduleIdByString.set(s, preId);
        else unresolved.push(s);
      }
      if (unresolved.length > 0) {
        const matched = await findModulesByLegacyValues(unresolved);
        for (const s of unresolved) {
          const existing = matched.get(s);
          if (existing) {
            moduleIdByString.set(s, existing.id);
            continue;
          }
          try {
            const id = await saveElectronicModule({ name: s });
            moduleIdByString.set(s, id);
          } catch (createErr) {
            console.warn(`Failed to create electronic module "${s}":`, createErr);
            failedModuleCreates.push(s);
          }
        }
      }
    } catch (err) {
      console.warn('Failed to resolve electronic modules for import:', err);
      // The whole pre-pass threw (e.g. the batch lookup failed): every string
      // that didn't already resolve is now unlinkable — surface them all too.
      for (const s of moduleStrings) {
        if (!moduleIdByString.has(s) && !failedModuleCreates.includes(s)) {
          failedModuleCreates.push(s);
        }
      }
    }
    moduleIdByStringRef.current = moduleIdByString;
    // Warn (don't block) on silent module-create failures. Mirrors the
    // toast.error summary pattern used for AI-enrichment / URL-extraction fails.
    if (failedModuleCreates.length > 0) {
      toast.error(
        `${failedModuleCreates.length} module${failedModuleCreates.length === 1 ? '' : 's'} failed to create — those refs import unlinked (see console)`,
      );
    }

    // Provenance for this run. Shopify entries are tagged individually below
    // (they carry product ids); everything else inherits the run's pipeline:
    // a Shopify URL fetch, a generic URL crawl, or a file/manual upload.
    const runSource: ReferenceSource = shopifyUrl
      ? 'shopify'
      : autoStartUrls
        ? 'generic'
        : 'manual';

    // ── Parent/variant grouping (Shopify variant products only) ───────────────
    // Group the items to import by Shopify PRODUCT ID. A product with 2+ variants
    // gets one parent doc (id `shopify__{productId}__parent`) created/updated up
    // front; each variant then imports linked to it via parentReferenceId.
    // Single-variant products and file/URL imports carry no product id/count → they
    // import standalone, exactly as before (no parent doc).
    const parentIdByItemIndex = new Map<number, string>();
    // productId → item indices. CRITICAL: unticked rows are filtered out
    // (shouldWritePreviewItem) inside the helper — otherwise excluded refs
    // get a parent doc created for them, and the "Resolving parents…"
    // progress list shows ghost refs the admin thought they'd dropped.
    // Groups whose only ticked rows were excluded are omitted entirely.
    const variantGroups = groupShopifyVariantsForImport(items);

    // Tally synthetic parent creates/updates as we go (these refs aren't in
    // `items`, so they're invisible to the status-based counters below).
    let parentsAddedRun = 0;
    let parentsUpdatedRun = 0;
    let parentsDivergedRun = 0;

    // Surface the parent pre-pass: this loop does one Firestore upsert per
    // multi-variant product, sequentially, so it can run for tens of seconds on
    // a big brand. Each iteration awaits a network write, so React repaints the
    // counter between iterations — no explicit yield needed.
    if (variantGroups.size > 0) {
      setImportPhase('Resolving parents…');
      setParentProgress({ done: 0, total: variantGroups.size });
    }
    let parentsResolved = 0;

    for (const [groupKey, indices] of variantGroups) {
      if (abortRef.current) break;
      const group = variantGroupOf(items[indices[0]].entry);
      if (!group) continue;
      parentsResolved += 1;
      setParentProgress({ done: parentsResolved, total: variantGroups.size });
      const groupItems = indices.map((i) => items[i]);
      // EFFECTIVE variant refs — the cleaned value, except on rows whose REF
      // CHANGED box the admin unticked, which keep their stored ref. Derived
      // here rather than read off `item.entry` (importSingle pins the entry
      // later, per item) so the parent's display ref and `variantReferences`
      // agree with what the variant docs will actually hold.
      const variantRefs = indices
        .map((idx) => {
          const entry = items[idx].entry;
          const derived = (entry.reference ?? '').trim();
          const docId = storeDocIdForEntry(entry);
          const routedDoc = docId ? routing?.docs.get(docId) : undefined;
          const stored = routing
            ? routedDoc?.existing?.reference
            : docId
              ? existingById.get(docId)?.reference
              : undefined;
          if (!refRewriteOptOut.has(idx)) return derived;
          return stored && derived && stored !== derived ? stored : derived;
        })
        .filter(Boolean);
      if (variantRefs.length === 0) continue;
      const handle = groupItems[0].entry.shopifyProductHandle ?? '';

      // Display ref only — doc identity is the composite parent id, so this no
      // longer needs to be unique against the variants' SKUs (the old collision
      // guard is gone). It still feeds the parent's `reference`/search text.
      // An expanded store product names its own parent (its SKU or name).
      const parentRef =
        groupItems[0].entry.storeParent?.reference?.trim() ||
        deriveParentReference(variantRefs, handle, importStopwordsRef.current);
      if (!parentRef || !normalizeReference(parentRef)) continue; // junk → variants standalone

      try {
        // Routed run: the parent is read from, and written to, its own brand.
        const parentRoute = routing?.docs.get(group.parentDocId)?.route;
        const parent = await upsertParentReference(
          parentRef,
          groupItems,
          parentRoute
            ? routing?.existingByBrand.get(parentRoute.brand.id) ?? new Map<string, WatchReference>()
            : existingById,
          group,
          variantRefs,
          parentRoute,
        );
        if (parent) {
          for (const i of indices) parentIdByItemIndex.set(i, parent.id);
          // 'unchanged' parents are counted in no tally — a no-change re-import
          // reports 0 added / 0 updated for parents, matching variants. 'diverged'
          // parents (source disagrees, nothing written) are tallied separately as
          // an informational signal.
          if (parent.status === 'created') parentsAddedRun += 1;
          else if (parent.status === 'updated') parentsUpdatedRun += 1;
          else if (parent.status === 'diverged') parentsDivergedRun += 1;
        }
      } catch (err) {
        // Non-fatal: if the parent can't be created the variants still import
        // standalone (just without the parent link this run).
        console.error(`Failed to upsert parent for product "${groupKey}":`, err);
      }
    }

    // Identity for one row: Shopify / Square / WooCommerce items resolve by
    // composite doc id (store product ids), looked up in the doc-id map;
    // everything else dedups by SKU string. Routing only ever knows Shopify
    // ids, so a Square id simply finds no route.
    const resolveExisting = (item: ImportItem) => {
      const shopifyDocId = storeDocIdForEntry(item.entry);
      const routed = shopifyDocId ? routing?.docs.get(shopifyDocId) : undefined;
      const existingRef = routed
        ? routed.existing
        : shopifyDocId
          ? existingById.get(shopifyDocId)
          : existingRefMap.get(item.entry.reference);
      return { shopifyDocId, routed, existingRef };
    };

    // Write one row (or mark it excluded). Shared by the rule pre-pass, which
    // writes each accepted group's parent first, and the batch loop.
    const writeItem = async (item: ImportItem, i: number) => {
      const { shopifyProductId, squareProductId, wooProductId } = item.entry;
      const { shopifyDocId, routed, existingRef } = resolveExisting(item);

      const itemSource: ReferenceSource = shopifyProductId
        ? 'shopify'
        : squareProductId
          ? 'square_online'
          : wooProductId
            ? 'woocommerce'
            : runSource;
      // REF CHANGED decision. Recomputed here against the FRESH
      // whole-brand read rather than trusting the preview's snapshot (the
      // doc may have moved since), while the admin's per-row choice comes
      // from the opt-out set — absent means the checkbox stayed ticked.
      // Gated on `shopifyDocId` (any composite store id): only those docs have
      // ref-independent identity, so only they can have their ref rewritten
      // in place.
      const storedRefString = existingRef?.reference;
      const derivedRefString = (item.entry.reference ?? '').trim();
      const refRewrite =
        shopifyDocId && storedRefString && derivedRefString &&
        storedRefString !== derivedRefString
          ? { stored: storedRefString, accept: !refRewriteOptOut.has(i) }
          : undefined;

      /*
       * The checkbox is the whole decision. An unticked row is never
       * written — full stop.
       *
       * This used to be `!item.included && !canSelfHeal`: an imageless
       * row whose existing ref carried a `pendingImageUrl` was imported
       * even though the admin left it unticked. The preview showed those
       * rows unchecked, so nothing on screen said they would be written —
       * and that is how 163 Glycine heritage refs landed with no image,
       * self-healing past the gate on `pendingImageUrl`s left by a dead
       * 2026 scrape that never resolved.
       *
       * The recovery path itself is kept: a row the admin DOES tick still
       * falls back to its saved `pendingImageUrl` inside `importSingle`.
       * It is opt-in per row now, instead of a silent override. The rule
       * lives in importGate.ts, where a test pins it.
       */
      if (!shouldWritePreviewItem(item, existingRef)) {
        updateItemStatus(i, { status: 'excluded', error: 'Excluded' });
      } else if (item.status === 'pending') {
        await importSingle(item, i, existingRef, overwriteExisting, parentIdByItemIndex.get(i), itemSource, shopifyDocId, refRewrite, routed?.route);
      }
    };

    // ── Variant grouping rule (accepted preview groups) ─────────────────────
    // Each accepted group's parent row is written first, as an ordinary
    // reference; its members then import as its variants. Rows the store
    // already grouped never reach a rule group (proposeRuleGroups skips them).
    // A group is skipped — every member imports standalone — when its parent
    // could not be written or is itself a variant already; a member that is
    // already some other parent's variant keeps that parent.
    const rulePrewritten = new Set<number>();
    const ruleNotes: string[] = [];
    const acceptedRuleGroups = resolveAcceptedGroups(ruleGroups, ruleDecisions, (i) => items[i]?.entry.reference ?? '')
      .map((g) => ({ ...g, variantIndices: g.variantIndices.filter((i) => items[i] && shouldWritePreviewItem(items[i])) }))
      .filter((g) => items[g.parentIndex] && shouldWritePreviewItem(items[g.parentIndex]) && g.variantIndices.length > 0);
    if (acceptedRuleGroups.length > 0) setImportPhase('Writing rule-group parents…');
    for (const g of acceptedRuleGroups) {
      if (abortRef.current) break;
      const parentItem = items[g.parentIndex];
      const parentRefStr = parentItem.entry.reference;
      const { existingRef: parentExisting } = resolveExisting(parentItem);
      if (deriveIsVariant(parentExisting?.parentReferenceId)) {
        ruleNotes.push(`${g.key}: parent ${parentRefStr} is already a variant — group not applied`);
        continue;
      }
      rulePrewritten.add(g.parentIndex);
      // Counted through by the batch loop (it skips the write, not the count).
      try {
        await writeItem(parentItem, g.parentIndex);
      } catch (err) {
        console.error(`Rule-group parent ${parentRefStr} failed:`, err);
      }
      const parentId = writtenRefIds.current.get(g.parentIndex);
      if (!parentId) {
        ruleNotes.push(`${g.key}: parent ${parentRefStr} was not written — members imported standalone`);
        continue;
      }
      for (const vi of g.variantIndices) {
        const { existingRef } = resolveExisting(items[vi]);
        const current = existingRef?.parentReferenceId;
        if (deriveIsVariant(current) && current !== parentId) {
          ruleNotes.push(`${items[vi].entry.reference}: already a variant of another parent — kept`);
          continue;
        }
        if (existingRef?.id === parentId) continue;
        parentIdByItemIndex.set(vi, parentId);
      }
    }
    if (ruleNotes.length > 0) {
      console.warn('[variant grouping rule]', ruleNotes);
      toast(`Variant grouping: ${ruleNotes.length} note${ruleNotes.length === 1 ? '' : 's'} — ${ruleNotes.slice(0, 3).join('; ')}${ruleNotes.length > 3 ? '…' : ''}`, { duration: 10000 });
    }

    // Snapshot the breakdown inputs for the summary: synthetic parent counts and
    // which item indices are variants (those linked to a parent this run).
    setSyntheticParentStats({ added: parentsAddedRun, updated: parentsUpdatedRun, diverged: parentsDivergedRun });
    setVariantItemIndices(new Set(parentIdByItemIndex.keys()));
    setRuleParentItemIndices(new Set([...rulePrewritten].filter((i) => writtenRefIds.current.has(i))));

    // Process in bounded-parallel batches. Each item still does its own
    // sequential Firestore/Storage work, but BATCH_SIZE items run concurrently
    // so a slow image fetch on one watch no longer blocks the others. The
    // dominant cost per watch is the network round-trip (image proxy /
    // Storage upload), so overlapping them is where the throughput win comes from.
    setImportPhase('Writing references…');
    const BATCH_SIZE = 6;
    for (let start = 0; start < items.length; start += BATCH_SIZE) {
      if (abortRef.current) break;
      const batch = items.slice(start, start + BATCH_SIZE);
      // Promise.all never rejects here because each task swallows its own
      // errors below, so one failing item can't abort the rest of the batch.
      await Promise.all(
        batch.map(async (item, offset) => {
          const i = start + offset;
          if (abortRef.current) return;
          try {
            // Rule-group parents were written by the pre-pass above.
            if (rulePrewritten.has(i)) return;
            await writeItem(item, i);
            // already-processed (non-pending) items just count through
          } catch (err) {
            // importSingle records its own errors, but guard against unexpected
            // throws so a single item can't reject the whole batch.
            console.error(`Import failed for item ${i}:`, err);
            updateItemStatus(i, {
              status: 'error',
              error: err instanceof Error ? err.message : 'Unknown error',
            });
          } finally {
            // Functional update — avoids a read-modify-write race on a shared
            // counter now that items complete concurrently. Increments as each
            // item finishes, so the UI counter advances in real time.
            setProcessedCount((c) => c + 1);
          }
        }),
      );
    }

    // Store shop-URL runs: the source remembers which categories were left out,
    // as the Shopify path remembers its excluded product types.
    if (storeCatExcludedRef.current) {
      storeCategoryPicker?.onExclusionsSaved?.(storeCatExcludedRef.current);
    }

    // Save Shopify metadata to brand (only for Shopify imports)
    if (shopifyUrl) {
      try {
        const allTypes = shopifyTypes.map((t) => t.type);
        const excluded = allTypes.filter((t) => !selectedTypes.has(t));
        const lastImportedAt = Timestamp.now();
        // Don't re-write the legacy shopifyEnabled / shopifyUrl fields here —
        // they're owned by the import-sources config now (and cleared from the
        // brand doc when the last Shopify source is removed). Resurrecting them
        // on every import would defeat that cleanup. Display reads the sources
        // doc, not these fields.
        await updateWatchBrand(brand.id, {
          excludedProductTypes: excluded.length > 0 ? excluded : [],
          lastImportedAt,
        });
        // Update local ref so the next import within this session remembers the exclusions
        savedExcludedTypesRef.current = excluded;
        // Notify parent of brand update so UI can refresh
        onBrandUpdated?.({
          ...brand,
          excludedProductTypes: excluded.length > 0 ? excluded : [],
          lastImportedAt,
        });
      } catch {
        // non-fatal — import succeeded even if metadata save fails
      }
    }

    // One analytics event per written reference whose calibre resolved within a
    // revision line: the FINAL choice (resolver or curator), not every click.
    void logAdminEvents(
      [...revisionPicks].flatMap(([idx, pick]) => {
        const refId = writtenRefIds.current.get(idx);
        if (!refId) return [];
        return [
          {
            name: 'calibre_revision_resolver_choice' as const,
            params: {
              refId,
              brandId: brand.id,
              method: pick.resolution.method,
              chosenRevisionId: pick.resolution.member.id,
              confidence: pick.resolution.confidence,
            },
          },
        ];
      }),
    );

    setImportPhase('');
    setGalleryStats({ ...galleryStatsRef.current });
    setImageFailureRows(imageFailuresRef.current.rows());
    setImporting(false);
    setStep('done');
  };

  // Item-level creates/updates (each Shopify variant / standalone product is one
  // item). Split by whether the item is a variant (linked to a parent this run).
  const variantsAdded = items.filter((i, idx) => i.status === 'success' && variantItemIndices.has(idx)).length;
  const variantsUpdated = items.filter((i, idx) => i.status === 'updated' && variantItemIndices.has(idx)).length;
  const variantsDivergedItems = items.filter((i, idx) => i.status === 'diverged' && variantItemIndices.has(idx)).length;
  const standaloneAdded = items.filter((i, idx) => i.status === 'success' && !variantItemIndices.has(idx)).length;
  const standaloneUpdated = items.filter((i, idx) => i.status === 'updated' && !variantItemIndices.has(idx)).length;
  const standaloneDiverged = items.filter((i, idx) => i.status === 'diverged' && !variantItemIndices.has(idx)).length;

  // Parents = synthetic parent refs (not in `items`) + standalone items
  // (parentReferenceId absent). Headline totals include both so the breakdown
  // partitions the total exactly.
  const parentsAdded = syntheticParentStats.added + standaloneAdded;
  const parentsUpdated = syntheticParentStats.updated + standaloneUpdated;
  const parentsDiverged = syntheticParentStats.diverged + standaloneDiverged;
  const variantsDiverged = variantsDivergedItems;

  // The curator-facing split: a parent HAS variants (a store product's
  // synthetic parent, or a rule group's parent row); a single has none.
  // `parentsAdded` above keeps its older meaning (every top-level ref) for
  // BulkImportStats / the import history.
  const ruleParentsWith = (status: ItemStatus) =>
    items.filter((i, idx) => i.status === status && ruleParentItemIndices.has(idx)).length;
  const breakdown = {
    added: {
      parents: syntheticParentStats.added + ruleParentsWith('success'),
      variants: variantsAdded,
      singles: standaloneAdded - ruleParentsWith('success'),
    },
    updated: {
      parents: syntheticParentStats.updated + ruleParentsWith('updated'),
      variants: variantsUpdated,
      singles: standaloneUpdated - ruleParentsWith('updated'),
    },
    kept: {
      parents: syntheticParentStats.diverged + ruleParentsWith('diverged'),
      variants: variantsDiverged,
      singles: standaloneDiverged - ruleParentsWith('diverged'),
    },
  };

  const addedCount = parentsAdded + variantsAdded;
  const updatedCount = parentsUpdated + variantsUpdated;
  // Diverged: existed, source disagrees with stored, nothing written (curated
  // values preserved). Purely informational — sits alongside, not inside, the
  // added/updated/unchanged partition.
  const divergedCount = parentsDiverged + variantsDiverged;
  const duplicateCount = items.filter((i) => i.status === 'duplicate').length;
  const excludedCount = items.filter((i) => i.status === 'excluded').length;
  const errorCount = items.filter((i) => i.status === 'error').length;
  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const imageFailureSummary = summarizeImageFailures(imageFailureRows);

  /**
   * The done step's "Retry failed images": re-attempt every image the run lost,
   * a few references at a time, and shrink the list as they land. What still
   * fails stays listed with its fresh reason.
   */
  const handleRetryFailedImages = async () => {
    const rows = imageFailureRows;
    if (rows.length === 0) return;
    setRetryingImages(true);
    const before = summarizeImageFailures(rows).images;
    let after = before;
    let next = 0;
    const worker = async () => {
      while (next < rows.length) {
        const row = rows[next++];
        const remaining = await retryImportImageFailureRow(row);
        after -= row.failures.length - remaining.length;
        setImageFailureRows((prev) => applyRetryOutcome(prev, row.brandId, row.referenceId, remaining));
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(RETRY_IMAGE_CONCURRENCY, rows.length) }, worker));
    } finally {
      setRetryingImages(false);
    }
    const fixed = before - after;
    if (after === 0) toast.success(`All ${fixed} image${fixed === 1 ? '' : 's'} imported`);
    else toast.error(`${fixed} of ${before} images imported — ${after} still failing`);
  };


  // ── Modal content by step ────────────────────────────────────────────────

  // Landing panel for a run launched from Import Sources. The wizard waits here
  // instead of firing straight into the network, so the user can choose how to
  // start — and set skip-cache / manual-paste BEFORE anything is fetched.
  const renderLaunchPanel = () => {
    // Counts are net of exclusions, so the button never offers to fetch URLs the
    // run is about to skip.
    const allDirectUrls = dedupeProductUrls(autoStartUrls?.directUrls ?? []);
    const directUrls = withoutExcludedUrls(allDirectUrls, autoStartUrls?.excludedUrls);
    const collectionPages = withoutExcludedUrls(
      autoStartUrls?.collectionPageUrls ?? [],
      autoStartUrls?.excludedUrls,
    );
    const excludedCount =
      allDirectUrls.length -
      directUrls.length +
      (autoStartUrls?.collectionPageUrls ?? []).length -
      collectionPages.length;
    const jsonListingUrl = autoStartUrls?.jsonListingUrl?.trim();
    const carriedCount = directUrls.filter((u) =>
      prefetchedByUrl.has(canonicalProductUrlKey(u)),
    ).length;
    const isShopify = sourceKind === 'shopify';

    return (
      <div className="rounded-md border border-brand-200 bg-brand-50 p-4 space-y-3">
        <div>
          <p className="text-sm font-medium text-brand-900">
            Ready to import — {isShopify ? 'Shopify source' : 'Generic URL source'}
          </p>
          <p className="text-xs text-brand-800 mt-0.5">
            {isShopify ? (
              <>Fetches the product list from <span className="font-mono">{brand.shopifyUrl}</span>.</>
            ) : (
              [
                jsonListingUrl ? 'JSON listing' : null,
                collectionPages.length > 0
                  ? `${collectionPages.length} collection page${collectionPages.length === 1 ? '' : 's'} to crawl`
                  : null,
                directUrls.length > 0
                  ? `${directUrls.length} direct product URL${directUrls.length === 1 ? '' : 's'}`
                  : null,
                excludedCount > 0 ? `${excludedCount} excluded` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'Nothing configured on this source.'
            )}
          </p>
        </div>

        {carriedCount > 0 && (
          <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1.5">
            {usePrefetched ? (
              <>
                {carriedCount} of {directUrls.length} URL{directUrls.length === 1 ? '' : 's'} already
                extracted in your test — those will be reused, not re-fetched
                {' '}(includes anything you pasted by hand).
              </>
            ) : (
              <>
                {carriedCount} test result{carriedCount === 1 ? '' : 's'} available, but “Skip cache”
                is on so every URL will be re-fetched. Untick it to reuse them.
              </>
            )}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {isShopify ? (
            <Button onClick={handleLaunchShopify} disabled={shopifyLoading}>
              Fetch Shopify products
            </Button>
          ) : (
            <>
              <Button onClick={handleLaunchFetch}>
                {collectionPages.length > 0 || jsonListingUrl
                  ? 'Crawl & fetch from URLs'
                  : `Fetch ${directUrls.length} URL${directUrls.length === 1 ? '' : 's'}`}
              </Button>
              {/* Only offered when there are direct product URLs to paste. A
                  collection page has nothing to paste against — it needs the
                  crawl first — so the button is hidden rather than shown dead. */}
              {directUrls.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={handleLaunchPaste}
                  title="Skip automated fetching and paste each page’s HTML by hand."
                >
                  Paste HTML instead
                </Button>
              )}
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setAwaitingLaunch(false)}>
            Other options
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-1 border-t border-brand-200">
          <label className="flex items-center gap-1.5 text-xs text-brand-900 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={skipExtractionCache}
              onChange={(e) => setSkipExtractionCache(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600"
            />
            Skip cache
          </label>
          {!isShopify && (
            <label
              className="flex items-center gap-1.5 text-xs text-brand-900 cursor-pointer pt-2"
              title="Send every URL straight to the manual paste queue instead of trying to fetch it."
            >
              <input
                type="checkbox"
                checked={forceManualPaste}
                onChange={(e) => setForceManualPaste(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600"
              />
              Force manual HTML paste
            </label>
          )}
        </div>
      </div>
    );
  };

  const renderUpload = () => {
    // Launched from Import Sources? Then the source's configured kind decides
    // which manual blocks are even applicable. Opened standalone from the
    // reference browser (no sourceKind) every block stays available, since
    // there's no configured source to scope them to.
    const scopedToSource = sourceKind !== undefined;
    const isShopifySource = sourceKind === 'shopify';
    // 'json-feed' is the extractor-output source: its whole input is the file
    // upload below, so neither URL-driven block applies. ('manual' still runs
    // the generic pipeline, so it groups with it.)
    const isJsonFeedSource = sourceKind === 'json-feed';
    const showShopifyBlock = !scopedToSource || isShopifySource;
    const showCollectionPageBlock = !scopedToSource || (!isShopifySource && !isJsonFeedSource);
    // The legacy "Saved Product URLs" block reads brand.productUrls and ends up
    // in handleImportFromSavedUrls — exactly where the launch panel's fetch
    // lands via runAutoStart. For a source-launched run the source config is
    // authoritative, so this is pure duplicate real estate.
    const showSavedUrlsBlock =
      !scopedToSource &&
      brand.importSource === 'generic' &&
      (brand.productUrls?.length ?? 0) > 0;

    // While the launch panel is up it IS the screen. Rendering the manual
    // fallbacks underneath it put two live import buttons on screen at once and
    // showed blocks belonging to the other source type.
    if (awaitingLaunch) {
      return <div className="space-y-4">{renderLaunchPanel()}</div>;
    }

    return (
    <div className="space-y-4">
      {(autoCrawlLoading || genericUrlsLoading) && (
        <div className="rounded-md border border-brand-200 bg-brand-50 p-3 text-sm text-brand-800 flex items-center justify-between gap-3">
          <span>
            {autoCrawlLoading
              ? `Crawling collection pages ${autoCrawlProgress}/${autoCrawlTotal}…`
              : `Extracting products ${genericUrlsProgress}/${genericUrlsTotal}…${
                  genericUrlsNote ? ` ${genericUrlsNote}` : ''
                }`}
          </span>
          <Button variant="ghost" size="sm" onClick={handleStopExtraction}>
            Stop
          </Button>
        </div>
      )}
      {stoppedUrls.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-sm font-medium text-amber-800">
            Stopped — {stoppedUrls.length} URL{stoppedUrls.length === 1 ? '' : 's'} not attempted
            {savedUrlEntries.length > 0
              ? ` (${savedUrlEntries.length} already extracted)`
              : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={pasteStoppedUrls}>
              Paste HTML for remaining {stoppedUrls.length}
            </Button>
            <Button variant="secondary" size="sm" onClick={resumeStoppedUrls}>
              Resume fetching
            </Button>
            {savedUrlEntries.length > 0 && (
              <Button variant="ghost" size="sm" onClick={continueFromStop}>
                Continue with {savedUrlEntries.length} extracted →
              </Button>
            )}
          </div>
        </div>
      )}
      {autoCrawlError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {autoCrawlError}
        </div>
      )}
      {savedUrlFailures.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-red-700">
              {savedUrlFailures.length} URL{savedUrlFailures.length === 1 ? '' : 's'} failed to extract
            </p>
            {savedUrlEntries.length > 0 && (
              <Button size="sm" onClick={continueFromSavedUrlFailures}>
                Continue with {savedUrlEntries.length} extracted →
              </Button>
            )}
          </div>
          <ul className="space-y-1">
            {savedUrlFailures.map((f) => (
              <li
                key={f.url}
                className="flex items-center gap-2 rounded border border-red-100 bg-white px-2 py-1.5 text-xs"
              >
                <span className="flex-1 truncate font-mono text-gray-700" title={f.url}>
                  {f.url}
                </span>
                {f.retrying ? (
                  <span className="text-blue-600 truncate max-w-[45%]" title={f.note}>
                    {f.note || 'Retrying…'}
                  </span>
                ) : (
                  <>
                    {f.note && (
                      <span className="text-red-600 truncate max-w-[35%]" title={f.note}>
                        {f.note}
                      </span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => retrySavedUrl(f.url)}>
                      Retry
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSavedUrlPasteUrl(f.url)}>
                      Enter HTML
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isJsonFeedSource ? (
        <p className="text-sm text-gray-600">
          Upload the extractor&apos;s result JSON — the{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">ExtractionResult</code>{' '}
          file the run wrote to Cloud Storage. It must have been extracted for{' '}
          <span className="font-medium">{brand.name}</span>; a file from another
          brand is rejected. A plain array of scraped entries still works too.
        </p>
      ) : (
        <p className="text-sm text-gray-600">
          Upload a JSON file containing scraped watch data. Each entry must have a{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">reference</code> and{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">model</code> field.
          An extractor{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">ExtractionResult</code>{' '}
          file is also accepted and unwrapped automatically.
        </p>
      )}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragOver ? 'border-brand-500 bg-brand-50' : 'border-gray-300 hover:border-brand-400'}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <svg
          className="mx-auto h-12 w-12 text-gray-400 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-sm text-gray-600">Click to select a JSON file</p>
        <p className="text-xs text-gray-400 mt-1">or drag and drop</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      <ImageDownloadPanel brand={brand} downloadEnabled={extractorJobsEnabled} onReady={loadParsedJson} />

      {/* Divider — only earns its place if a direct-import block follows it. */}
      {(showShopifyBlock || showCollectionPageBlock) && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center"><span className="bg-white px-3 text-sm text-gray-400">or import directly from</span></div>
        </div>
      )}

      {/* Shopify URL import */}
      {showShopifyBlock && (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-600 font-medium">Shopify Store URL</p>
          {brand.mainWebsite && !shopifyUrl && (
            <button
              onClick={() => setShopifyUrl(brand.mainWebsite!.replace(/\/+$/, ''))}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-brand-100 text-brand-700 hover:bg-brand-200 transition-colors"
            >
              Use {new URL(brand.mainWebsite.startsWith('http') ? brand.mainWebsite : `https://${brand.mainWebsite}`).hostname}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="url"
            value={shopifyUrl}
            onChange={(e) => setShopifyUrl(e.target.value)}
            placeholder="https://somebrand.com"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-brand-500"
            onKeyDown={(e) => e.key === 'Enter' && handleShopifyFetch()}
          />
          <Button onClick={handleShopifyFetch} disabled={shopifyLoading || !shopifyUrl.trim()}>
            {shopifyLoading ? 'Fetching...' : 'Fetch Products'}
          </Button>
        </div>
        {shopifyError && (
          <p className="text-sm text-red-600">{shopifyError}</p>
        )}
        <p className="text-xs text-gray-400">
          Paste a Shopify store URL or a direct .json URL (e.g. a collection page ending in .json).
        </p>
      </div>
      )}

      {/* Collection page URL fallback — extract individual product URLs from a listing page */}
      {showCollectionPageBlock && (
      <div
        className={
          showShopifyBlock
            ? 'space-y-2 pt-3 border-t border-gray-100'
            : /* First block under the divider — its own rule would double up. */ 'space-y-2'
        }
      >
        <p className="text-sm text-gray-600 font-medium">Collection page URL</p>
        <p className="text-xs text-gray-400">
          Paste a brand collection/listing page (e.g. Tag Heuer). We'll extract individual product URLs
          via DOM links, JSON-LD images, or meta tags — then run the per-URL extractor on each.
        </p>
        <div className="flex gap-2">
          <input
            type="url"
            value={collectionPageUrl}
            onChange={(e) => setCollectionPageUrl(e.target.value)}
            placeholder="https://www.tagheuer.com/.../carrera"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-brand-500"
            onKeyDown={(e) => e.key === 'Enter' && handleExtractFromCollectionPage()}
          />
          <Button
            onClick={handleExtractFromCollectionPage}
            disabled={collectionPageLoading || !collectionPageUrl.trim()}
            loading={collectionPageLoading}
          >
            Extract Product URLs
          </Button>
        </div>
        {collectionPageError && (
          <p className="text-sm text-red-600">{collectionPageError}</p>
        )}
        {collectionPageUrls.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              Found <span className="font-medium">{collectionPageUrls.length}</span> URL{collectionPageUrls.length === 1 ? '' : 's'}
              {' '}via <span className="font-mono">{collectionPageMethod}</span>
            </p>
            <div className="max-h-40 overflow-y-auto border rounded bg-gray-50 divide-y divide-gray-100 text-xs">
              {collectionPageUrls.map((u) => (
                <div key={u} className="px-2 py-1 font-mono truncate text-gray-700">{u}</div>
              ))}
            </div>
            <Button
              onClick={handleImportExtractedCollectionUrls}
              disabled={genericUrlsLoading}
              loading={genericUrlsLoading}
            >
              {genericUrlsLoading
                ? `Extracting ${genericUrlsProgress}/${genericUrlsTotal}…`
                : `Import ${collectionPageUrls.length} extracted URL${collectionPageUrls.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}
      </div>
      )}

      {showSavedUrlsBlock && (
        <div className="space-y-2 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-600 font-medium">
            Saved Product URLs ({brand.productUrls?.length ?? 0})
          </p>
          <p className="text-xs text-gray-400">
            Imports each saved URL via the generic product extractor. Configure URLs in the brand edit modal.
          </p>
          <div className='flex items-center gap-3'>
            <Button
              onClick={() => handleImportFromSavedUrls()}
              disabled={genericUrlsLoading}
              loading={genericUrlsLoading}
            >
              {genericUrlsLoading
                ? `Extracting ${genericUrlsProgress}/${genericUrlsTotal}…`
                : `Import from saved URLs (${brand.productUrls?.length ?? 0})`}
            </Button>
            <label className='flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer'>
              <input
                type='checkbox'
                checked={skipExtractionCache}
                onChange={(e) => setSkipExtractionCache(e.target.checked)}
                className='h-3.5 w-3.5 rounded border-gray-300 text-brand-600'
              />
              Skip cache
            </label>
            <label
              className='flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer'
              title='Skip auto-extraction and paste each page&apos;s HTML by hand — useful for image-heavy pages that return 0 products.'
            >
              <input
                type='checkbox'
                checked={forceManualPaste}
                onChange={(e) => setForceManualPaste(e.target.checked)}
                className='h-3.5 w-3.5 rounded border-gray-300 text-brand-600'
              />
              Force manual HTML paste
            </label>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Product-type step: what the title rule excluded (so the curator can see why
  // the count is short and tick watches back in), and what will actually go on.
  const titleExcluded = useMemo(
    () => titleExcludedProducts(shopifyProducts, selectedTypes),
    [shopifyProducts, selectedTypes],
  );
  const titleOverrideCount = titleExcluded.filter(({ product }) => titleIncludeOverrides.has(product.id)).length;
  const shopifyIncludedCount = useMemo(
    () => filterProducts(shopifyProducts, selectedTypes, titleIncludeOverrides).length,
    [shopifyProducts, selectedTypes, titleIncludeOverrides],
  );

  // ── Store (WooCommerce / Square Online) shop-URL category step ─────────────
  // Mirrors renderShopifyTypes: same rows, badges, title-exclusion list, footer.
  const storeCatOptions = useMemo(
    () => storeCategoryOptions(storeCatEntries, storeCategoryPicker?.hierarchy, storeTitleOverrides),
    [storeCatEntries, storeCategoryPicker?.hierarchy, storeTitleOverrides],
  );
  const storeTitleExcluded = useMemo(
    () => titleExcludedStoreProducts(storeCatEntries, selectedStoreCats),
    [storeCatEntries, selectedStoreCats],
  );
  const storeIncluded = useMemo(
    () => filterStoreEntries(storeCatEntries, selectedStoreCats, storeTitleOverrides),
    [storeCatEntries, selectedStoreCats, storeTitleOverrides],
  );
  const storeIncludedProducts = useMemo(
    () => new Set(storeIncluded.map(storeProductKey)).size,
    [storeIncluded],
  );
  const storeTitleOverrideCount = storeTitleExcluded.filter(({ key }) => storeTitleOverrides.has(key)).length;

  const handleStoreCategoriesProceed = () => {
    if (storeIncluded.length === 0) {
      toast.error('No products in the selected categories');
      return;
    }
    // Remembered for the source once the import runs (see the import tail).
    storeCatExcludedRef.current = storeCatOptions.filter((o) => !selectedStoreCats.has(o.key)).map((o) => o.key);
    loadParsedJson(storeIncluded);
  };

  const renderStoreCategories = () => (
    <div className="space-y-4" data-testid="store-category-step">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Found <span className="font-medium">{new Set(storeCatEntries.map(storeProductKey)).size}</span> products.
          Select which categories to import (a product in several goes on if any of them is ticked):
        </p>
      </div>

      <div className="border rounded-lg divide-y">
        {storeCatOptions.map(({ key, label, count, likelyWatch }) => (
          <div
            key={key}
            className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${
              selectedStoreCats.has(key) ? '' : 'opacity-50'
            }`}
          >
            <label className="flex items-center gap-3 flex-1 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedStoreCats.has(key)}
                onChange={() =>
                  setSelectedStoreCats((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
                className="rounded border-gray-300"
                aria-label={label}
              />
              <span className="flex-1 text-sm font-medium text-gray-800">{label}</span>
              <span className="text-xs text-gray-400">{count} product{count !== 1 ? 's' : ''}</span>
              {likelyWatch && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Watch</span>
              )}
            </label>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setViewingStoreCat(key); }}
              className="text-xs text-brand-600 hover:text-brand-800 font-medium px-2 py-1 rounded hover:bg-brand-50"
              title={`View the products in "${label}"`}
            >
              View
            </button>
          </div>
        ))}
      </div>

      {storeTitleExcluded.length > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs" open={storeIncluded.length === 0}>
          <summary className="cursor-pointer font-medium text-amber-900">
            {storeTitleExcluded.length} product{storeTitleExcluded.length !== 1 ? 's' : ''} excluded by title
            {storeTitleOverrideCount > 0 ? ` — ${storeTitleOverrideCount} included anyway` : ''}
          </summary>
          <p className="mt-1 text-amber-800">
            These have an accessory word in the title (strap, bracelet, tool…) and nothing that marks them as a
            watch. Tick any that are watches to include them.
          </p>
          <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
            {storeTitleExcluded.map(({ key, title, keyword }) => (
              <li key={key}>
                <label className="flex items-start gap-2 text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded text-brand-600 focus:ring-brand-500"
                    checked={storeTitleOverrides.has(key)}
                    onChange={(e) =>
                      setStoreTitleOverrides((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(key);
                        else next.delete(key);
                        return next;
                      })
                    }
                  />
                  <span>
                    {title} <span className="text-gray-500">— matched “{keyword}”</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-400">
          {selectedStoreCats.size} categor{selectedStoreCats.size !== 1 ? 'ies' : 'y'} selected •{' '}
          {storeIncludedProducts} products •{' '}
          {storeTitleExcluded.length === 0
            ? 'Title-based exclusions (straps, tools, etc.) applied automatically'
            : `${storeTitleExcluded.length - storeTitleOverrideCount} excluded by title (see above)`}
        </p>
        <Button onClick={handleStoreCategoriesProceed} disabled={selectedStoreCats.size === 0}>
          Extract &amp; Preview →
        </Button>
      </div>

      {viewingStoreCat !== null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setViewingStoreCat(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium">
                {storeCatOptions.find((o) => o.key === viewingStoreCat)?.label ?? viewingStoreCat}
              </h3>
              <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setViewingStoreCat(null)}>
                ✕
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs">
              {JSON.stringify(
                storeCatEntries.filter((e) =>
                  viewingStoreCat === '__none__'
                    ? !(e.storeCategories?.length)
                    : e.storeCategories?.some((c) => c.id === viewingStoreCat),
                ),
                null,
                2,
              )}
            </pre>
          </div>
        </div>
      )}
    </div>
  );

  const renderShopifyTypes = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Found <span className="font-medium">{shopifyProducts.length}</span> products.
          Select which product types to import:
        </p>
        <Button variant="secondary" size="sm" onClick={() => setStep('upload')}>
          Back
        </Button>
      </div>

      <div className="border rounded-lg divide-y">
        {shopifyTypes.map(({ type, count }) => (
          <div
            key={type}
            className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${
              selectedTypes.has(type) ? '' : 'opacity-50'
            }`}
          >
            <label className="flex items-center gap-3 flex-1 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTypes.has(type)}
                onChange={() => toggleType(type)}
                className="rounded border-gray-300"
              />
              <span className="flex-1 text-sm font-medium text-gray-800">
                {type === '(none)' ? '(none) — no product type' : type}
              </span>
              <span className="text-xs text-gray-400">{count} product{count !== 1 ? 's' : ''}</span>
              {isLikelyWatch(type) && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Watch</span>
              )}
            </label>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setViewingType(type); }}
              className="text-xs text-brand-600 hover:text-brand-800 font-medium px-2 py-1 rounded hover:bg-brand-50"
              title={`View raw JSON for "${type}"`}
            >
              View
            </button>
          </div>
        ))}
      </div>

      {shopifyError && <p className="text-sm text-red-600">{shopifyError}</p>}

      {titleExcluded.length > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs" open={shopifyIncludedCount === 0}>
          <summary className="cursor-pointer font-medium text-amber-900">
            {titleExcluded.length} product{titleExcluded.length !== 1 ? 's' : ''} excluded by title
            {titleOverrideCount > 0 ? ` — ${titleOverrideCount} included anyway` : ''}
          </summary>
          <p className="mt-1 text-amber-800">
            These have an accessory word in the title (strap, bracelet, tool…) and nothing that marks them as a
            watch. Tick any that are watches to include them.
          </p>
          <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
            {titleExcluded.map(({ product, keyword }) => (
              <li key={product.id}>
                <label className="flex items-start gap-2 text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded text-brand-600 focus:ring-brand-500"
                    checked={titleIncludeOverrides.has(product.id)}
                    onChange={(e) =>
                      setTitleIncludeOverrides((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(product.id);
                        else next.delete(product.id);
                        return next;
                      })
                    }
                  />
                  <span>
                    {product.title} <span className="text-gray-500">— matched “{keyword}”</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-400">
          {selectedTypes.size} type{selectedTypes.size !== 1 ? 's' : ''} selected •{' '}
          {shopifyIncludedCount} products •{' '}
          {titleExcluded.length === 0
            ? 'Title-based exclusions (straps, tools, etc.) applied automatically'
            : `${titleExcluded.length - titleOverrideCount} excluded by title (see above)`}
        </p>
        <Button
          onClick={handleShopifyProceed}
          disabled={selectedTypes.size === 0 || proceedLoading}
        >
          {proceedLoading ? 'Parsing variants…' : 'Extract & Preview →'}
        </Button>
      </div>

      {viewingType !== null && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setViewingType(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium">
                {viewingType} —{' '}
                {shopifyProducts.filter((p) => (p.product_type || '(none)') === viewingType).length} product
                {shopifyProducts.filter((p) => (p.product_type || '(none)') === viewingType).length === 1 ? '' : 's'}
              </h3>
              <button
                onClick={() => setViewingType(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-gray-900 text-green-400">
              {JSON.stringify(
                shopifyProducts.filter((p) => (p.product_type || '(none)') === viewingType),
                null,
                2,
              )}
            </pre>
          </div>
        </div>
      )}
    </div>
  );

  // Per-item detail panel shown when a preview row is expanded: large image,
  // source URL, extraction warnings, and inline-editable fields.
  const renderItemDetail = (item: ImportItem, index: number) => {
    const e = item.entry;
    const warnings = entryWarnings(e);
    return (
      <div className="flex gap-4">
        <div className="shrink-0">
          <ImageCell
            src={e.imgSrc}
            alt={e.reference}
            boxClassName="w-28 h-28 rounded"
            imgClassName="object-contain border border-gray-200 bg-white cursor-pointer"
            placeholderClassName="border border-dashed border-gray-300"
            labelClassName="text-xs"
            onClick={() => setPreviewImage(e.imgSrc!)}
            title="Click to enlarge"
            onFailed={markImageFailed}
          />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            {e.url ? (
              <a
                href={e.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-600 hover:underline break-all"
                title={e.url}
              >
                {e.url}
              </a>
            ) : (
              <span className="text-xs text-gray-400">No source URL</span>
            )}
            <span className="text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
              Click a value to edit
            </span>
          </div>
          {warnings.length > 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              ⚠ {warnings.join(' · ')}
            </div>
          )}
          <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
            {PREVIEW_DETAIL_FIELDS.map(({ label, key }) => (
              <div key={key} className="flex flex-col min-w-0">
                <dt className="text-[10px] uppercase tracking-wide text-gray-400">{label}</dt>
                <dd>
                  <input
                    type="text"
                    // The Calibre row shows the name the import will write (a
                    // matched row's canonical name lives in `calibre`).
                    value={
                      ((key === 'movement' ? e.calibre ?? e.movement : e[key]) as string | undefined) ?? ''
                    }
                    onChange={(ev) => updateEntryField(index, key, ev.target.value)}
                    placeholder="—"
                    className="w-full text-sm text-gray-800 border-b border-transparent hover:border-gray-300 focus:border-brand-500 focus:outline-none bg-transparent py-0.5"
                  />
                </dd>
              </div>
            ))}
          </dl>
          {e.description && (
            <details className="text-xs text-gray-600">
              <summary className="cursor-pointer text-gray-400">Description</summary>
              <p className="mt-1 whitespace-pre-wrap">{e.description}</p>
            </details>
          )}
        </div>
      </div>
    );
  };

  // ---- Preview paging ---------------------------------------------------------

  // Grouped display order: every group's rows sit together under its header —
  // a store product's options in row order, a rule group's chosen parent first
  // and then its variants — each at the position of the group's first row.
  // Singles keep their place. Paging runs over this order; each row still
  // carries its ABSOLUTE index in `items` (previewPaging.ts explains why every
  // handler depends on that).
  const ruleGroupByKey = new Map(previewStructure.ruleGroups.map((g) => [`rule:${g.key}`, g]));
  const {
    order: previewOrder,
    keyByIndex: groupKeyByIndex,
    membersByKey: groupMembersByKey,
  } = groupedDisplayOrder(items.length, [
    // Every option of a store product, ticked or not: unticking one must not
    // pull it out from under its product header.
    ...[...items.reduce((m, it, idx) => {
      const key = variantGroupOf(it.entry)?.key;
      if (key) m.set(key, [...(m.get(key) ?? []), idx]);
      return m;
    }, new Map<string, number[]>())].map(([key, members]) => ({ key: `store:${key}`, members })),
    ...previewStructure.ruleGroups.map((g) => ({ key: `rule:${g.key}`, members: [g.parentIndex, ...g.variantIndices] })),
  ]);
  const {
    rows: previewPageOrder,
    currentPage: previewCurrentPage,
    totalPages: previewTotalPages,
    start: previewStart,
  } = pagePreviewItems(previewOrder, previewPage, previewPageSize);
  const previewPageItems = previewPageOrder.map(({ item: index }) => ({ item: items[index], index }));

  // Store variants (Shopify, WooCommerce, Square Online): rows of one product.
  const storeGroupKeyAt = (idx: number): string | undefined => {
    const key = groupKeyByIndex.get(idx);
    return key?.startsWith('store:') ? key : undefined;
  };
  const storeVariantGroups = groupMembersByKey;
  const formatPrice = (p: unknown): string =>
    typeof p === 'number' ? p.toLocaleString(undefined, { maximumFractionDigits: 2 }) : typeof p === 'string' ? p : '';

  const allPageIncluded =
    previewPageItems.length > 0 && previewPageItems.every(({ item }) => item.included);
  const somePageIncluded =
    previewPageItems.some(({ item }) => item.included) && !allPageIncluded;

  /** Include/exclude just this page — the footer's scope, next to the pager. */
  const togglePageIncluded = () => {
    const next = !allPageIncluded;
    const onPage = new Set(previewPageItems.map(({ index }) => index));
    setItems((prev) => prev.map((it, idx) => (onPage.has(idx) ? { ...it, included: next } : it)));
  };

  const imageMissingCount = items.filter((i) => !i.entry.imgSrc).length;
  const imageFailedCount = items.filter(
    (i) => i.entry.imgSrc && failedImageUrls.has(i.entry.imgSrc),
  ).length;
  const probeFailureOf = (entry: ScrapedWatchEntry) => {
    const result = entry.imgSrc ? imageProbe.get(entry.imgSrc.trim()) : undefined;
    return result && !result.ok ? result : undefined;
  };
  const probeFailedIncludedCount = items.filter((i) => i.included && probeFailureOf(i.entry)).length;
  const unprobedCount = step === 'preview' ? unprobedImageUrls().length : 0;

  // Structure summary over what is ticked now. Create vs update is known only for
  // rows with a store doc id (and their synthetic parents); file / URL rows are
  // matched by reference string at import time.
  const previewSplit = previewExistingDocIds
    ? splitCreateUpdate(items, previewStructure, storeDocIdForEntry, previewExistingDocIds)
    : null;
  const undecidedRuleGroups = ruleGroups.filter((g) => (ruleDecisions.get(g.key)?.status ?? 'proposed') === 'proposed').length;
  const showNoNativeGroupingHint = lacksNativeGrouping(items) && !ruleIsActive(variantGroupingRule);

  const renderPreviewStructure = () => {
    const c = previewStructure.counts;
    if (c.parents + c.variants + c.singles === 0) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-1.5" data-testid="preview-structure">
        <p className="text-sm text-gray-800">
          <span className="font-medium">Will import:</span> {describeCounts(c)}
          {c.parents > 0 && (c.storeParents > 0 && c.ruleParents > 0) && (
            <span className="text-gray-500">
              {' '}({c.storeParents} parent{c.storeParents === 1 ? '' : 's'} from the store&apos;s own products, {c.ruleParents} from your grouping rule)
            </span>
          )}
        </p>
        {previewSplit && (
          <p className="text-xs text-gray-600">
            {previewSplit.wouldCreate} new · {previewSplit.wouldUpdate} already in Watch Keeper (will be checked for updates)
            {previewSplit.unmatched > 0 && ` · ${previewSplit.unmatched} matched by reference when the import runs`}
          </p>
        )}
        {undecidedRuleGroups > 0 && (
          <p className="text-xs text-purple-700">
            {undecidedRuleGroups} group{undecidedRuleGroups === 1 ? '' : 's'} proposed by your grouping rule {undecidedRuleGroups === 1 ? 'is' : 'are'} not accepted yet — until you accept {undecidedRuleGroups === 1 ? 'it' : 'them'}, {undecidedRuleGroups === 1 ? 'its' : 'their'} rows import as singles.
          </p>
        )}
        {showNoNativeGroupingHint && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="no-native-grouping-hint">
            {ignoreShopifyParentChildRef.current
              ? 'Parent-child grouping is switched off for this source ("Ignore Shopify parent-child relationships"), so every row imports on its own. Turn it back on, or set a grouping rule on the source, to group versions of the same watch.'
              : NO_NATIVE_GROUPING_HINT}
          </p>
        )}
      </div>
    );
  };

  const renderPreview = () => (
    <div className="space-y-4">
      {renderPreviewStructure()}
      {variantGroupingRule && ruleGroups.length > 0 && (
        <VariantRuleGroupsPanel
          rule={variantGroupingRule}
          groups={ruleGroups}
          decisions={ruleDecisions}
          onDecide={decideRuleGroup}
          onDecideAll={decideAllRuleGroups}
          refOf={(i) => items[i]?.entry.reference ?? ''}
          labelOf={(i) => items[i]?.entry.productName || items[i]?.entry.model || ''}
        />
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          <span className="font-medium">{items.filter((i) => i.included).length}</span> of{' '}
          <span className="font-medium">{items.length}</span> references selected to import into{' '}
          <span className="font-medium">{brand.displayName || brand.name}</span>.
          {imageFailedCount > 0 && (
            <span className="text-amber-600">
              {' · '}
              <span className="font-medium">{imageFailedCount}</span> image
              {imageFailedCount === 1 ? '' : 's'} failed
            </span>
          )}
          {imageMissingCount > 0 && (
            <span className="text-gray-500">
              {' · '}
              <span className="font-medium">{imageMissingCount}</span> not extracted
            </span>
          )}
          {probeFailedIncludedCount > 0 && (
            <span className="text-red-600" title="The import's own fetch of these images failed, so these references would be committed WITHOUT an image.">
              {' · '}
              <span className="font-medium">{probeFailedIncludedCount}</span> selected will commit with no image
            </span>
          )}
          {probingImages && <span className="text-gray-500">{' · '}checking image fetches…</span>}
        </p>
        <div className="flex gap-2">
          {probeFailedIncludedCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setItems((prev) =>
                  prev.map((it) => (it.included && probeFailureOf(it.entry) ? { ...it, included: false } : it)),
                )
              }
              title="Leave out every selected row whose image fetch failed"
            >
              Untick {probeFailedIncludedCount} without image
            </Button>
          )}
          {unprobedCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void runImageProbe(unprobedImageUrls())}
              loading={probingImages}
              disabled={probingImages}
              title="Ask the server to fetch each selected row's image the way the import will — nothing is stored"
            >
              Check image fetches ({unprobedCount})
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleOpenPreviewEnrich()}
            loading={openingPreviewEnrich}
            disabled={items.filter((i) => i.included).length === 0 || openingPreviewEnrich}
            title={`Review AI proposals for the ${items.filter((i) => i.included).length} selected items before anything is imported. Variants are asked only for their variant-level fields, with their parent as context.`}
          >
            ✨ Enrich with AI ({items.filter((i) => i.included).length})
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setStep('field-rules')}>
            Back to Rules
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setStep('upload')}>
            Change file
          </Button>
        </div>
      </div>

      {refDiffLoading && (
        <p className="text-xs text-gray-500">Checking existing references for changes…</p>
      )}

      {refDiffs.size > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {refDiffs.size} reference{refDiffs.size === 1 ? '' : 's'} changed since the
                last import
                {' — '}
                <span className={refRewriteAcceptedCount === 0 ? 'text-amber-700' : undefined}>
                  {refRewriteAcceptedCount} of {refDiffs.size} accepted
                </span>
              </p>
              <p className="text-sm text-amber-800 mt-1">
                These products already exist and their derived reference no longer matches
                what&apos;s stored — usually because a stopword was stripped out of the SKU
                or handle. Shopify docs are keyed on the product + variant id, so the ref
                can safely be rewritten in place. Ticked rows get the new value; untick a
                row to keep the stored one.
              </p>
            </div>
            <div className="shrink-0 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setRefRewriteOptOut(new Set())}
                className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={() => setRefRewriteOptOut(new Set(refDiffs.keys()))}
                className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              >
                Keep all existing
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide w-12">
                  <label
                    className="flex items-center justify-center gap-1 cursor-pointer"
                    title="Toggle every parsed reference, across all pages — the checkbox below the table toggles just this page"
                  >
                    <input
                      type="checkbox"
                      checked={items.every((item) => item.included)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setItems((prev) => prev.map((item) => ({ ...item, included: checked })));
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="sr-only">Include all</span>
                  </label>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Reference
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Model
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Collection
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Variant
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Calibre
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Module
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Image
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide w-16">
                  Rule
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {previewPageItems.map(({ item, index: i }, pageRow) => (
                <Fragment key={i}>
                  {(() => {
                    // Group header (store product or accepted rule group): shown
                    // above its first row, and again at the top of a page it
                    // continues on.
                    const key = groupKeyByIndex.get(i);
                    const prevIndex = pageRow > 0 ? previewPageItems[pageRow - 1].index : undefined;
                    if (!key || (prevIndex !== undefined && groupKeyByIndex.get(prevIndex) === key)) return null;
                    const members = storeVariantGroups.get(key) ?? [i];
                    const ruleGroup = ruleGroupByKey.get(key);
                    const all = members.every((m) => items[m].included);
                    const some = members.some((m) => items[m].included);
                    const first = item.entry;
                    const groupCheckbox = (
                      <input
                        type="checkbox"
                        aria-label={`Include every row of ${first.productName || first.model || first.reference}`}
                        checked={all}
                        ref={(el) => {
                          if (el) el.indeterminate = some && !all;
                        }}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const set = new Set(members);
                          setItems((prev) => prev.map((it, idx) => (set.has(idx) ? { ...it, included: checked } : it)));
                        }}
                        className="rounded border-gray-300"
                      />
                    );
                    if (ruleGroup) {
                      const parentEntry = items[ruleGroup.parentIndex]?.entry;
                      return (
                        <tr className="bg-purple-50/70" data-testid="rule-group-header">
                          <td className="px-3 py-2 text-center">{groupCheckbox}</td>
                          <td colSpan={8} className="px-3 py-2 text-xs text-gray-700">
                            <span className="font-semibold text-gray-900">{parentEntry?.productName || parentEntry?.model || ruleGroup.key}</span>
                            <span className="ml-2 font-mono text-gray-500">{ruleGroup.key}</span>
                            <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-700">
                              Grouped by your rule → {parentEntry?.reference} as the parent, {ruleGroup.variantIndices.length} variant
                              {ruleGroup.variantIndices.length === 1 ? '' : 's'} under it
                            </span>
                          </td>
                        </tr>
                      );
                    }
                    const storeParentId = variantGroupOf(first)?.parentDocId;
                    const parentExists = storeParentId ? previewExistingDocIds?.has(storeParentId) : undefined;
                    return (
                      <tr className="bg-indigo-50/60" data-testid="variant-group-header">
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            aria-label={`Include every option of ${first.productName || first.model}`}
                            checked={all}
                            ref={(el) => {
                              if (el) el.indeterminate = some && !all;
                            }}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const set = new Set(members);
                              setItems((prev) => prev.map((it, idx) => (set.has(idx) ? { ...it, included: checked } : it)));
                            }}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td colSpan={8} className="px-3 py-2 text-xs text-gray-700">
                          <span className="font-semibold text-gray-900">{first.productName || first.model}</span>
                          {first.storeParent?.reference && first.storeParent.reference !== (first.productName || first.model) && (
                            <span className="ml-2 font-mono text-gray-500">{first.storeParent.reference}</span>
                          )}
                          <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700">
                            {members.length} options → imported as variants under one parent
                            {parentExists === true ? ' (parent already exists)' : parentExists === false ? ' (new parent)' : ''}
                          </span>
                          {(first.collection || first.calibre || first.movement) && (
                            <span className="ml-2 text-gray-500">
                              Shared: {[first.collection, first.calibre || first.movement].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })()}
                  <tr className={`hover:bg-gray-50 ${!item.included ? 'opacity-40' : ''} ${storeGroupKeyAt(i) ? 'bg-indigo-50/20' : groupKeyByIndex.has(i) ? 'bg-purple-50/20' : ''}`}>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={item.included}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setItems((prev) =>
                            prev.map((it, idx) => (idx === i ? { ...it, included: checked } : it))
                          );
                        }}
                        title={!item.entry.imgSrc && !item.included ? NO_IMAGE_REASON : undefined}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                          className="flex items-center gap-1.5 font-mono text-xs text-gray-900 hover:text-brand-600"
                          title="View / edit all extracted fields"
                        >
                          <span className="text-gray-400 w-3 inline-block">
                            {expandedRow === i ? '▾' : '▸'}
                          </span>
                          {item.entry.reference}
                        </button>
                        {entryWarnings(item.entry).length > 0 && (
                          <span
                            className="text-amber-500 text-xs cursor-help"
                            title={entryWarnings(item.entry).join(' · ')}
                          >
                            ⚠
                          </span>
                        )}
                      </div>
                      {/* Store options — a Square Online / WooCommerce product
                          sold in several options (dial, finish, size…) imports
                          as ONE reference in v1. Flagged so the curator can see
                          which rows a Phase 2 variant expansion would split. */}
                      {(item.entry.storeOptionCount ?? 0) > 1 && (
                        <span
                          className="mt-1 inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700"
                          title="The store sells this product in several options. It imports as one reference now; variant expansion is planned for Phase 2."
                        >
                          {item.entry.storeOptionCount} options on the store — Phase 2 variant candidate
                        </span>
                      )}
                      {/* REF CHANGED — this product already exists and the
                          freshly-derived ref disagrees with the stored one.
                          Rendered only for the third state: new products and
                          matching refs get no row at all. */}
                      {refDiffs.has(i) && (
                        <label className="mt-1 flex items-start gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!refRewriteOptOut.has(i)}
                            onChange={(e) => {
                              const accept = e.target.checked;
                              setRefRewriteOptOut((prev) => {
                                const next = new Set(prev);
                                if (accept) next.delete(i);
                                else next.add(i);
                                return next;
                              });
                            }}
                            className="mt-0.5 rounded border-gray-300"
                          />
                          <span className="text-[11px] leading-tight">
                            <span className="font-semibold text-amber-700">REF CHANGED:</span>{' '}
                            <span className="font-mono line-through text-gray-500">
                              {refDiffs.get(i)}
                            </span>
                            <span className="text-gray-400"> → </span>
                            <span className="font-mono text-amber-800">
                              {item.entry.reference}
                            </span>
                            <span className="block text-gray-500">
                              {refRewriteOptOut.has(i)
                                ? `Keeping “${refDiffs.get(i)}”`
                                : 'Will overwrite the stored reference'}
                            </span>
                          </span>
                        </label>
                      )}
                      {(() => {
                        // Brand routing — only when the importing brand has
                        // acquired siblings, and only on Shopify rows.
                        const { shopifyProductId: productId, shopifyVariantId: variantId } = item.entry;
                        if (!routingPreview || !productId || !variantId) return null;
                        const view = rowBrandView(
                          routingPreview.plan,
                          productId,
                          shopifyVariantDocId(productId, variantId),
                        );
                        const detectedId = routingPreview.plan.detections.get(productId)?.brandId;
                        if (!view || !detectedId) return null;
                        const nameOf = (id: string) => {
                          const b = routingPreview.brandById.get(id);
                          return b?.displayName || b?.name || id;
                        };
                        return (
                          <ImportBrandCell
                            reference={item.entry.reference}
                            options={routingOptions(routingPreview.ctx).map((b) => ({
                              id: b.id,
                              label: routingOptionLabel(b, routingPreview.ctx),
                            }))}
                            brandId={
                              view.lockedUnderBrandId ?? brandChoiceByProduct.get(productId) ?? view.brandId
                            }
                            lockedBrandName={
                              view.lockedUnderBrandId ? nameOf(view.lockedUnderBrandId) : undefined
                            }
                            detectedBrandId={detectedId}
                            detectedBrandName={nameOf(detectedId)}
                            flags={view.flags}
                            onChange={(brandId) =>
                              setBrandChoiceByProduct((prev) => new Map(prev).set(productId, brandId))
                            }
                          />
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{item.entry.model}</td>
                    <td className="px-3 py-2 text-gray-500">{item.entry.collection}</td>
                    <td className="px-3 py-2 text-gray-500">
                      {previewStructure.roles[i] === 'rule-parent' ? (
                        <span className="flex flex-col items-start gap-0.5">
                          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-700">Parent</span>
                          {item.entry.variant}
                        </span>
                      ) : previewStructure.roles[i] === 'rule-variant' ? (
                        <span className="text-gray-800">↳ {item.entry.variant || 'variant'}</span>
                      ) : storeGroupKeyAt(i) ? (
                        <span className="flex flex-col">
                          <span className="text-gray-800">↳ {item.entry.variant}</span>
                          {item.entry.price !== undefined && (
                            <span className="text-[11px] text-gray-500">{formatPrice(item.entry.price)}</span>
                          )}
                        </span>
                      ) : (
                        item.entry.variant
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      <div className="flex flex-col gap-1 items-start">
                        <span>
                          {(item.entry as any).calibre || item.entry.movement || (
                            <span className="text-gray-300">—</span>
                          )}
                        </span>
                        {revisionPicks.has(i) && (
                          <RevisionPickCell
                            pick={revisionPicks.get(i)!}
                            onPick={(memberId) => pickRowRevision(i, memberId)}
                            onYear={(year) => setRowRevisionYear(i, year)}
                            onAi={() => void lookupRowReleaseYear(i)}
                            aiBusy={revisionAiRow === i}
                            aiBlocked={revisionAiRow !== null && revisionAiRow !== i}
                          />
                        )}
                        {(() => {
                          // The importer's working fields live off-type on the
                          // entry; read them once through a Record rather than
                          // casting at each of the three use sites.
                          const loose = item.entry as unknown as Record<string, unknown>;
                          // Only rows that linked a calibre WITH brand-visible
                          // tiers get a selector — otherwise every ETA row in a
                          // 600-reference import grows an empty dropdown.
                          const rowTiers = tierOptionsFor(importTierIndex, loose._calibreId);
                          if (rowTiers.length === 0) return null;
                          const label = tierRowLabel(
                            loose._calibreTierName,
                            manualTierRows.has(i),
                          );
                          return (
                            <div
                              className="flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <select
                                value={(loose._calibreTierId as string) || ''}
                                onChange={(e) => setRowTier(i, e.target.value)}
                                title="Tier for this reference only. The calibre-check step sets the default for every reference sharing this calibre string."
                                className="rounded border-gray-300 text-[11px] py-0.5"
                              >
                                <option value="">— No tier —</option>
                                {rowTiers.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                              </select>
                              {label && (
                                <span
                                  className="text-[10px] text-gray-400"
                                  title={
                                    label.origin === 'auto'
                                      ? 'Detected from the calibre string — change it here to override this row only.'
                                      : 'Set by hand on this row.'
                                  }
                                >
                                  {label.origin}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {item.entry.module || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-1">
                      <ImageCell
                        src={item.entry.imgSrc}
                        alt={item.entry.reference}
                        boxClassName="w-10 h-10 rounded"
                        imgClassName="object-cover cursor-pointer hover:ring-2 hover:ring-brand-400 transition-shadow"
                        onClick={(e) => { e.stopPropagation(); setPreviewImage(item.entry.imgSrc!); }}
                        title="Click to enlarge"
                        onFailed={markImageFailed}
                      />
                      {(() => {
                        const failure = probeFailureOf(item.entry);
                        if (!failure) return null;
                        return (
                          <span
                            className="block mt-0.5 text-[10px] leading-tight font-medium text-red-600 whitespace-nowrap"
                            title={`The import could not fetch this image, so the reference would be committed without one: ${failure.error}`}
                          >
                            ✕ fetch failed
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => openExcludeForm(i)}
                        className="text-gray-400 hover:text-amber-600 text-base leading-none w-6 h-6 inline-flex items-center justify-center rounded hover:bg-amber-50"
                        title="Create exclusion rule from this item"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                  {expandedRow === i && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-4 py-3">
                        {renderItemDetail(item, i)}
                      </td>
                    </tr>
                  )}
                  {excludeFormRow === i && (
                    <tr className="bg-amber-50">
                      <td colSpan={9} className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-amber-800">
                            Exclude items where
                          </span>
                          <select
                            value={excludeFormField}
                            onChange={(e) => onExcludeFieldChange(e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                          >
                            {EXCLUDE_FIELDS.map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                          <select
                            value={excludeFormCondition}
                            onChange={(e) => setExcludeFormCondition(e.target.value as ExclusionRule['condition'])}
                            className="text-sm border rounded px-2 py-1"
                          >
                            {EXCLUDE_CONDITIONS.map((c) => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={excludeFormValue}
                            onChange={(e) => setExcludeFormValue(e.target.value)}
                            placeholder="value..."
                            className="text-sm border rounded px-2 py-1 flex-1 min-w-[160px]"
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateExclusionRule()}
                          />
                          <Button size="sm" onClick={handleCreateExclusionRule}>
                            Create Rule
                          </Button>
                          <Button size="sm" variant="secondary" onClick={closeExcludeForm}>
                            Cancel
                          </Button>
                        </div>
                        <p className="text-xs text-amber-700 mt-2">
                          Saves to this brand's exclusion rules and re-applies across all items.
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: page size + page-scoped selection + pager. Same shape as the
          Brands and Calibres listings, including the split scopes — the table
          header ticks every parsed reference, this one ticks the page. */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            Showing {previewPageItems.length === 0 ? 0 : previewStart + 1}–
            {previewStart + previewPageItems.length} of {items.length}
          </p>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">Show</label>
            <select
              value={previewPageSize}
              onChange={(e) => {
                setPreviewPageSize(Number(e.target.value));
                setPreviewPage(1);
              }}
              className="border border-gray-300 rounded px-1.5 py-0.5 text-xs text-gray-600"
            >
              {PREVIEW_PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <label
            className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer"
            title="Include just the references on this page — the checkbox in the table header covers every page"
          >
            <input
              type="checkbox"
              checked={allPageIncluded}
              ref={(el) => {
                if (el) el.indeterminate = somePageIncluded;
              }}
              onChange={togglePageIncluded}
              disabled={previewPageItems.length === 0}
              className="rounded text-brand-600 focus:ring-brand-500 disabled:opacity-40"
            />
            Select page ({previewPageItems.length})
          </label>
        </div>
        {previewTotalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={previewCurrentPage <= 1}
              onClick={() => setPreviewPage(previewCurrentPage - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {previewCurrentPage} of {previewTotalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={previewCurrentPage >= previewTotalPages}
              onClick={() => setPreviewPage(previewCurrentPage + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <p className="text-xs bg-blue-50 text-blue-700 p-3 rounded">
        References will be created in Firestore. Images will be fetched from the source URLs and
        uploaded to Firebase Storage.{' '}
        {overwriteExisting
          ? 'Existing references will be overwritten with all import data.'
          : 'Duplicate references will be merged — empty fields will be populated without overwriting existing values.'}
      </p>

      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={overwriteExisting}
          onChange={(e) => setOverwriteExisting(e.target.checked)}
          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
        Overwrite existing
      </label>

      <div>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={replaceAllImages}
            onChange={(e) => setReplaceAllImages(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Replace all images (re-download main + gallery from source)
        </label>
        <p className="text-xs text-gray-500 ml-6 mt-1">
          Useful when the scraper's image extraction has improved since your last import.
          Ignores previously-set curator tags on the main image.
        </p>
      </div>

      {overwriteExisting && replaceAllImages && (
        <p className="text-xs bg-red-50 text-red-700 border border-red-200 p-3 rounded">
          Both checked will re-scrape every field AND every image for ~{imageFetchEstimate.refs}{' '}
          reference{imageFetchEstimate.refs === 1 ? '' : 's'}. This may take a while and will hit
          source sites with {imageFetchEstimate.refs} × {imageFetchEstimate.perRef} ={' '}
          {imageFetchEstimate.total.toLocaleString()} requests.
        </p>
      )}

      <div className="flex justify-between pt-2 border-t">
        <Button variant="secondary" onClick={() => setStep('field-rules')}>
          ← Back to Rules
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleImport}>
            Import {items.filter((i) => i.included).length} References
          </Button>
        </div>
      </div>
      {/* Image preview overlay */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] cursor-pointer"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300"
            onClick={() => setPreviewImage(null)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );

  const renderImporting = () => {
    // Two distinct progress states: the pre-pass (load refs + resolve parents),
    // which runs before any item is written, and the per-item write pass. During
    // the pre-pass the item counter is still 0, so we show the phase label (and a
    // parent counter when resolving parents) instead of a frozen "0 / N".
    const isWriting = importPhase === 'Writing references…' || importPhase === '';
    const isResolvingParents = importPhase === 'Resolving parents…' && parentProgress.total > 0;
    const progress = isWriting
      ? (items.length > 0 ? (processedCount / items.length) * 100 : 0)
      : isResolvingParents
        ? (parentProgress.done / parentProgress.total) * 100
        : 0;
    const counterLabel = isWriting
      ? `${processedCount} / ${items.length}`
      : isResolvingParents
        ? `${parentProgress.done} / ${parentProgress.total}`
        : '';
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{importPhase || 'Importing references…'}</span>
          {counterLabel && <span>{counterLabel}</span>}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-200 ${isWriting ? 'bg-brand-600' : 'bg-brand-400'}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-8">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Reference
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-900">
                      {item.entry.reference}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge item={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDone = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-6 gap-3 text-center">
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-green-600">{addedCount}</div>
          <div className="text-xs text-green-700 mt-1">New</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-green-600">{updatedCount}</div>
          <div className="text-xs text-green-700 mt-1">Updated</div>
        </div>
        <div
          className="bg-blue-50 rounded-lg p-3"
          title="Already in Watch Keeper. The store's data is different from what you have, but your values were kept — nothing was changed."
        >
          <div className="text-2xl font-bold text-blue-600">{divergedCount}</div>
          <div className="text-xs text-blue-700 mt-1">Kept your edits</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3" title="Already in Watch Keeper and matching the store — nothing to change.">
          <div className="text-2xl font-bold text-gray-600">{duplicateCount}</div>
          <div className="text-xs text-gray-700 mt-1">Already up to date</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3" title="Unticked in the preview, so not imported.">
          <div className="text-2xl font-bold text-amber-600">{excludedCount}</div>
          <div className="text-xs text-amber-700 mt-1">Skipped (unticked)</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-red-600">{errorCount}</div>
          <div className="text-xs text-red-700 mt-1">Failed</div>
        </div>
      </div>

      <p className="text-sm text-gray-700 text-center" data-testid="import-summary-line">
        {summaryLine({
          added: addedCount,
          updated: updatedCount,
          kept: divergedCount,
          upToDate: duplicateCount,
          skipped: excludedCount,
          failed: errorCount,
          breakdown,
        })}
      </p>

      {galleryStats.refs > 0 && (
        <p className="text-xs text-gray-500 text-center">
          Gallery photos: {galleryStats.added} added, {galleryStats.tagged} already there now labelled by view (front, caseback, lume…)
          {galleryStats.replaced > 0 && `, ${galleryStats.replaced} galler${galleryStats.replaced === 1 ? 'y' : 'ies'} replaced`}
          {' · '}
          across {galleryStats.refs} reference{galleryStats.refs === 1 ? '' : 's'}
        </p>
      )}

      {imageFailureSummary.images > 0 && (
        <div className="border border-amber-300 rounded-lg overflow-hidden">
          <div className="bg-amber-50 px-3 py-2 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-amber-900">
                {formatImageFailureHeadline(addedCount + updatedCount, imageFailureSummary)}
              </div>
              <div className="text-xs text-amber-800">
                {imageFailureSummary.main} reference{imageFailureSummary.main === 1 ? '' : 's'} missing the main image
                {imageFailureSummary.gallery > 0 &&
                  `, ${imageFailureSummary.gallery} gallery shot${imageFailureSummary.gallery === 1 ? '' : 's'}`}
                . Source URLs are kept on each ref for a later retry.
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleRetryFailedImages()}
              loading={retryingImages}
              disabled={retryingImages}
            >
              Retry failed images
            </Button>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
            {imageFailureRows.flatMap((row) =>
              row.failures.map((f) => (
                <div key={`${row.brandId}/${row.referenceId}/${f.kind}/${f.url}`} className="px-3 py-1.5 text-xs">
                  <span className="font-mono text-gray-900">{row.reference}</span>
                  <span className="ml-2 text-gray-500">{f.kind === 'main' ? 'main image' : 'gallery'}</span>
                  <span className="ml-2 text-red-600">{f.error}</span>
                  <div className="text-gray-400 truncate" title={f.url}>{f.url}</div>
                </div>
              )),
            )}
          </div>
        </div>
      )}

      {errorCount > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-red-50 px-3 py-2 text-xs font-medium text-red-700">Errors</div>
          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
            {items
              .filter((i) => i.status === 'error')
              .map((item, i) => (
                <div key={i} className="px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-gray-900">{item.entry.reference}</span>
                  <span className="text-red-600 ml-2 text-xs">{item.error}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {pendingCount > 0 && (
        <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded">
          {pendingCount} items were not processed (import was cancelled).
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t">
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          onClick={() => {
            handleClose();
            onComplete({
              addedCount,
              updatedCount,
              divergedCount,
              duplicateCount,
              errorCount,
              excludedCount,
              parentsAdded,
              variantsAdded,
              parentsUpdated,
              variantsUpdated,
              parentsDiverged,
              variantsDiverged,
            });
          }}
        >
          Done — Refresh List
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {!hideTriggerButton && (
        <Button variant="secondary" onClick={handleOpen}>
          Bulk Import
        </Button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Bulk Import — ${brand.displayName || brand.name}`}
        size="xl"
      >
        {step === 'upload' && renderUpload()}
        {step === 'shopify-types' && renderShopifyTypes()}
        {step === 'store-categories' && renderStoreCategories()}
        {step === 'calibre-check' && (
          <ImportCalibreCheck
            entries={prePreviewItems.map((i) => i.entry) as unknown as { movement: string; [key: string]: unknown }[]}
            brandName={brand.displayName || brand.name}
            brandId={brand.id}
            extractionRules={extractionRules}
            extractorCalibres={extractorCalibres}
            manufacturerIdsManualInclude={brand.manufacturerIdsManualInclude}
            manufacturerIdsManualExclude={brand.manufacturerIdsManualExclude}
            onExtractionRulesChange={setExtractionRules}
            onProceed={(cMap) => {
              setCalibreMap(cMap);
              const picks = new Map<number, RevisionPickState>();
              // Enrich items with calibre data — match by movement field, extraction rules, or text search
              const enriched = prePreviewItems.map((item, index) => {
                let calEntry: CalibreMapEntry | undefined;
                let matchedCalibre = '';
                let matchedTarget: 'calibre' | 'movement' = 'calibre';

                // 1. Try exact movement field match
                if (item.entry.movement) {
                  calEntry = cMap.get(item.entry.movement);
                  if (calEntry) matchedCalibre = item.entry.movement;
                }

                // 2. Re-apply extraction rules to find calibre name for this entry
                //    (handles replaceWith — the map key may differ from the raw text)
                if (!calEntry) {
                  for (const rule of extractionRules) {
                    // Module-targeted rules feed the Module step, not the calibre map.
                    if (rule.targetField === 'module') continue;
                    const fieldVal = String((item.entry as unknown as Record<string, unknown>)[rule.sourceField] || '');
                    if (!fieldVal) continue;
                    try {
                      const regex = new RegExp(rule.pattern, 'i');
                      const match = fieldVal.match(regex);
                      if (match) {
                        const extracted = rule.replaceWith?.trim() || (match[1] || match[0]).trim();
                        if (extracted && cMap.has(extracted)) {
                          calEntry = cMap.get(extracted);
                          matchedCalibre = extracted;
                          matchedTarget = rule.targetField || 'calibre';
                          break;
                        }
                      }
                    } catch { /* invalid regex */ }
                  }
                }

                // 3. Fallback: search all text fields for any known calibre name
                if (!calEntry) {
                  const allText = [item.entry.description, item.entry.title, item.entry.variant, item.entry.productName]
                    .filter(Boolean).join(' ').toLowerCase();
                  for (const [calName, mapEntry] of cMap) {
                    if (allText.includes(calName.toLowerCase())) {
                      calEntry = mapEntry;
                      matchedCalibre = calName;
                      break;
                    }
                  }
                }

                if (!calEntry) return item;

                // Everything the match implies for this entry — the `calibreId`
                // link, the legacy spec copies (flag-gated) and the tier — in one
                // tested helper. See importCalibreStamp.
                let entry = stampCalibreOnEntry({
                  entry: item.entry,
                  calEntry,
                  matchedCalibre,
                  matchedTarget,
                  brandId: brand.id,
                  flags: calibreDualWriteFlags,
                });

                // The match named a revision LINE: pick the revision for this
                // reference (its year → the brand's founded year when ≥ 2000 →
                // the current revision), unless the string named the revision.
                const line = calEntry.revisionLine;
                const matched = line?.find((m) => m.id === calEntry.id);
                if (line && matched && matchedTarget === 'calibre') {
                  const year = refYearOf(item.entry as unknown as Record<string, unknown>);
                  const resolution: RevisionResolution<RevisionLineMember> | null = namesExplicitRevision(matchedCalibre, matched)
                    ? { member: matched, method: 'explicit', confidence: 'high', inRange: yearInRevision(matched, year), ...(year !== null ? { year } : {}) }
                    : resolveRevisionInLine({ members: line, refYear: year, brandFounded: brand.founded });
                  if (resolution) {
                    if (resolution.member.id !== matched.id) {
                      entry = applyRevisionPick({
                        entry,
                        from: { name: calEntry.normalizedName || matched.name, data: calEntry.data },
                        to: resolution.member,
                        brandId: brand.id,
                        flags: calibreDualWriteFlags,
                      });
                    }
                    picks.set(index, { line, resolution });
                  }
                }
                return { ...item, entry };
              });
              setRevisionPicks(picks);
              setItems(enriched);
              setStep('module-check');
            }}
            onBack={() => setStep(shopifyProducts.length > 0 ? 'shopify-types' : 'upload')}
          />
        )}
        {step === 'module-check' && (
          <ImportModuleCheck
            entries={items.map((i) => i.entry) as unknown as { module?: string; [key: string]: unknown }[]}
            brandName={brand.displayName || brand.name}
            extractionRules={extractionRules}
            onExtractionRulesChange={setExtractionRules}
            onProceed={(mMap) => {
              // Map of FINAL entry.module string → resolved moduleId, for rows
              // matched to an existing/created module. Seeded into the save-time
              // pre-pass so those link by this exact id and skip match-or-create
              // (which would duplicate the module when the raw extracted string
              // differs from its canonical name — the "5476" double-create bug).
              const resolvedIds = new Map<string, string>();
              // Resolve each entry's module identifier and stamp it onto
              // entry.module so the save-time module link pre-pass
              // (findModulesByLegacyValues → match-or-create → moduleId) picks it
              // up. The string is sourced (in priority order) from: the extracted
              // module field, a module-targeted extraction rule, or a text-field
              // fallback against known module names. When it matches an existing
              // module it's normalized to that doc's canonical name (e.g.
              // "QW-3229" → "3229"); when unmatched the raw value is kept so the
              // save-time pre-pass creates + links a fresh module — the digital
              // counterpart of the calibre link.
              const enriched = items.map((item) => {
                let modEntry: ModuleMapEntry | undefined;
                let moduleStr = (item.entry.module || '').trim();

                // 1. Exact module-field match
                if (moduleStr) modEntry = mMap.get(moduleStr);

                // 2. Module-targeted extraction rules (handles replaceWith — the
                //    map key may differ from the raw matched text). Also seeds
                //    moduleStr when the entry had no extracted module field.
                if (!modEntry) {
                  for (const rule of extractionRules) {
                    if (rule.targetField !== 'module') continue;
                    const fieldVal = String((item.entry as unknown as Record<string, unknown>)[rule.sourceField] || '');
                    if (!fieldVal) continue;
                    try {
                      const regex = new RegExp(rule.pattern, 'i');
                      const match = fieldVal.match(regex);
                      if (match) {
                        const extracted = rule.replaceWith?.trim() || (match[1] || match[0]).trim();
                        if (extracted) {
                          if (!moduleStr) moduleStr = extracted;
                          if (mMap.has(extracted)) { modEntry = mMap.get(extracted); break; }
                        }
                      }
                    } catch { /* invalid regex */ }
                  }
                }

                // 3. Fallback: search all text fields for any known module name
                if (!modEntry && !moduleStr) {
                  const allText = [item.entry.description, item.entry.title, item.entry.variant, item.entry.productName]
                    .filter(Boolean).join(' ').toLowerCase();
                  for (const [modName, mapEntry] of mMap) {
                    if (allText.includes(modName.toLowerCase())) {
                      modEntry = mapEntry;
                      moduleStr = modName;
                      break;
                    }
                  }
                }

                if (!moduleStr) return item;

                // Matched → canonical name; unmatched → raw string (save-time creates it).
                const moduleName = modEntry ? (modEntry.normalizedName || modEntry.module.name) : moduleStr;
                // Record the concrete id keyed by the FINAL entry.module value so
                // the save-time pre-pass links by id instead of re-resolving.
                if (modEntry?.module?.id) resolvedIds.set(moduleName, modEntry.module.id);
                if (moduleName === item.entry.module) return item;
                const entry = { ...item.entry, module: moduleName };
                return { ...item, entry };
              });
              preResolvedModuleIdsRef.current = resolvedIds;
              setItems(enriched);
              setStep('field-rules');
            }}
            onBack={() => setStep('calibre-check')}
          />
        )}
        {step === 'field-rules' && (
          <ImportFieldRules
            entryCount={items.length}
            entries={items.map((i) => i.entry)}
            brandId={brand.id}
            initialRules={savedFieldRules}
            initialExclusionRules={savedExclusionRules}
            extractionRules={extractionRules.map(({ sourceField, pattern, replaceWith, targetField }) => ({
              sourceField, pattern,
              ...(replaceWith ? { replaceWith } : {}),
              ...(targetField && targetField !== 'calibre' ? { targetField } : {}),
            }))}
            productTypes={[...new Set(shopifyProducts.map((p) => p.product_type).filter(Boolean))]}
            onApply={(rules: FieldRule[], activeExclusionRules: ExclusionRule[], allFieldRules: FieldRule[], allExclusionRules: ExclusionRule[]) => {
              // Persist ALL rules in state so they survive back/forward navigation
              setSavedFieldRules(allFieldRules);
              setSavedExclusionRules(allExclusionRules);
              const entries = items.map((i) => i.entry);
              const { entries: modified } = applyRules(entries, rules);
              const { excludedIndices } = applyExclusionRules(modified, activeExclusionRules);
              setItems(modified.map((entry, idx) => ({
                ...items[idx],
                entry,
                included: excludedIndices.has(idx) ? false : items[idx].included,
              })));
              setStep('preview');
            }}
            onBack={() => setStep('module-check')}
            onSkip={() => setStep('preview')}
          />
        )}
        {step === 'preview' && renderPreview()}
        {(step === 'importing' || step === 'done') && (
          <>
            {step === 'importing' ? renderImporting() : renderDone()}
          </>
        )}
      </Modal>

      {/* Paste HTML fallback modal for generic URL imports */}
      {pasteQueue.length > 0 && (
        <PasteHtmlModal
          queue={pasteQueue}
          method={extractionMethodRef.current}
          imageSelector={imageSelectorRef.current}
          onDone={handlePasteQueueDone}
          onCancel={() => {
            setPasteQueue([]);
            setPasteAutoEntries([]);
          }}
        />
      )}

      {/* Per-row manual paste for a single failed saved URL (Enter HTML button).
          Appends the parsed entry and clears the failure row, staying on the
          upload step so the user can keep working through the other failures. */}
      {savedUrlPasteUrl && (
        <PasteHtmlModal
          queue={[savedUrlPasteUrl]}
          method={extractionMethodRef.current}
          imageSelector={imageSelectorRef.current}
          onResult={({ url, extracted }) => {
            setSavedUrlEntries((prev) => [...prev, mapExtractedToScrapedEntry(extracted)]);
            setSavedUrlFailures((prev) => prev.filter((f) => f.url !== url));
          }}
          onDone={() => setSavedUrlPasteUrl(null)}
          onCancel={() => setSavedUrlPasteUrl(null)}
        />
      )}

      {/* Preview-step AI enrichment — the review grid over rows that are not
          written yet. Backdrop dismissal is off and ✕ / Escape go through the
          grid's unapplied-results guard: its results cost AI credits and live
          only in its state. Apply writes into the preview rows, nothing else. */}
      <Modal
        isOpen={previewEnrich !== null}
        onClose={() => {
          const close = () => setPreviewEnrich(null);
          const guard = enrichExitGuardRef.current;
          if (guard) guard(close);
          else close();
        }}
        title={`AI Enrich ${previewEnrich?.refs.length ?? 0} preview row${previewEnrich?.refs.length === 1 ? '' : 's'}`}
        size="full"
        dismissOnBackdrop={false}
      >
        {previewEnrich && previewEnrichHost && (
          <BulkAIEnrich
            embedded
            initialBrandId={brand.id}
            initialRefs={previewEnrich.refs}
            inMemory={previewEnrichHost}
            onApplySucceeded={() => setPreviewEnrich(null)}
            exitGuardRef={enrichExitGuardRef}
          />
        )}
      </Modal>
    </>
  );
}

// ── Helper: per-item status badge ─────────────────────────────────────────────

function StatusBadge({ item }: { item: ImportItem }) {
  switch (item.status) {
    case 'pending':
      return <span className="text-gray-400 text-xs">Waiting</span>;
    case 'processing':
      return (
        <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Processing
        </span>
      );
    case 'success':
      return (
        <span className="text-green-600 text-xs">
          {item.error ? `New (${item.error})` : 'New'}
        </span>
      );
    case 'updated':
      return <span className="text-green-600 text-xs">Updated</span>;
    case 'diverged':
      return (
        <span
          className="text-blue-600 text-xs"
          title="The store's data is different from yours, but your values were kept — nothing was changed."
        >
          Kept your edits
        </span>
      );
    case 'duplicate':
      return <span className="text-gray-500 text-xs" title={item.error}>Up to date</span>;
    case 'excluded':
      return <span className="text-amber-600 text-xs">Skipped (unticked)</span>;
    case 'error':
      return <span className="text-red-600 text-xs" title={item.error}>Error</span>;
  }
}
